// ────────────────────────────────────────────────────────────────────────────────────────────────────────────────────
// transient-shape.js — pure, framework-agnostic derivation of an asteroid/comet's VOXEL SHAPE + per-cell CRUST kube,
// deterministic from the body's address. SHARED VERBATIM by the client renderer (system-view.js builds the transient mesh
// from makeNoise + transientIsSolid + voxSurfMat) and the server (functions/lib/transient-shape.mjs is a BYTE-IDENTICAL
// copy) so the mineTransient CF can compute the EXACT kube a player mines and refuse to credit anything else.
// No THREE / DOM / fetch. If you edit this, edit the functions/ copy too — functions/test/transient-shape.test.mjs
// byte-compares them — and re-check that a transient still renders in-system.
// ────────────────────────────────────────────────────────────────────────────────────────────────────────────────────

// BIOMES (WG-09): the climate a version 3 world's biomes follow, and the biomes, from byte-identical twins that sit beside
// this file in all three places it lives (public/, functions/lib/, zone-server/lib/), so they resolve the same everywhere.
import { climateOf, fieldAt, rotationOf, starClassOf } from './climate.mjs';
import { BIOMES, BIOME_IDS, biomeMap, cellHash, columnDir, rareRollPasses, eligibleBiomes, biomeById, lifeChemistryOf } from './biomes.mjs';
import { BIOME_TURF, TURF_SINCE, turfColourOf, upOf } from './turf.mjs';
import { organicsOf, LIFE_PIGMENTS_SINCE, BARK_SINCE, NO_RIME_SINCE, pigmentsOf } from './organics.mjs';   // version 16: the six pigments' life (pigmentsOf); 18: a town's living bits in its world's
import { floraOf } from './flora.mjs';
// …and the frozen pool every real world's version 3 genome is drawn from (genomeOf), a twin like them.
import { V3_POOL } from './v3-pool.mjs';
import { geologyOf } from './geology.mjs';
import { decodeItem } from './item-ids.mjs';   // a recorded sea names its material by id (Oct 1); see recordedSeaOf
import { reliefOf, landBudget, bandDepth, spread, reliefHeight, duneProfile, terraceOf, craterField, holeField } from './biome-relief.mjs';
import { ALIENS_SINCE, peopleOf, townSizesFor, plotHalfFor, planFor, marginFor, RING_WIDTH, PEOPLE_MATERIALS, MATERIAL_ROLES, TOWNS18_SINCE, encodeTerr, decodeTerr, townFrame, townFrame19, cornerSlot, ST18 } from './alien-peoples.mjs';
import { TOWNS19_SINCE, CITY19, pickForm19, TOWNS20_SINCE, pickForm20, townSizes20, TOWNS21_SINCE, TOWN21, townShares21 } from './alien-towns.mjs';   // version 19: city layouts and town interiors (the owner, Oct 19)   // version 17: alien peoples' towns (18: rebuilt, on their own ground, in bits)

// FNV-1a seeded xorshift — byte-identical to makeRng in system-engine.js (duplicated so this module stands alone).
export function makeRng(str) {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) h = Math.imul(h ^ str.charCodeAt(i), 16777619);
    let a = h >>> 0;
    return () => { a = (a ^ (a << 13)) >>> 0; a = (a ^ (a >>> 17)) >>> 0; a = (a ^ (a << 5)) >>> 0; return (a >>> 0) / 4294967296; };
}

// Seeded 3D value noise (world-gen.html's hash3 + noise3D). Used for the lumpy shape AND the crust-material field.
export function makeNoise(seedSalt) {
    const fade = t => t * t * t * (t * (t * 6 - 15) + 10), lerp = (a, b, t) => a + t * (b - a);
    const hash3 = (x, y, z) => {
        x = (x + 100000) | 0; y = (y + 100000) | 0; z = (z + 100000) | 0;
        let h = seedSalt + x; h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
        h += y; h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
        h += z; h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
        return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
    };
    const grad = (h, x, y, z) => { let u = h < 8 ? x : y, v = h < 4 ? y : (h === 12 || h === 14 ? x : z); return ((h & 1) ? -u : u) + ((h & 2) ? -v : v); };
    const hi = (a, b, c) => Math.floor(hash3(a, b, c) * 16);   // made once, not on every call (the same arithmetic)
    return (x, y, z) => {
        const x0 = Math.floor(x), y0 = Math.floor(y), z0 = Math.floor(z);
        const tx = x - x0, ty = y - y0, tz = z - z0, fx = fade(tx), fy = fade(ty), fz = fade(tz);
        const n000 = grad(hi(x0, y0, z0), tx, ty, tz), n100 = grad(hi(x0+1, y0, z0), tx-1, ty, tz);
        const n010 = grad(hi(x0, y0+1, z0), tx, ty-1, tz), n110 = grad(hi(x0+1, y0+1, z0), tx-1, ty-1, tz);
        const n001 = grad(hi(x0, y0, z0+1), tx, ty, tz-1), n101 = grad(hi(x0+1, y0, z0+1), tx-1, ty, tz-1);
        const n011 = grad(hi(x0, y0+1, z0+1), tx, ty-1, tz-1), n111 = grad(hi(x0+1, y0+1, z0+1), tx-1, ty-1, tz-1);
        return lerp(lerp(lerp(n000, n100, fx), lerp(n010, n110, fx), fy), lerp(lerp(n001, n101, fx), lerp(n011, n111, fx), fy), fz) * 1.2;
    };
}

// The transient LUMPY shape: is cell (x,y,z) inside the potato? `o` = the 12 grid offsets [ox1,oy1,oz1, …, ox4,oy4,oz4].
// VERBATIM from system-view.js buildVoxelWorld's transient isSolid branch.
export function transientIsSolid(noise, o, R, x, y, z) {
    const [ox1, oy1, oz1, ox2, oy2, oz2, ox3, oy3, oz3, ox4, oy4, oz4] = o;
    const px = x + 0.5, py = y + 0.5, pz = z + 0.5, d = Math.sqrt(px*px + py*py + pz*pz);
    if (d <= Math.max(1, R * 0.4)) return true;                          // solid lumpy core (no holes)
    const nx = px / d, ny = py / d, nz = pz / d;                         // unit direction → surface-radius field
    const big  = noise((nx+ox1)*1.4, (ny+oy1)*1.4, (nz+oz1)*1.4);        // few big lobes
    const med  = noise((nx+ox2)*3.6, (ny+oy2)*3.6, (nz+oz2)*3.6);
    const fine = noise((nx+ox3)*7.5, (ny+oy3)*7.5, (nz+oz3)*7.5);        // fine bumps
    let surfR = R * (0.84 + big*0.30 + med*0.13 + fine*0.06);            // ≈ R·(0.35 … 1.33) by direction
    surfR += noise((px+ox4)*0.28, (py+oy4)*0.28, (pz+oz4)*0.28) * R * 0.16; // positional warp → concavities/lobes
    return d <= surfR;
}

// ── ROCKY WORLDS ────────────────────────────────────────────────────────────────────────────────────────────────────
// The rocky-world terrain shape, VERBATIM from buildVoxelWorld's isSolid. It lives here for the same reason
// transientIsSolid does: the server has to be able to answer "is that cell really ground?" without trusting the client.
//
// It was deliberately NOT ported before — functions/index.js said so in as many words, that genDensity ports surfMat
// "but not isSolid, which needs the world radius plus the rough/variance draws it deliberately discards". Those two
// draws are the ONLY thing that was missing: the noise seed, the twelve offsets and the draw order were already being
// consumed to keep surfThresh aligned (see transientFields' draws 14-15). Capturing them instead of throwing them away
// is the whole port, which is why this is twenty lines rather than a second terrain generator.
//
// A POINT QUERY, not an enumeration: O(1) per cell, no grid walk, so a Cloud Function can ask about a handful of cells
// per call for the price of a few noise lookups.
export function rockyIsSolid(noise, o, R, rough, variance, x, y, z) {
    const [ox1, oy1, oz1, ox2, oy2, oz2, ox3, oy3, oz3, ox4, oy4, oz4] = o;
    const rF = Math.max(Math.abs(x + 0.5), Math.abs(y + 0.5), Math.abs(z + 0.5)), r = Math.round(rF);
    if (r > R) return false;
    if (r <= Math.max(1, R - 3)) return true;          // deep interior: always solid

    const refR = Math.max(1, R - 2);                   // project to the reference shell, then warp
    let nx = x, ny = y, nz = z;
    if (rF > 0) { nx = x / rF * refR; ny = y / rF * refR; nz = z / rF * refR; }
    const wS = 12, s = 0.015;
    const wx = nx + noise((nx+ox1)*s, (ny+oy1)*s, (nz+oz1)*s) * wS;
    const wy = ny + noise((ny+ox2)*s, (nz+oy2)*s, (nx+oz2)*s) * wS;
    const wz = nz + noise((nz+ox3)*s, (nx+oy3)*s, (ny+oz3)*s) * wS;
    const hills = noise((wx+ox1)*0.03, (wy+oy1)*0.03, (wz+oz1)*0.03);
    const bumps = noise((wx+ox2)*0.08, (wy+oy2)*0.08, (wz+oz2)*0.08);
    const det   = noise((wx+ox3)*0.18, (wy+oy3)*0.18, (wz+oz3)*0.18);
    const bN    = noise((nx+ox4)*0.008, (ny+oy4)*0.008, (nz+oz4)*0.008);
    const t = (Math.max(1, Math.min(10, rough + bN * 8 * variance)) - 1) / 9;
    const raw = hills * (1.1 - t * 0.4) + bumps * (0.05 + t * 0.2) + det * (0.01 + t * 0.1);
    let target = R - 3;
    if (raw >= 0.20) target = R; else if (raw >= -0.03) target = R - 1; else if (raw >= -0.26) target = R - 2;
    return r <= target;
}

// ── ROCKY WORLDS, VERSION 2: TERRAIN WORTH LANDING ON ───────────────────────────────────────────────────────────────
// Version 1 gives every world the same four height levels, R-3 to R, whatever its size, so a Ø96 world is a Ø24 world
// with more of the same ripple on it. Version 2 changes the SHAPE and nothing else: the #vox draws, the crust material
// field, the ore bands and the grid are all version 1's, which is why a world's colours and its ore do not move.
//
// RELIEF SCALES WITH THE WORLD. Heights run from about a quarter of R below the old surface to a quarter of R above
// it, so the ground on Ø24 can span 8 levels and on Ø96 25 (measured on the golden worlds: 6 and 24), and every noise
// frequency is set per REFERENCE RADIUS rather than per cell, so a small world gets the same few hills and basins
// across a face as a big one instead of a zoomed-in patch. Upward it spends part of the build headroom: the grid
// already reaches 2R so players can pillar, and a quarter of R leaves three quarters of that for building.
//
// ON TOP OF THE HILLS, THREE LANDFORMS, each switched on per world by its own draw so worlds differ in kind and not
// just in seed: mountain ranges (ridged noise inside a regional mask), mesas (flat tops on terraces behind cliffs)
// and craters (a bowl, a raised rim, a flat floor and a central peak on the big ones). Some worlds are old and pocked,
// some are smooth, some are all plateaus. Measured over 5,291 real rocky worlds: 93% have craters (46% ten or more),
// 64% have mountain ranges, 45% mesas and 55% caves.
//
// AND IN THE BASINS, A SEA, when asked for the kind the world's climate allows: water, liquid methane or nitrogen, a
// sheet of ice cracked by lineae, or lava. A layer of its own over the rock (ROCKY SEAS, after the function).
//
// CAVES, AS WORM TUNNELS, and three rules keep them honest:
//   * CLEAR OF THE CORE. No cell deeper than caveMinR is ever carved — half of R, and never within three cells of the
//     outer core — so the dig to the Cor stays a dig. Below Ø32 that leaves no room between core and roof, so the
//     smallest worlds have none.
//   * A ROOF. A cave cell is carved only if at least `roof` cells of rock lie above it in its own face column, so a
//     flat surface is never thin over a cave and no landing spot opens into one. Where a tunnel runs beside a cliff
//     the cliff face can cut it, and that is where a cave mouth appears.
//   * ORE STAYS BURIED by the existing rule: veinAt asks this isSolid about all six neighbours, so a vein beside a
//     tunnel is simply not a vein. Nothing here had to change for that.
//
// LANDFORMS RUN STRAIGHT OVER THE CUBE'S EDGES, as version 1's ripple always did. The relief is one field over the
// whole cube (every column samples it where the column meets the reference cube, and the two columns either side of an
// edge sample it less than a cell apart), so a basin, a valley, a crater or a lake that reaches an edge carries on down
// the next face. It used to fade back to version 1's ripple over a band a fifth of R wide along every edge, which left
// each face a tile with a flat rim: measured over 150 real worlds, the height's spread two to three columns from an edge
// was a sixth of what it is inside the face, and it never left version 1's R-3..R band there at all.
// THE ONE RULE AT AN EDGE: NOTHING THERE STANDS ABOVE THE OLD SURFACE R. The walker rounds an edge by parking the new
// face's tangents just inside ±R (system-view.js tryStep) and rounds its camera about ±R (seamAnchor), the surface
// scan's corner fallback reads the column at R-1, and the share-card renderer walks each face's columns out to ±R, so
// ground above R at an edge would teleport the walker a cell per level and leave a hole in the card. So what rises above
// R near an edge (a range, a mesa, a crater's rim) may climb at most one level per column away from it (V2_EDGE_SLOPE),
// eased into by a smooth minimum rather than cut, and meets the next face's ground on a slope a player can jump up:
// the same 45° staircase the walker already auto-steps within two cells of an edge. Everything below R crosses freely.
//
// DETERMINISTIC ON EVERY ENGINE. Only + - * /, Math.sqrt, Math.round/floor/abs/min/max and the #vox value noise:
// Math.sin, pow and the rest are only approximated by the standard, and a one-ulp difference between Safari and the
// server's V8 could flip a cell the two then disagree about. tests/worldgen-golden.test.mjs refuses them here.
//
// FAST BECAUSE EACH FACE IS A HEIGHT MAP. Every cell asks for the height of the face column it stands in; the heights
// live in a lazily filled table of one sample per column per face (about 52k on a Ø96 world), so the build pays for
// the noise once per column rather than once per cell, and a server asking about a handful of cells pays for a handful
// of samples. The order the table fills in cannot change an answer: each sample is a pure function of its column,
// which tests/worldgen-parity.test.mjs checks by asking the servers' copies in a different order from the renderer.
// Measured in node on a Ø96 world, the fill costs about what version 1's did (its shell is ten times the cells, each a
// table lookup instead of seven noise calls).
const V2_UP = 0.24, V2_DOWN = 0.26;
const V2_EDGE_SLOPE = 1, V2_EDGE_EASE = 3;                 // levels per column above R near an edge; the ease's width
const V2_SEAM = 5;                                         // columns over which a seam's joining fades out (sample)
const V2_NONE = -32768;                                    // a table sample not computed yet

function v2smooth(t) { return t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t); }

// Everything version 2 knows about one world, built once per (addr, R). `d` is voxDraws(addr); the extra draws come
// from streams of their own ('#relief', '#craters', '#caves'), so the sixteen #vox draws every version starts from are
// untouched and each feature's draws cannot shift another's.
function rockyTerrainV2(d, R, addr) {
    const noise = d.noise;
    const refR = Math.max(1, R - 2), inv = 1 / refR;
    const up = Math.max(3, Math.round(R * V2_UP)), down = Math.max(4, Math.round(R * V2_DOWN));
    const lo = R - down, hi = R + up;                      // the surface is always within [lo, hi]

    // ---- CHARACTER: what kind of world this is, one draw per landform ----
    const rel = makeRng((addr || '') + '#relief');
    const o3 = () => [rel() * 10000, rel() * 10000, rel() * 10000];
    const W1 = o3(), W2 = o3(), W3 = o3(), OB = o3(), OH = o3(), OU = o3(), OD = o3(), OR = o3();
    const OM = o3(), ON = o3(), OS = o3(), OL = o3();
    const kCrater = rel(), kMount = rel(), kMesa = rel(), kCave = rel(), kMesaTop = rel();
    // A fifth of worlds are barely cratered and a few are saturated; squared so most sit in between.
    const craterDensity = kCrater < 0.2 ? kCrater * 0.25 : 0.1 + 0.9 * ((kCrater - 0.2) / 0.8) * ((kCrater - 0.2) / 0.8);
    const mountAmp = kMount < 0.35 ? 0 : (kMount - 0.35) / 0.65;          // 35% of worlds have no ranges
    const mesaAmt = kMesa < 0.55 ? 0 : (kMesa - 0.55) / 0.45;             // 55% have no mesas
    const caveAmt = kCave < 0.3 ? 0 : (kCave - 0.3) / 0.7;                // 30% have no caves at all
    const mesaTop = 1 + Math.round((0.08 + 0.08 * kMesaTop) * R);         // a mesa's middle terrace, above the old surface
    const mesaStep = Math.max(2, Math.round(R * 0.06));

    // ---- CRATERS: anywhere on a face, edges included, sized by a square law so most are small and a few are big ----
    // A crater whose bowl reaches an edge carries on over it: its shape is a distance in three dimensions from a centre
    // on the reference cube, so the next face's columns fall inside it too.
    const cr = makeRng((addr || '') + '#craters');
    const rhoMax = Math.max(3, refR * 0.35);
    const nCr = Math.round(craterDensity * 6 * refR * refR / 70);
    const craters = [];                                    // [x, y, z, rho, 1/rho, depth, rim, peak, floor, outer²]
    for (let i = 0; i < nCr; i++) {
        const u = cr(), fresh = 0.55 + 0.45 * cr(), face = Math.floor(cr() * 6), a = cr(), b = cr();   // five draws each, always
        const rho = 2 + (rhoMax - 2) * u * u;
        const ax = face >> 1, sg = (face & 1) ? -1 : 1, p = [0, 0, 0];
        p[ax] = sg * refR; p[(ax + 1) % 3] = (a * 2 - 1) * refR; p[(ax + 2) % 3] = (b * 2 - 1) * refR;
        const depth = rho * (0.35 + 0.2 * fresh);
        // THE RIM STANDS UP ON EVERY CRATER. It was a fixed share of the radius, which left the commonest craters (a
        // radius of two to five) a rim of under a cell: rounded to the grid it was not there, and a bowl with no rim is a
        // dip, not a crater. Now even the smallest fresh one has a ring of about a cell, and the big ones are as before.
        craters.push(p[0], p[1], p[2], rho, 1 / rho, depth, (0.9 + 0.1 * rho) * fresh, rho > 7 ? depth * 0.45 : 0,
                     depth * 0.8, 2.56 * rho * rho);
    }
    const CN = 10;

    // The surface height, in cells above (or below) the old surface R, in the direction of reference-shell point p.
    // `e` is p's distance to the nearest cube edge along the face.
    function relief(px, py, pz, e) {
        const ws = 0.22 * refR, wf = 0.9 * inv;           // domain warp: features bend instead of sitting on a lattice
        const wx = px + noise((px + W1[0]) * wf, (py + W1[1]) * wf, (pz + W1[2]) * wf) * ws;
        const wy = py + noise((py + W2[0]) * wf, (pz + W2[1]) * wf, (px + W2[2]) * wf) * ws;
        const wz = pz + noise((pz + W3[0]) * wf, (px + W3[1]) * wf, (py + W3[2]) * wf) * ws;
        const fb = 1.1 * inv, fh = 3.0 * inv;
        const basin = noise((wx + OB[0]) * fb, (wy + OB[1]) * fb, (wz + OB[2]) * fb);
        const hills = noise((wx + OH[0]) * fh, (wy + OH[1]) * fh, (wz + OH[2]) * fh);
        const bumps = noise((wx + OU[0]) * 0.09, (wy + OU[1]) * 0.09, (wz + OU[2]) * 0.09);
        const det   = noise((wx + OD[0]) * 0.21, (wy + OD[1]) * 0.21, (wz + OD[2]) * 0.21);
        // Version 1's roughness, read the same way: draws 14 and 15 decide how busy the small-scale surface is.
        const rn = noise((px + OR[0]) * 0.8 * inv, (py + OR[1]) * 0.8 * inv, (pz + OR[2]) * 0.8 * inv);
        const tr = Math.max(0, Math.min(1, (d.rough + rn * 8 * d.variance - 1) / 9));
        let H = basin * 0.11 * R + hills * 0.075 * R + bumps * (0.4 + 1.4 * tr) + det * (0.15 + 0.55 * tr) - 1;
        if (mountAmp > 0) {                                // ranges: ridged noise inside a regional mask
            const fm = 0.9 * inv, fr = 2.2 * inv;
            const m = noise((px + OM[0]) * fm, (py + OM[1]) * fm, (pz + OM[2]) * fm);
            const mask = v2smooth((m + 0.25 - 0.6 * (1 - mountAmp)) * 2.2);
            if (mask > 0) {
                const rr = 1 - Math.abs(noise((wx + ON[0]) * fr, (wy + ON[1]) * fr, (wz + ON[2]) * fr));
                H += mask * rr * rr * mountAmp * 0.28 * R;
            }
        }
        // Mesas: flat tops on three terraces, which only ever raise the ground. The terraces are what keep them buttes
        // rather than needles: each is a higher contour of the same field, so a top can only be as wide as the step
        // below it allows, and a blob that barely clears the threshold makes a low shelf instead of a one-cell spire
        // six cells tall. Unwarped, because the warp that bends the hills into ridges drew mesas out into slivers.
        if (mesaAmt > 0) {
            const fs = 1.8 * inv;
            const s = noise((px + OS[0]) * fs, (py + OS[1]) * fs, (pz + OS[2]) * fs);
            const k = (s - (0.55 - 0.35 * mesaAmt)) * 7;
            if (k > 0) {
                const fl = 1.4 * inv, lvl = noise((px + OL[0]) * fl, (py + OL[1]) * fl, (pz + OL[2]) * fl);
                const top = mesaTop + (lvl > 0.2 ? mesaStep : lvl < -0.2 ? -mesaStep : 0);
                const terrace = k >= 1 ? 1 : Math.floor(k * 3) / 3;
                const lift = top * terrace;
                if (terrace > 0 && lift > H) H = lift;
            }
        }
        for (let i = 0; i < craters.length; i += CN) {     // craters punch through whatever is there
            const dx = px - craters[i], dy = py - craters[i + 1], dz = pz - craters[i + 2];
            const d2 = dx * dx + dy * dy + dz * dz;
            if (d2 >= craters[i + 9]) continue;
            const q = Math.sqrt(d2) * craters[i + 4];
            if (q < 1) {
                let h = -craters[i + 5] * (1 - q * q) + craters[i + 6] * q * q * q * q;
                if (craters[i + 7] > 0 && q < 0.3) { const s = 1 - q / 0.3; h += craters[i + 7] * s * s; }
                H += h < -craters[i + 8] ? -craters[i + 8] : h;
            } else {
                const s = (1.6 - q) / 0.6;
                H += craters[i + 6] * s * s;
            }
        }
        // AT AN EDGE, NOTHING ABOVE R (see LANDFORMS RUN STRAIGHT OVER THE CUBE'S EDGES): the ceiling rises one level per
        // column from the edge column's 0, and the ground meets it through a polynomial smooth minimum V2_EDGE_EASE
        // wide, which is the height itself wherever the ground is more than that below the ceiling (almost everywhere)
        // and never above either. The edge column's centre is half a column in, so its ceiling is exactly 0.
        const cap = V2_EDGE_SLOPE * (e - 0.5);
        if (H > cap - V2_EDGE_EASE) {
            const k = 0.5 + 0.5 * (cap - H) / V2_EDGE_EASE, s = k < 0 ? 0 : k;
            H = cap + (H - cap) * s - V2_EDGE_EASE * s * (1 - s);
        }
        return H;
    }

    // ---- THE HEIGHT TABLE: one sample per face column, filled on demand ----
    // A cell reads the column it stands in on the face its dominant axis names, NOT the direction it lies in. Version 1
    // projects each cell onto a reference shell, which makes every height radial: harmless at four levels, but a
    // radial cliff twenty cells tall leans outward by half its height half way to an edge, and the walker's gravity,
    // the rock scatter and the card renderer all think in face columns. So a version 2 cliff is vertical to whoever
    // is standing beside it, and every face is an exact height map.
    //
    // WHERE TWO FACES MEET, THEIR COLUMNS ARE JOINED. A column samples the relief where it meets the reference cube, so
    // the two columns either side of an edge sample points less than a cell apart only when the ground there stands at
    // about the reference radius. Where the ground is lower, the two faces meet lower down, and the columns that meet
    // there sample the field on their own faces: the +x column at y = 40 and the +y column at x = 40 read points √2 × (R-2
    // - 40) apart, so a basin eight levels below the old surface put a step of several levels on the seam. So near where
    // a column's ground meets the next face, its height is averaged with the column that mirrors it across the edge (the
    // reflection that swaps the two faces), fully within two cells of the seam and fading out over V2_SEAM more: the two
    // sides of every seam stand at the same height, and away from the seams each face is its own field as before.
    // Every height is still a pure function of its column: of the relief at that column and at its (at most two) mirrors,
    // which are kept unrounded in `raw` so each is worked out once whichever of its columns asks first.
    const Wd = 2 * refR, WW = Wd * Wd;
    const table = new Int16Array(6 * WW).fill(V2_NONE);
    const raw = new Float64Array(6 * WW).fill(NaN);
    function rawAt(fi, iu, iv) {
        const ti = fi * WW + iu * Wd + iv;
        let h = raw[ti];
        if (h !== h) {                                     // not worked out yet (NaN is the one value unequal to itself)
            const ax = fi >> 1, tu = iu - refR + 0.5, tv = iv - refR + 0.5, p = [0, 0, 0];
            p[ax] = (fi & 1) ? -refR : refR; p[(ax + 1) % 3] = tu; p[(ax + 2) % 3] = tv;
            h = raw[ti] = R + relief(p[0], p[1], p[2], refR - Math.max(Math.abs(tu), Math.abs(tv)));
        }
        return h;
    }
    function sample(fi, iu, iv) {
        const ax = fi >> 1, sa = (fi & 1) ? -1 : 1, tu = iu - refR + 0.5, tv = iv - refR + 0.5;
        const h = rawAt(fi, iu, iv);
        let sum = h, wsum = 1;
        for (let k = 0; k < 2; k++) {                      // toward the edge each of the two tangents runs to
            const t = k === 0 ? tu : tv, at = t < 0 ? -t : t;
            // Not near that seam whatever the mirror holds (it is never below lo): skipped without asking, and the pair's
            // weight is the same from either side, so both columns of a pair always agree on whether they are joined.
            if ((h + lo) * 0.5 - 0.5 - at >= 2 + V2_SEAM) continue;
            // The mirror: on the face that tangent runs to, at the same distance from the edge along this face's axis
            // and the same place along the edge.
            const nAx = (ax + 1 + k) % 3, nFi = nAx * 2 + (t > 0 ? 0 : 1), c = [0, 0, 0];
            c[ax] = sa * at; c[3 - ax - nAx] = k === 0 ? tv : tu;
            const hm = rawAt(nFi, c[(nAx + 1) % 3] + refR - 0.5, c[(nAx + 2) % 3] + refR - 0.5);
            const g = (h + hm) * 0.5 - 0.5 - at;          // how far inside the seam the pair's ground is, in cells
            const w = g <= 2 ? 1 : 1 - v2smooth((g - 2) / V2_SEAM);
            if (w > 0) { sum += w * hm; wsum += w; }
        }
        const t = Math.round(sum / wsum);
        return t < lo ? lo : t > hi ? hi : t;
    }

    // ---- CAVES: worm tunnels in the shell between caveMinR and the roof ----
    const bands = Math.max(12, Math.min(48, R));           // ore-veins layerBands' clamp, for the outer core's radius
    const ocR = Math.max(1, Math.round(bands / 4) - 2) + Math.ceil(bands / 8);
    const caveMinR = Math.max(ocR + 3, Math.round(R * 0.5));
    const roof = Math.max(3, Math.round(R * 0.08));
    const capR = R - roof - 1;                             // tunnels are steered under a typical surface
    const cv = makeRng((addr || '') + '#caves');
    const spheres = [];                                    // [x, y, z, r²]
    const nWorms = (caveAmt > 0 && capR - caveMinR >= 4) ? Math.round(caveAmt * 3 * R * R / 144) : 0;
    for (let w = 0; w < nWorms; w++) {
        const ax = Math.floor(cv() * 3), sg = cv() < 0.5 ? -1 : 1, tu = cv() * 2 - 1, tv = cv() * 2 - 1;
        const r0 = caveMinR + 2 + cv() * (capR - caveMinR - 4);
        const p = [0, 0, 0];
        p[ax] = sg * r0; p[(ax + 1) % 3] = tu * r0; p[(ax + 2) % 3] = tv * r0;
        let dx = cv() - 0.5, dy = cv() - 0.5, dz = cv() - 0.5;
        let rad = 1.1 + cv() * 0.8;
        const steps = 16 + Math.floor(cv() * (10 + R));
        const chamberAt = cv() < 0.4 ? Math.floor(cv() * steps) : -1;
        for (let i = 0; i < steps; i++) {
            const L = Math.max(Math.abs(p[0]), Math.abs(p[1]), Math.abs(p[2]));
            const pl = Math.sqrt(p[0] * p[0] + p[1] * p[1] + p[2] * p[2]) || 1;
            const nx = p[0] / pl, ny = p[1] / pl, nz = p[2] / pl;
            if (L < caveMinR + rad + 1.5) { dx += nx * 0.5; dy += ny * 0.5; dz += nz * 0.5; }
            else if (L > capR - rad - 0.5) { dx -= nx * 0.5; dy -= ny * 0.5; dz -= nz * 0.5; }
            dx += (cv() - 0.5) * 0.45; dy += (cv() - 0.5) * 0.45; dz += (cv() - 0.5) * 0.45;
            const dr = dx * nx + dy * ny + dz * nz;        // run along under the surface rather than straight down
            dx -= nx * dr * 0.35; dy -= ny * dr * 0.35; dz -= nz * dr * 0.35;
            const dl = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
            dx /= dl; dy /= dl; dz /= dl;
            p[0] += dx * 0.9; p[1] += dy * 0.9; p[2] += dz * 0.9;
            rad += (cv() - 0.5) * 0.25; rad = rad < 0.9 ? 0.9 : rad > 2.3 ? 2.3 : rad;
            spheres.push(p[0], p[1], p[2], rad * rad);
            if (i === chamberAt) { const c = 2.4 + cv() * 1.8; spheres.push(p[0], p[1], p[2], c * c); }
        }
    }
    // A coarse hash of which spheres touch which 8³ block, so a cell asks about the few tunnels near it.
    const CB = 8, cbOff = Math.ceil((hi + 4) / CB), nb = 2 * cbOff + 1;
    const blocks = spheres.length ? new Array(nb * nb * nb) : null;
    for (let s = 0; s < spheres.length; s += 4) {
        const rr = Math.sqrt(spheres[s + 3]);
        const x0 = Math.floor((spheres[s] - rr) / CB), x1 = Math.floor((spheres[s] + rr) / CB);
        const y0 = Math.floor((spheres[s + 1] - rr) / CB), y1 = Math.floor((spheres[s + 1] + rr) / CB);
        const z0 = Math.floor((spheres[s + 2] - rr) / CB), z1 = Math.floor((spheres[s + 2] + rr) / CB);
        for (let bx = x0; bx <= x1; bx++) for (let by = y0; by <= y1; by++) for (let bz = z0; bz <= z1; bz++) {
            const k = ((bx + cbOff) * nb + (by + cbOff)) * nb + (bz + cbOff);
            (blocks[k] || (blocks[k] = [])).push(s);
        }
    }
    function inCave(cx, cy, cz) {
        const list = blocks[((Math.floor(cx / CB) + cbOff) * nb + (Math.floor(cy / CB) + cbOff)) * nb + (Math.floor(cz / CB) + cbOff)];
        if (!list) return false;
        for (let i = 0; i < list.length; i++) {
            const s = list[i], dx = cx - spheres[s], dy = cy - spheres[s + 1], dz = cz - spheres[s + 2];
            if (dx * dx + dy * dy + dz * dz <= spheres[s + 3]) return true;
        }
        return false;
    }
    const hasCaves = spheres.length > 0;
    const solidR = hasCaves ? Math.min(lo, caveMinR - 1) : lo;

    // The face column holding cell (x,y,z), as its index in the height table. Ties between axes go to x, then y, the
    // same way on every side, and a column beyond the reference face (the corner region) reads the edge sample beside it.
    let colFi = 0, colU = 0, colV = 0;                     // the column columnOf last found, for the lineae below
    function columnOf(x, y, z, cx, cy, cz, ax, ay, az) {
        let fi, u, v;
        if (ax >= ay && ax >= az) { fi = cx > 0 ? 0 : 1; u = y; v = z; }
        else if (ay >= az) { fi = cy > 0 ? 2 : 3; u = z; v = x; }
        else { fi = cz > 0 ? 4 : 5; u = x; v = y; }
        colFi = fi;
        colU = (u < -refR ? -refR : u > refR - 1 ? refR - 1 : u) + refR;
        colV = (v < -refR ? -refR : v > refR - 1 ? refR - 1 : v) + refR;
        return fi * WW + colU * Wd + colV;
    }
    function heightOfColumn(ti) {
        let t = table[ti];
        if (t === V2_NONE) t = table[ti] = sample((ti / WW) | 0, ((ti % WW) / Wd) | 0, ti % Wd);
        return t;
    }
    // The surface's L∞ radius over the face column holding cell (x,y,z).
    function surfaceAt(x, y, z, cx, cy, cz, ax, ay, az) { return heightOfColumn(columnOf(x, y, z, cx, cy, cz, ax, ay, az)); }
    function isSolid(x, y, z) {
        const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5;
        const ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
        const rF = ax > ay ? (ax > az ? ax : az) : (ay > az ? ay : az), r = Math.round(rF);
        if (r > hi) return false;
        if (r <= solidR) return true;
        const t = surfaceAt(x, y, z, cx, cy, cz, ax, ay, az);
        if (r > t) return false;
        if (!hasCaves || r < caveMinR || r > t - roof) return true;
        return !inCave(cx, cy, cz);
    }

    // ---- SEAS: what stands in the basins below one level (the rules are under ROCKY SEAS, after this function) ----
    // Asked for by KIND, which the caller works out from the world's average temperature and pressure through the phase
    // model (phase.mjs worldSeaOf). This generator knows nothing about temperature: only where the basins are and how
    // full to fill them. Its draws come from a stream of their own ('#seas'), always the same number of them whatever the
    // kind, so asking for a sea cannot move anything else in version 2, and a world's lakes cannot move the amount of
    // them it was dealt.
    const sr = makeRng((addr || '') + '#seas');
    const kSeaAmount = sr(), kSeaCover = sr(), kLineae = sr();
    const LIN = [];                                        // per lineament: its plane's normal, the plane's offset, a noise offset
    for (let i = 0; i < V2_LINEAE_MAX; i++) {
        const dx = sr() * 2 - 1, dy = sr() * 2 - 1, dz = sr() * 2 - 1, dl = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
        LIN.push(dx / dl, dy / dl, dz / dl, (sr() * 2 - 1) * 0.6, sr() * 10000, sr() * 10000, sr() * 10000);
    }
    const nLin = 3 + Math.floor(kLineae * (V2_LINEAE_MAX - 2));   // three to six crack families on an ice world

    // THE LEVEL IS READ OFF A FIXED GRID OF COLUMNS, 24 a side on every face (fewer on a face narrower than that), so it
    // is the same answer whether the renderer has filled the whole height table or a server asks about one cell, and
    // whatever order anybody asked in.
    const SG = Math.min(Wd, V2_SEA_GRID);
    let seaHist = null, seaN = 0;                          // sampled columns per surface level, lo..hi
    function seaHistogram() {
        if (seaHist) return seaHist;
        seaHist = new Int32Array(hi - lo + 1);
        for (let fi = 0; fi < 6; fi++) for (let i = 0; i < SG; i++) for (let j = 0; j < SG; j++) {
            const iu = Math.floor((i + 0.5) * Wd / SG), iv = Math.floor((j + 0.5) * Wd / SG);
            seaHist[heightOfColumn(fi * WW + iu * Wd + iv) - lo]++;
            seaN++;
        }
        return seaHist;
    }

    // LINEAE, the long cracks across an ice sheet. Each is where its own field changes sign: a plane through the world,
    // bent by low noise, so it runs across the whole world in one long arc, as Europa's do, and crosses the others. A
    // column is cracked where a field is positive and one of its eight neighbours on the same face is not. Four would
    // make the thinnest line, but where it runs diagonally its columns would touch only at their corners: a staircase of
    // separate pits, not a crack. With eight it is one column wide along the grid and two on a diagonal, and every
    // column of it shares a side with the next. Kept per column as a byte of signs (bit k for lineament k, 0x80 once
    // known), so each field is read once per column however many cells ask.
    let linSign = null;
    const fl = 1.5 * inv;
    function linSigns(fi, iu, iv) {
        const ti = fi * WW + iu * Wd + iv;
        let s = linSign[ti];
        if (s) return s;
        const ax = fi >> 1, p = [0, 0, 0];
        p[ax] = (fi & 1) ? -refR : refR; p[(ax + 1) % 3] = iu - refR + 0.5; p[(ax + 2) % 3] = iv - refR + 0.5;
        s = 0x80;
        for (let k = 0; k < nLin; k++) {
            const o = k * 7;
            // Where the plane alone is further from zero than the bend can reach, the bend cannot change the sign, so
            // the noise is not read: that is most of every face, and the noise is most of this function's cost. Where it
            // is read, f is the same sum in the same order as ever, so the answer is the same to the last bit.
            const f0 = (p[0] * LIN[o] + p[1] * LIN[o + 1] + p[2] * LIN[o + 2]) * inv - LIN[o + 3];
            const f = (f0 > V2_LINEA_REACH || f0 < -V2_LINEA_REACH) ? f0
                    : f0 + noise((p[0] + LIN[o + 4]) * fl, (p[1] + LIN[o + 5]) * fl, (p[2] + LIN[o + 6]) * fl) * V2_LINEA_WARP;
            if (f > 0) s |= 1 << k;
        }
        return (linSign[ti] = s);
    }
    let crackTab = null;                                   // per face column: 0 not asked yet, 1 intact, 2 cracked
    function cracked(fi, iu, iv) {
        const ti = fi * WW + iu * Wd + iv;
        if (crackTab[ti]) return crackTab[ti] === 2;
        const s = linSigns(fi, iu, iv) & 0x7f;
        let gone = 0;                                      // fields positive here and not at a neighbour
        if (s) for (let du = -1; du <= 1; du++) for (let dv = -1; dv <= 1; dv++) {
            const nu = iu + du, nv = iv + dv;
            if ((du || dv) && nu >= 0 && nu < Wd && nv >= 0 && nv < Wd) gone |= s & ~linSigns(fi, nu, nv);
        }
        crackTab[ti] = (gone & 0x7f) !== 0 ? 2 : 1;
        return crackTab[ti] === 2;
    }

    // One sea per kind asked for, made on first ask. Null when this world's draw leaves its basins dry.
    const seas = new Map();
    function surfaceSea(desc) {
        const kind = desc && desc.kind;
        if (!Object.prototype.hasOwnProperty.call(V2_SEA_AMOUNT, kind)) return null;
        if (seas.has(kind)) return seas.get(kind);
        const [dryBelow, lakesBelow] = V2_SEA_AMOUNT[kind];
        let layer = null;
        if (kSeaAmount >= dryBelow) {
            const ocean = kSeaAmount >= lakesBelow;
            const want = ocean ? 0.45 + 0.45 * kSeaCover : 0.03 + 0.12 * kSeaCover;   // of the surface, before the level rounds
            const h = seaHistogram();
            // The lowest level with at least `want` of the sampled columns below it. A lake fills whatever basin reaches
            // that low, and a basin that crosses an edge makes a lake that crosses it too.
            let S = lo, below = 0;
            while (S <= hi && below < want * seaN) { below += h[S - lo]; S++; }
            // LAKES STAY LAKES. The levels are whole cells, and on a small or a flat world one level can hold a third of
            // the surface, so the level that first reaches `want` can flood far past it. A lake world takes the level
            // below instead when that one comes nearer `want` (by ratio: the one whose cover times want's is nearer
            // want squared) or when this one floods more than V2_LAKES_MAX; and when the level below holds no water
            // at all and this one is too much, the world's basins are too shallow for lakes and it stays dry.
            if (!ocean && S > lo) {
                const under = below - h[S - 1 - lo], w2 = want * seaN * want * seaN;
                if (under > 0 && (under * below > w2 || below > V2_LAKES_MAX * seaN)) { S--; below = under; }
                else if (below > V2_LAKES_MAX * seaN) below = 0;
            }
            if (below > 0) layer = makeSea(kind, desc.name || null, ocean, S, below / seaN);
        }
        seas.set(kind, layer);
        return layer;
    }
    function makeSea(kind, name, ocean, S, cover) {
        const ice = kind === 'ice';
        if (ice && !linSign) { linSign = new Uint8Array(6 * WW); crackTab = new Uint8Array(6 * WW); }
        // What is in cell (x,y,z) before anybody has touched it: SEA_CELL.DRY above the level, in the ground or in a
        // cave (a cave is dry, however deep: nothing here connects it to the sea); LIQUID or ICE between the ground and
        // the level; and on an ice sheet, DRY again in a crack through its top layer and CRACK_FLOOR in the ice under it.
        function at(x, y, z) {
            const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5;
            const ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
            const rF = ax > ay ? (ax > az ? ax : az) : (ay > az ? ay : az), r = Math.round(rF);
            if (r > S || r <= lo) return SEA_CELL.DRY;
            const ti = columnOf(x, y, z, cx, cy, cz, ax, ay, az);
            if (r <= heightOfColumn(ti)) return SEA_CELL.DRY;
            if (!ice) return SEA_CELL.LIQUID;
            if (r < S - 1 || !cracked(colFi, colU, colV)) return SEA_CELL.ICE;
            return r === S ? SEA_CELL.DRY : SEA_CELL.CRACK_FLOOR;
        }
        return Object.freeze({ kind, name, level: S, lowest: lo, amount: ocean ? 'ocean' : 'lakes', cover, lineae: ice ? nLin : 0, at });
    }
    return { isSolid, solidR, airR: hi, surfLoR: lo, caveMinR: hasCaves ? caveMinR : null, roof,
             craters: craters.length / CN, tunnels: nWorms, mountains: mountAmp > 0, mesas: mesaAmt > 0, surfaceSea };
}

// ── ROCKY SEAS (version 2) ──────────────────────────────────────────────────────────────────────────────────────────
// WHICH, from physics; HOW MUCH, from a draw; WHERE, from the terrain.
//   WHICH is the caller's: phase.mjs worldSeaOf reads the world's average temperature and pressure through the same
//   phase model every stored block obeys, and says 'liquid' (Water, Liquid Methane or Liquid Nitrogen), 'ice' (a sheet
//   of Water Ice where water would freeze), 'lava' on the hottest worlds, or nothing. An airless world never holds a
//   liquid, because vacuum is below every triple point.
//   HOW MUCH is the world's own '#seas' draw, by kind (V2_SEA_AMOUNT): its basins stay dry, or hold scattered lakes
//   (3-15% of the surface asked for, never more than V2_LAKES_MAX), or an ocean (45-90%).
//   WHERE is every column whose ground lies below the level, filled flat to it, on whichever faces the basin reaches.
//   The level is one L∞ radius, the same concentric shell the fluid solver calls a layer (fluid-flow.js layerOf), so a
//   sea's surface is level in the game's own gravity and meets itself across a cube edge.
// A point query like isSolid: the level is read once from a fixed grid of columns, and every cell after that costs a
// table lookup (and on an ice sheet one byte of crack signs), so a server can ask about the handful of cells an edit names.
//
// WHAT THAT COMES TO, measured over 20,342 real rocky worlds (galaxy 930718, positions 1-2000, each world's average
// environment built as computeBodyEnv builds it): none 72.5%, lakes 19.8%, oceans 7.7%. By kind:
//   Water             possible on 4.8% of worlds:  lakes 1.9%, oceans 1.4%
//   Liquid Methane    possible on 0.7%:            lakes 0.30%, seas 0.26%
//   Liquid Nitrogen   possible on 0.7%:            lakes 0.28%, seas 0.22%
//   Water Ice         possible on 55%:             frozen lakes 16.5%, ice sheets 5.4%
//   lava              possible on 1.4%:            lakes 0.74%, oceans 0.34%
// A lake world's lakes cover a median 9% of it (a tenth of them over 18%, none over 25%), on small worlds as on big ones;
// an ocean covers 46-100% (median 78%).
// tests/world-seas.test.mjs recounts the three shares on a smaller sample and holds them near these.
//
// A SEPARATE LAYER, NEVER PART OF isSolid. The rock is the same whatever the sea, so everything that already reads the
// rock (the ore rule, the rock scatter, the servers' checks) is untouched, and a sea cannot make the renderer and a
// server disagree about a single rock cell. Ice is solid ground to stand on, but the renderer adds it on top of the rock.
// ORE NEVER TOUCHES A SEA: a vein needs six neighbours of rock (ore-veins.js buried), and a sea cell is not rock.
const V2_SEA_GRID = 24;
// [dry below, lakes below]: the share of worlds of each kind whose basins stay dry, and the share of the rest (up to the
// second number) that hold lakes rather than an ocean. Ice is the commonest kind by far (over half of all worlds are cold
// enough for it), so more of its worlds stay dry; lava worlds are rare and a lava lake is what makes one.
const V2_SEA_AMOUNT = { liquid: [0.3, 0.7], ice: [0.6, 0.9], lava: [0.25, 0.8] };
const V2_LAKES_MAX = 0.25;                                 // the most of a surface a lake world's lakes may cover
const V2_LINEAE_MAX = 6, V2_LINEA_WARP = 0.22;
// How far the bend can move a lineament's field: makeNoise blends eight gradient terms, each at most 2 in size (two
// offsets within one cell), so the blend is at most 2 as well, scaled by 1.2 to 2.4; times the warp, 0.528. Held at 2.5
// times the warp for the rounding of the blend. linSigns skips the noise wherever the plane is further off than this.
const V2_LINEA_REACH = V2_LINEA_WARP * 2.5;
export const SEA_CELL = Object.freeze({ DRY: 0, LIQUID: 1, ICE: 2, CRACK_FLOOR: 3 });
const NO_SEA = () => null;

// ── ROCKY WORLDS, VERSION 3: LAND THAT MATTERS ─────────────────────────────────────────────────────────────────────
// Version 2 went live on 2026-09-28 and is frozen with every claim made on it. Version 3 is what the owner's review of
// 32 version 2 worlds asked for, as a generator of its own. Version 2's function is not called, shared or edited here:
// its machinery (the height table, the seam joining, the caves, the lineae, the histogram) is COPIED, so that nothing
// done to version 3 can move a cell of version 2.
//
// WHAT CHANGES, from the review:
//   * A WORLD-SCALE LANDFORM ON EVERY WORLD. Big flat expanses lost the owner's interest and strong relief won, so each
//     world is dealt a signature (V3_SIGNATURES) from a stream of its own: a great range along an arc round the world,
//     a great crater about half a face across, a field of mesas, or a canyon. It is laid over the character version 2
//     dealt the world (its craters, ranges, mesas and caves, read from the same '#relief' draws), so a cratered world
//     stays cratered and gains one thing big enough to read from orbit.
//   * CONTINENTS. A low field raises continents and sinks basins, with a steeper slope between them where a coast
//     falls, so a sea fills basins between land masses instead of drowning a level of hills.
//   * SMOOTHER RANGES. Version 2's ridged noise, squared, stood up as rows of thin parallel crests: the sawtooth of its
//     hottest world. Here a range is a broad crest at a lower frequency, and the great range one chain with foothills.
//   * VALLEYS in the lowlands of most worlds: shallow winding channels, dry on a dry world, and on a wet or molten
//     one the arms of its seas and the rivers of its lava.
//   * THE CRUST FOLLOWS THE LAND. Version 2's material field is version 1's three-dimensional noise, which read at world
//     scale as a patchwork (W08's pink-grey checkering, W13's cyan-grey noise). Here one crust covers the lowlands and
//     basins and the other the highlands, in large patches, and a cliff shows the two in strata (surfMat, below). So the
//     material depends on the terrain, and the terrain on the world's size: version 3's material field needs the
//     diameter (rockySurfMat), where version 1's and 2's did not.
//   * SEAS OF A QUARTER TO A HALF (VERSION 3 SEAS, below): oceans and ice sheets cover 25-55% of the surface; an ice
//     sheet has low pressure ridges and hummocks along its cracks; lava comes from the world's temperature.
//   Lakes are version 2's rule (the review liked every lake world), with a little more room on version 3's flatter plains.
//
// Everything else is version 2's: relief scaled with the world, the one rule at an edge (nothing above R, eased), the
// seams joined, the caves (kept a roof under every face's ground, not only their own), and exactly-specified arithmetic
// only (tests/worldgen-golden.test.mjs refuses the rest).
export const V3_SIGNATURES = Object.freeze(['range', 'crater', 'mesas', 'canyon']);
export const V3_BOLD_MAX_R = 20;                            // a small world, for the bold landform: Ø40 and under
const V3_SIG_ODDS = [0.3, 0.55, 0.75];                     // cumulative: a range 30%, a crater 25%, mesas 20%, a canyon 25%
const V3_VALLEYS_WIDE_R = 18;                              // Ø36 and up: valleys over a cell wide; below that, one column
const V3_PROBE_N = 20;                                     // crustDominant's probe grid: 6 · 20² cells of the surface shell
const V3_JOIN = 6;                                         // columns over which two faces' heights are joined at an edge (sample)
// A column's look under its biomes (rockyTerrainV3's biome table): 0 none, a biome of the catalog by its index + 1
// (biomes.mjs BIOMES), and three looks of their own at the table's top: a lava pool, the warm ground round one, a
// crystal spire. They were 32 to 34 until the atlas's later biomes (Sep 30) took the catalog past 31, where they read as
// biomes of their own (a rime fringe as a lava pool, a crystal spire as a lake); the catalog must stay under 253.
const BIO_LAVA = 253, BIO_WARM = 254, BIO_CRYSTAL = 255;
const BIO_SULFUR = 252, BIO_RIFT = 251;
const BIO_PITCH = 250, BIO_BRINE = 249, BIO_MUD = 248;      // version 12's own: a pitch lake's tar, a halite field's brine, a mudpot's puddle
// VERSION 12 HAS NO LATITUDE (climate.mjs opts.flat): what its turning worlds' climate is asked with.
const FLAT_CLIMATE = Object.freeze({ flat: true });
export const FLAT_CLIMATE_SINCE = 12;   // the world version whose turning worlds keep one temperature, and whose woods read the world's
// VERSION 13 HAS ONE TEMPERATURE ON EVERY WORLD (climate.mjs opts.oneTemp; the owner, Oct 12: "change world gen so the
// entire worlds are one temperature — seasons only affects daylight/geometry"): a world locked to its star is flat too,
// its biomes, life and turf reading the one number the card shows, as a turning world's have since version 12.
const ONE_TEMP_CLIMATE = Object.freeze({ flat: true, oneTemp: true });
export const ONE_TEMP_SINCE = 13;
// VERSION 14, "MINE WHAT'S DRAWN" (the owner, Oct 12: keep every world's look, and make the ground the kube it is drawn
// as): a cell of the ground whose biome draws it as a tint over the crust is the kube that tint is drawn on. See
// rockyTerrainV4's DRAWN KUBE, at its end, and ROCKY_GENERATORS[14].
export const DRAWN_KUBE_SINCE = 14;
// VERSION 15, "TUFTS ARE PAINTED" (the owner's turf picks, Oct 12: "no small swaying tuft kubes — just tuft texture"): turf
// wears its tufts in its own texture (texture-turf.js), so a grass tuft whose ground is the seed's turf grows no more
// (flora.mjs ctx.turfAt). See ROCKY_GENERATORS[15].
export const TUFT_TEXTURE_SINCE = 15;
const SPECK = 0.01, SPECK_GRID = 24;                        // version 4: a biome picked on under 1% of a 24² grid a face is left off
const FOREST_MOIST = 0.1;                                   // version 4: how moist a forest's ground must be (0.22 for the rest)                    // version 4's own: a sulfur spring's acid-green pool, a void rift's fissure

// ── VERSION 3'S GENOME (WG-08, the terrain evolution) ─────────────────────────────────────────────────────────────────
// The owner teaches the generator what they like by selection: every round renders a new random set of worlds, each
// built from a GENOME sampled from a pool (tools/terrain-evolve/pool.json), and the owner's likes and dislikes move the
// pool. So every knob version 3 has is a gene here, and each gene's DEFAULT is exactly the value version 3 was built and
// frozen with: rockyFields(addr, dia, 3) with no genome, with {} and with V3_GENE_DEFAULTS are the same world, cell for
// cell (tests/world-genome.test.mjs), and the golden hashes do not move. A genome is plain data, and a world's genome is
// part of its generation input beside its address, diameter and version: the same genome builds the same world on
// every side, in any order.
//
// A gene is { def, min, max, type }. min and max are the generator's own hard limits, which no pool can widen: inside
// them the core stays whole, ore stays buried, a landing column has room to stand and no thin roof, the seas stay in
// their basins and nothing stands above R at an edge (tests/world-genome.test.mjs sweeps sampled genomes and every
// gene at its min, then its max, for all of it). That a column a landing steps in from an edge finds ground within
// eight columns is held for the pool's genomes only: a canyon over a corner of a big world can defeat it, at the
// defaults too (4 of 300 real worlds of Ø64 and more). Types: 'num' a number, clamped; 'int' a whole number, rounded and clamped; 'bool' on or off; 'choice' one of
// `choices`, or null for the world's own draw. A gene with `look` changes how a world is drawn (system-view.js reads it),
// never what it is made of. BOOSTS shift one of the world's own draws (a boost of 0.2 turns about a fifth more worlds
// that way), and SCALES multiply what today's rule makes, so version 3's per-world variety survives inside every genome.
const V3_NUM = (def, min, max) => ({ def, min, max, type: 'num' });
export const V3_GENES = Object.freeze({
    // THE LAND. landformScale multiplies the height of every hill, range, mesa and signature landform.
    landformScale: V3_NUM(1, 0.5, 1.6),
    reliefUp: V3_NUM(0.24, 0.18, 0.3),                    // how far the ground may rise above R, as a share of R
    reliefDown: V3_NUM(0.28, 0.2, 0.34),                  // how far it may fall below R
    sink: V3_NUM(0.06, 0, 0.12),                          // how far below R the land's middle stands
    warp: V3_NUM(0.2, 0, 0.4),                            // how far features bend off a lattice
    continentScale: V3_NUM(1.05, 0.6, 2.2),               // the continents' frequency: higher is more, smaller land masses
    continentAmp: V3_NUM(0.066, 0, 0.12),                 // the continents' broad rise and fall, × R
    coastAmp: V3_NUM(0.054, 0, 0.1),                      // the step at a coast, × R
    coastSteep: V3_NUM(2.2, 1, 6),                        // how sharply the land rises there
    hillAmp: V3_NUM(0.05, 0, 0.1),                        // hills, × the landform height
    hillScale: V3_NUM(2.8, 1.5, 5),                       // the hills' frequency
    roughness: V3_NUM(1, 0, 2),                           // the small-scale bumps, × today's
    valleyBoost: V3_NUM(0, -0.3, 0.75),                   // more (or fewer) worlds with valleys
    valleyDepth: V3_NUM(0.03, 0, 0.08),                   // a valley's depth, × R (plus one level)
    valleyWidth: V3_NUM(1, 0.5, 2.5),                     // a valley's width, × today's
    rangeBoost: V3_NUM(0, -0.35, 0.65),                   // more (or fewer, or bigger) mountain ranges
    rangeHeight: V3_NUM(1, 0.3, 1.8),                     // their height, × today's
    rangeSharpness: V3_NUM(2, 1, 5),                      // their crests: low is broad and soft, high is sharp
    mesaBoost: V3_NUM(0, -0.55, 0.45),                    // more (or fewer) worlds with mesas
    mesaHeight: V3_NUM(1, 0.5, 1.8),                      // their height, × today's
    mesaTerraces: { def: 3, min: 1, max: 5, type: 'int' },   // how many terraces a mesa steps up in
    craterBoost: V3_NUM(0, -0.2, 0.8),                    // more (or fewer) cratered worlds, and denser craters
    craterCount: V3_NUM(1, 0, 2.5),                       // the number of craters, × today's
    craterSize: V3_NUM(1, 0.5, 1.6),                      // the biggest crater's radius, × today's
    craterDepth: V3_NUM(1, 0.4, 1.6),                     // a crater's depth, × today's
    craterRim: V3_NUM(1, 0, 2),                           // a crater's rim, × today's
    caveBoost: V3_NUM(0, -0.3, 0.7),                      // more (or fewer) worlds with caves
    // THE SIGNATURE LANDFORM: its kind (null: the world's own draw, 30/25/20/25), its height and its breadth.
    signature: { def: null, type: 'choice', choices: V3_SIGNATURES },
    signatureScale: V3_NUM(1, 0.5, 1.6),
    signatureWidth: V3_NUM(1, 0.6, 1.6),
    // THE CRUST. crustFollowsLand off is version 1's noise patchwork; on, one crust on the lowlands (lowlandShare of the
    // surface) and the other on the highlands, in patches patchScale × today's and nudged patchNoise × R levels.
    crustFollowsLand: { def: true, type: 'bool' },
    lowlandShare: V3_NUM(0.7, 0.3, 0.9),
    patchScale: V3_NUM(1, 0.5, 3),
    patchNoise: V3_NUM(0.05, 0, 0.15),
    strata: { def: true, type: 'bool' },                   // cliffs banded in the two crusts
    strataThickness: { def: 0, min: 0, max: 5, type: 'int' },   // cells a band: 0 is the world's own (2 or 3)
    // THE SEAS. seaBoost shifts the draw that makes a world dry, lakes or an ocean; the covers are shares of the surface.
    seaBoost: V3_NUM(0, -0.3, 0.3),
    oceanCoverMin: V3_NUM(0.25, 0.1, 0.5),
    oceanCoverMax: V3_NUM(0.55, 0.2, 0.75),
    oceanCap: V3_NUM(0.62, 0.35, 0.9),
    lakeScale: V3_NUM(1, 0.3, 2.5),
    lakeCap: V3_NUM(0.33, 0.15, 0.5),
    iceRidges: { def: true, type: 'bool' },                // an ice sheet's pressure ridges and hummocks
    ridgeRate: V3_NUM(0.3, 0, 1),                         // how unbroken the ridges along a crack are
    hummockRarity: V3_NUM(0.45, 0.1, 1),                  // how rare the hummocks beside them are
    crackFamilies: { def: 0, min: 0, max: 6, type: 'int' },   // families of lineae: 0 is the world's own (3 to 6)
    // LAVA, by temperature: lakes cover lavaLakeCover at 700 °C rising by lavaLakeRise to lavaOceanFrom, then a molten
    // surface of lavaMoltenCover rising by lavaMoltenRise to lavaFullAt.
    lavaLakeCover: V3_NUM(0.1, 0.03, 0.3),
    lavaLakeRise: V3_NUM(0.15, 0, 0.3),
    lavaOceanFrom: V3_NUM(1200, 900, 1600),
    lavaMoltenCover: V3_NUM(0.55, 0.3, 0.8),
    lavaMoltenRise: V3_NUM(0.35, 0, 0.45),
    lavaFullAt: V3_NUM(2000, 1600, 3000),
    lavaLakeCap: V3_NUM(0.35, 0.15, 0.5),
    // NEW IDEAS (round 2), each off unless a genome turns it on (ROUND 2'S NEW IDEAS, below).
    volcanoes: { def: false, type: 'bool' },
    volcanoCount: V3_NUM(1, 0.3, 2.5),
    volcanoSize: V3_NUM(1, 0.6, 1.6),
    spires: { def: false, type: 'bool' },
    spireDensity: V3_NUM(1, 0.3, 2.5),
    spireHeight: V3_NUM(1, 0.5, 2),
    tablelands: { def: false, type: 'bool' },
    tableHeight: V3_NUM(1, 0.5, 1.8),
    tableCover: V3_NUM(1, 0.4, 1.8),
    dunes: { def: false, type: 'bool' },
    duneHeight: V3_NUM(1, 0.5, 2),
    duneSpacing: V3_NUM(1, 0.6, 2),
    // ROUND 3'S NEW IDEAS (the owner's picks after round 2), each off by default. (Lava channels were tried and taken
    // out at the owner's word: lava is pools, lakes and seas, only.) BASALT COLUMNS: packed fields of pillars one and two columns across, stepping down to the coasts.
    basaltColumns: { def: false, type: 'bool' },
    basaltCover: V3_NUM(1, 0.5, 1.6),
    basaltStep: { def: 1, min: 1, max: 2, type: 'int' },
    // ICE CREVASSES AND ICE CLIFFS: deep cuts across the ice, walled in ice, and a sheer wall of ice where it meets land.
    iceCrevasses: { def: false, type: 'bool' },
    crevasseDepth: V3_NUM(1, 0.5, 1.5),
    crevasseDensity: V3_NUM(1, 0.5, 1.6),
    iceWallHeight: { def: 3, min: 0, max: 4, type: 'int' },
    // ONE BOLD LANDFORM on a small world (V3_BOLD_MAX_R, Ø40 and under): the signature landform boldScale taller and half
    // as much again broader, the busy small features (craters, hills, the small bumps) boldThin of what they were.
    boldSmall: { def: false, type: 'bool' },
    boldScale: V3_NUM(1.5, 1, 1.6),
    boldThin: V3_NUM(0.4, 0.2, 1),
    // BIOMES, wave 1 (WG-09, tools/terrain-evolve/BIOMES.md; biomes.mjs is their catalog, and their names are its ids).
    // Each is off by default, and nothing at all unless the caller also says what world this is (rockyFields' `world`:
    // its star, climate and sea), because a biome keeps to its climate. The first the genome turns on is the world's
    // dominant biome, the rest secondary.
    duneSea: { def: false, type: 'bool' },
    saltFlats: { def: false, type: 'bool' },
    glacier: { def: false, type: 'bool' },
    volcanicFields: { def: false, type: 'bool' },
    crystalFields: { def: false, type: 'bool' },
    lichenSteppe: { def: false, type: 'bool' },
    // …and wave 2 (WG-10), living biomes: their trees, towers and growths are organics.mjs's, their small growths
    // flora.mjs's, and each grows only on a world whose life is of its chemistry (biomes.mjs lifeChemistryOf).
    woods: { def: false, type: 'bool' },
    bloomCarpets: { def: false, type: 'bool' },
    gillTowers: { def: false, type: 'bool' },
    tidalGardens: { def: false, type: 'bool' },
    nightsideGlow: { def: false, type: 'bool' },
    hothouseCanopy: { def: false, type: 'bool' },
    spiritGardens: { def: false, type: 'bool' },
    tholinReefs: { def: false, type: 'bool' },
    // THE LOOK (system-view.js): a sea's colour (hue turned by seaHue degrees, lightness and clarity × today's) and its
    // glints' chop; a lava sea's plates (kubes across), glow, seams and drift; how far saturated crusts are tamed from
    // orbit (the chroma cap, and the share of the excess kept); an ice sheet's tone.
    seaHue: { def: 0, min: -60, max: 60, type: 'num', look: true },
    seaLightness: { def: 1, min: 0.6, max: 1.6, type: 'num', look: true },
    seaClarity: { def: 1, min: 0.4, max: 1.8, type: 'num', look: true },
    seaChop: { def: 1, min: 0, max: 3, type: 'num', look: true },
    lavaPlate: { def: 6, min: 3, max: 14, type: 'num', look: true },
    lavaGlow: { def: 1, min: 0.5, max: 2, type: 'num', look: true },
    lavaSeams: { def: 1, min: 0.5, max: 2.2, type: 'num', look: true },
    lavaDrift: { def: 1, min: 0, max: 3, type: 'num', look: true },
    tameCap: { def: 0.24, min: 0.1, max: 0.8, type: 'num', look: true },
    tameKeep: { def: 0.35, min: 0.1, max: 1, type: 'num', look: true },
    iceTone: { def: 1, min: 0, max: 2, type: 'num', look: true },
});
export const V3_GENE_DEFAULTS = Object.freeze(Object.fromEntries(Object.keys(V3_GENES).map((k) => [k, V3_GENES[k].def])));
// A genome with every gene, each checked and held inside its limits: a missing or malformed gene is its default, so a
// pool (or a hand) can never ask the generator for something it was not built to make.
export function v3Genome(g) {
    if (!g || typeof g !== 'object') return V3_GENE_DEFAULTS;
    const out = {};
    for (const k of Object.keys(V3_GENES)) {
        const s = V3_GENES[k], v = g[k];
        let x = s.def;
        if (s.type === 'bool') { if (typeof v === 'boolean') x = v; }
        else if (s.type === 'choice') { if (v === null || s.choices.indexOf(v) >= 0) x = v; }
        else if (typeof v === 'number' && v === v) {
            x = s.type === 'int' ? Math.round(v) : v;
            x = x < s.min ? s.min : x > s.max ? s.max : x;
        }
        out[k] = x;
    }
    if (out.oceanCoverMax < out.oceanCoverMin) out.oceanCoverMax = out.oceanCoverMin;
    if (out.lavaFullAt < out.lavaOceanFrom + 100) out.lavaFullAt = out.lavaOceanFrom + 100;
    return Object.freeze(out);
}

// ── VERSION 3 AS IT SHIPS: EVERY WORLD'S OWN GENOME ─────────────────────────────────────────────────────────────────
// A real world's version 3 genome is drawn from the frozen pool (v3-pool.mjs) by its address, so its terrain is a
// function of its address alone, as every version's is, and every side that builds it (the renderer, the Cloud
// Functions, the zone server, a share card) draws the same one. rockyFields and rockySurfMat use it at version 3 when
// no genome is given; a genome that is given (the terrain evolution's, or {} for version 3's defaults) is used instead.
//
// THE RULE OF A DRAW, which the terrain evolution's sampleGenome uses too (tools/terrain-evolve/genome.mjs), so a card
// in the deck and a real world are drawn alike: the pool's genes in name order, each read from its own stream
// (streamFor(gene)), so adding or dropping a gene never moves another's draw. A number or an integer is its mean plus
// its spread times a triangular jitter in (-1, 1), kept inside its range (an integer rounded, a number to four places);
// a choice is picked by its weights ('auto' is none); a trait is on with its probability, unless `fixed` sets it; an
// extinct gene is its default. Only exactly specified arithmetic (sums, products, toFixed, round), so every engine
// draws the same numbers. The answer is v3Genome's: every gene, held inside the generator's limits, frozen.
export function v3SampleGenome(genes, streamFor, fixed) {
    const raw = {};
    for (const gene of Object.keys(genes).sort()) {
        const p = genes[gene];
        if (!V3_GENES[gene]) continue;
        if (p.status === 'extinct') { raw[gene] = p.default; continue; }
        const r = streamFor(gene);
        if (p.type === 'number' || p.type === 'integer') {
            const t = r() + r() - 1;
            let v = p.mean + p.spread * t;
            v = v < p.min ? p.min : v > p.max ? p.max : v;
            raw[gene] = p.type === 'integer' ? Math.round(v) : +v.toFixed(4);
        } else if (p.type === 'choice') {
            const opts = Object.keys(p.weights).filter((k) => p.weights[k] > 0);
            const total = opts.reduce((a, k) => a + p.weights[k], 0);
            let u = r() * total, pick = opts[opts.length - 1];
            for (const k of opts) { if (u < p.weights[k]) { pick = k; break; } u -= p.weights[k]; }
            raw[gene] = pick === 'auto' ? null : pick;
        } else if (p.type === 'trait') {
            raw[gene] = fixed && Object.prototype.hasOwnProperty.call(fixed, gene) ? !!fixed[gene] : r() < p.probability;
        }
    }
    return v3Genome(raw);
}
// A world's own version 3 genome: its address's draw from the frozen pool, with every new idea and every biome at its
// probability (none of the live deck's quotas or its dealing), and a rare biome only on a world its roll allows (biomes.mjs
// rareRollPasses: crystal fields are 1 world in 12, and then at their probability), which the map, dealing whatever a
// genome turns on as the live deck needs, does not ask. Remembered for a few hundred addresses.
export const V3_POOL_ID = V3_POOL.id;
const GENOME_OF = new Map();
export function genomeOf(addr) {
    const a = String(addr || '');
    let g = GENOME_OF.get(a);
    if (!g) {
        if (GENOME_OF.size >= 512) GENOME_OF.clear();
        const fixed = {};
        for (const id of BIOME_IDS) if (!rareRollPasses(id, a)) fixed[id] = false;
        g = v3SampleGenome(V3_POOL.genes, (gene) => makeRng(V3_POOL.id + '#' + a + '#' + gene), fixed);
        GENOME_OF.set(a, g);
    }
    return g;
}
// A genome's fingerprint, as a claim records it beside the pool it came from: FNV-1a (32 bit) over the genome as JSON
// with its keys sorted, as 8 lowercase hex digits (the live deck's genomeHash8, tools/terrain-evolve/cards.mjs).
export function genomeHashOf(g) {
    const o = {};
    for (const k of Object.keys(g || {}).sort()) o[k] = g[k];
    const s = JSON.stringify(o);
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193);
    return (h >>> 0).toString(16).padStart(8, '0');
}

// THE WORLD A VERSION 3 WORLD'S BIOMES FOLLOW, AS IT IS RECORDED. Its biomes follow its star, its average climate and its
// sea (rockyFields' `world`), and the climate is worked out from light and air in floating point, which two machines
// can round a hair apart and a later change to the climate model could move, when a biome shapes the ground that every
// build on the world is a difference from. So a claim records it, as it records the sea (recordedSeaOf), and every side
// builds from the record: the star's name, the temperature in whole degrees, the pressure in whole millibars and
// whether the world is airless. An unclaimed world's is worked out and rounded the same way, so claiming it moves
// nothing. worldRecordOf(env, star) makes a record from a climate ({ tempC, pressureMbar, airless }) and a star's name,
// or null without a climate; recordedWorldOf(rec, addr, sea) makes the world rockyFields takes from a record and the
// sea recordedSeaOf gives, null for an unusable record, undefined for none (so a caller can tell "none" from "not
// known", as with the sea). worldKeyOf(world) names a world's inputs, for a cache.
// …AND ITS STARLIGHT (version 23, the owner's bark rule, Oct 23: organics.mjs barkOf): `flux`, the light its stars give it
// (temperature.mjs irradiance over the solar constant: 1 at the habitable-zone centre, the number the suit charges by),
// to four places, when the climate it is made from carries one (env.flux). A record from before has none, and a world
// built from it is lit as the median life world (organics.mjs BARK_FLUX_UNKNOWN); below version 23 nothing reads it.
export const roundFlux = (f) => Math.round(f * 10000) / 10000;
export function worldRecordOf(env, star) {
    if (!env || typeof env.tempC !== 'number' || env.tempC !== env.tempC) return null;
    const out = { star: typeof star === 'string' ? star : null, tempC: Math.round(env.tempC),
                  pressureMbar: env.airless ? 0 : Math.round(Number(env.pressureMbar) || 0), airless: !!env.airless };
    if (Number.isFinite(env.flux) && env.flux >= 0) out.flux = roundFlux(env.flux);
    return out;
}
export function recordedWorldOf(rec, addr, sea) {
    if (rec === undefined) return undefined;
    if (!rec || typeof rec !== 'object' || typeof rec.tempC !== 'number' || rec.tempC !== rec.tempC) return null;
    const out = { addr: String(addr || ''), star: typeof rec.star === 'string' ? rec.star : null, tempC: rec.tempC,
                  pressureMbar: rec.airless ? 0 : (Number(rec.pressureMbar) || 0), airless: !!rec.airless, sea: sea || null };
    if (Number.isFinite(rec.flux) && rec.flux >= 0) out.flux = rec.flux;
    return out;
}
export function worldKeyOf(w) {
    return !w ? '-' : [w.star, w.tempC, w.pressureMbar, w.airless ? 1 : 0, w.sea ? [w.sea.kind, w.sea.name, w.sea.tempC].join('/') : '-'].join('|')
        + (w && Number.isFinite(w.flux) ? '|f' + w.flux : '');   // (version 23's flux: a key from before reads as it did)
}
// A boost moves one of the world's own draws, kept in the draw's range; with no boost the draw is untouched.
function v3Boost(k, b) {
    if (b === 0) return k;
    const x = k + b;
    return x < 0 ? 0 : x > 0.9999999999 ? 0.9999999999 : x;
}
// A number in [0, 1) for a whole-number position, from integer arithmetic only (makeNoise's hash, reseeded).
function v3Hash(x, y, z, seed) {
    let h = seed + (x | 0); h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
    h += y | 0; h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
    h += z | 0; h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
    return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
}

function rockyTerrainV3(d, R, addr, genome, world) {
    const G = v3Genome(genome);
    // ONE BOLD LANDFORM (round 3's new idea) acts on a small world only. Everywhere else its factors are 1, and x * 1 is x.
    const bold = G.boldSmall && R <= V3_BOLD_MAX_R, bS = bold ? G.boldScale : 1, bT = bold ? G.boldThin : 1;
    const noise = d.noise;
    const refR = Math.max(1, R - 2), inv = 1 / refR;
    const up = Math.max(3, Math.round(R * G.reliefUp)), down = Math.max(4, Math.round(R * G.reliefDown));
    const lo = R - down, hi = R + up;                      // the surface is always within [lo, hi]
    // THE LANDFORMS' HEIGHT, a little under R on the smallest worlds. A landform is the same share of a face at every
    // size, so on a Ø24 or Ø32 world a range or a mesa is a few cells wide and as many tall, and read as big blocky
    // steps (W03 in the review). Their heights are scaled by Rs: 55% of R at Ø24, all of it from about Ø72.
    const kS = R >= 36 ? 1 : 0.55 + 0.45 * (R - 12) / 24;
    const Rs = R * (kS < 0.55 ? 0.55 : kS) * G.landformScale;
    // BIOMES (WG-09): the genome's biomes that the world's climate allows (biomes.mjs), or none: the genome turns none on,
    // or the caller did not say what world this is (its star, climate and sea). The sea their rules read is the one this
    // world holds: `world.sea` says what its basins would hold (phase.mjs worldSeaOf), and its basins are dry by the
    // same draw surfaceSea reads (a dry world has no water to freeze into a glacier, and room for dunes and salt flats).
    const bioOn = world ? BIOME_IDS.filter((id) => G[id]) : [];
    const bioSea = !bioOn.length || !world.sea ? null : world.sea.kind === 'lava' ? world.sea
        : (world.sea.kind === 'liquid' || world.sea.kind === 'ice') && v3Boost(makeRng((addr || '') + '#seas')(), G.seaBoost) >= V3_SEA_AMOUNT[world.sea.kind][0] ? world.sea : null;
    const bioWorld = bioOn.length ? { ...world, sea: bioSea } : null, bioClim = bioWorld ? climateOf(bioWorld) : null;
    const bm = bioOn.length ? biomeMap(bioClim, bioWorld, bioOn, refR, { caps: false }) : null;   // version 3's map, as frozen: no caps, no spread
    const bw = bm ? new Float64Array(bm.ids.length) : null, bk = bm ? bm.ids.map((id) => BIOME_IDS.indexOf(id)) : null;

    // ---- CHARACTER: version 2's five draws, so a world keeps the kind of world it was (moved by the genome's boosts) ----
    const rel2 = makeRng((addr || '') + '#relief');
    for (let i = 0; i < 36; i++) rel2();                   // version 2's twelve offsets, three draws each
    const kCrater = v3Boost(rel2(), G.craterBoost), kMount = v3Boost(rel2(), G.rangeBoost), kMesa = v3Boost(rel2(), G.mesaBoost);
    const kCave = v3Boost(rel2(), G.caveBoost), kMesaTop = rel2();
    const craterDensity = kCrater < 0.2 ? kCrater * 0.25 : 0.1 + 0.9 * ((kCrater - 0.2) / 0.8) * ((kCrater - 0.2) / 0.8);
    const mountAmp = kMount < 0.35 ? 0 : (kMount - 0.35) / 0.65;
    const mesaAmt = kMesa < 0.55 ? 0 : (kMesa - 0.55) / 0.45;
    const caveAmt = kCave < 0.3 ? 0 : (kCave - 0.3) / 0.7;
    const mesaTop = 1 + Math.round((0.08 + 0.08 * kMesaTop) * Rs * G.mesaHeight);
    const mesaStep = Math.max(2, Math.round(Rs * 0.06 * G.mesaHeight));

    // ---- VERSION 3'S OWN DRAWS. Its large fields are read in the unit cube's space (a column's point divided by the
    // reference radius), so a feature is the same share of a face on every size of world. ----
    const rel = makeRng((addr || '') + '#relief3');
    const o3 = () => [rel() * 1000, rel() * 1000, rel() * 1000];
    const W1 = o3(), W2 = o3(), W3 = o3(), OC = o3(), OH = o3(), OG = o3(), OV = o3(), OM = o3(), ON = o3();
    const OS = o3(), OL = o3(), OP = o3(), OU = o3(), OD = o3(), OR = o3(), OB = o3(), OA = o3(), OK = o3();
    const kValley = rel(), kStrata = rel(), kStrataT = rel();
    const valleys = kValley + G.valleyBoost >= 0.25;
    // A channel's half-width in the noise's own units: near a zero the field changes by about three units per unit of
    // the cube at this frequency, and a cell is `inv` of a unit. About 1.3 cells from Ø36 up; below that a channel that
    // wide would be most of a basin, so a small world's are a single column.
    const valleyW = (R >= V3_VALLEYS_WIDE_R ? 3.9 : 2.4) * G.valleyWidth * inv, valleyD = 1 + G.valleyDepth * R;

    // ---- THE SIGNATURE: one landform big enough to read from orbit. Always the same fourteen draws, whichever it is ----
    const sr3 = makeRng((addr || '') + '#sig3');
    const kSig = sr3();
    const sig = G.signature !== null ? V3_SIGNATURES.indexOf(G.signature)
        : kSig < V3_SIG_ODDS[0] ? 0 : kSig < V3_SIG_ODDS[1] ? 1 : kSig < V3_SIG_ODDS[2] ? 2 : 3;
    const unitDir = () => {
        const x = sr3() * 2 - 1, y = sr3() * 2 - 1, z = sr3() * 2 - 1, l = Math.sqrt(x * x + y * y + z * z) || 1;
        return [x / l, y / l, z / l];
    };
    const sN = unitDir(), sM0 = unitDir(), sC = unitDir();
    const sA = sr3(), sB = sr3(), sF = sr3(), sG = sr3();
    // The arc a range or a canyon follows: the great circle whose plane has normal sN, bent by noise, over the part of
    // it on sM's side (about 215° of the circle), fading at its two ends.
    const sMd = sM0[0] * sN[0] + sM0[1] * sN[1] + sM0[2] * sN[2];
    let sM = [sM0[0] - sMd * sN[0], sM0[1] - sMd * sN[1], sM0[2] - sMd * sN[2]];
    const sMl = Math.sqrt(sM[0] * sM[0] + sM[1] * sM[1] + sM[2] * sM[2]);
    sM = sMl > 1e-6 ? [sM[0] / sMl, sM[1] / sMl, sM[2] / sMl] : [sN[1], sN[2], sN[0]];
    const sgW = G.signatureWidth * (bold ? 1 + (bS - 1) * 0.5 : 1), sgH = G.signatureScale * bS;
    const rangeW = Math.max(4.5, 0.17 * refR) * inv * sgW, rangeA = 0.3 * Rs * sgH;   // half-width (unit sphere), height
    const canyonF = Math.max(1.5, 0.045 * refR) * inv * sgW, canyonWall = Math.max(2, 0.07 * refR) * inv * sgW;
    const canyonD = Math.max(4, Math.round(0.2 * Rs * (0.9 + 0.2 * sA) * sgH));
    // The great crater: its centre on a face, within the middle of it, in cells on the reference cube as the other
    // craters are, so it carries on over an edge its bowl reaches.
    const gcFace = Math.floor(sF * 6), gcAx = gcFace >> 1, gcP = [0, 0, 0];
    gcP[gcAx] = (gcFace & 1) ? -refR : refR;
    gcP[(gcAx + 1) % 3] = (sC[0] * 0.5) * refR; gcP[(gcAx + 2) % 3] = (sC[1] * 0.5) * refR;
    const gcRho = (0.42 + 0.2 * sA) * refR * sgW, gcInv = 1 / gcRho, gcDepth = 0.17 * R * (0.9 + 0.2 * sB) * sgH;
    const gcRim = 0.07 * R * sgH, gcOuter2 = 2.56 * gcRho * gcRho;
    // The mesa field: a region about 45° across round sC, full of buttes on two or three terraces.
    const mfTop = 1 + Math.round((0.1 + 0.06 * sG) * Rs * sgH), mfReach = 0.8 * sgW;

    function signature(ux, uy, uz, px, py, pz, H) {
        if (sig === 1) {                                   // THE GREAT CRATER
            const dx = px - gcP[0], dy = py - gcP[1], dz = pz - gcP[2], d2 = dx * dx + dy * dy + dz * dz;
            if (d2 >= gcOuter2) return H;
            const q = Math.sqrt(d2) * gcInv;
            if (q >= 1) { const s = (1.6 - q) / 0.6; return H + gcRim * s * s; }
            let h = -gcDepth * (1 - q * q) + gcRim * q * q * q * q;
            if (q < 0.22) { const s = 1 - q / 0.22; h += gcDepth * 0.4 * s * s; }        // a central peak
            return H + (h < -gcDepth * 0.75 ? -gcDepth * 0.75 : h);                      // a flat floor
        }
        const ul = Math.sqrt(ux * ux + uy * uy + uz * uz) || 1, nx = ux / ul, ny = uy / ul, nz = uz / ul;
        if (sig === 2) {                                   // THE MESA FIELD
            const cx = nx - sC[0], cy = ny - sC[1], cz = nz - sC[2];
            const mask = v2smooth((mfReach - Math.sqrt(cx * cx + cy * cy + cz * cz)) * 4);
            if (mask <= 0) return H;
            const s = noise(ux * 2.4 + OK[0], uy * 2.4 + OK[1], uz * 2.4 + OK[2]);
            const k = (s + 0.05) * 6 * mask;
            H -= 0.03 * R * mask;                          // the plain the buttes stand on
            if (k <= 0) return H;
            const terrace = k >= 1 ? 1 : Math.floor(k * 3) / 3;
            const lvl = noise(ux * 1.2 + OL[0], uy * 1.2 + OL[1], uz * 1.2 + OL[2]);
            const lift = (mfTop + (lvl > 0.25 ? mesaStep : 0)) * terrace;
            return lift > H ? lift : H;
        }
        // A RANGE OR A CANYON: distance from the bent great circle, in the unit sphere's radians near enough.
        const bend = noise(nx * 1.4 + OB[0], ny * 1.4 + OB[1], nz * 1.4 + OB[2]) * 0.1;
        const sd = nx * sN[0] + ny * sN[1] + nz * sN[2] + bend, ad = sd < 0 ? -sd : sd;
        const am = v2smooth((nx * sM[0] + ny * sM[1] + nz * sM[2] + 0.3) * 1.8);
        if (am <= 0) return H;
        if (sig === 0) {                                   // THE GREAT RANGE, with foothills and crags
            const t = ad / rangeW;
            if (t >= 2.4) return H;
            const a = rangeA * (0.75 + 0.3 * noise(nx * 3.5 + OA[0], ny * 3.5 + OA[1], nz * 3.5 + OA[2]));
            let h = 0;
            if (t < 1) {
                const s = 1 - t * t, cr = noise(ux * 7 + OA[1], uy * 7 + OA[2], uz * 7 + OA[0]);
                h = a * s * s + a * 0.12 * (1 - t) * (1 - (cr < 0 ? -cr : cr));
            }
            const f = 1 - t / 2.4;
            return H + am * (h + a * 0.22 * f * f);
        }
        // THE CANYON: a flat floor, steep walls, and a lip at its rim.
        const t = ad - canyonF;
        if (t >= canyonWall * 2.2) return H;
        if (t <= 0) return H - canyonD * am;
        if (t < canyonWall) return H - canyonD * am * (1 - v2smooth(t / canyonWall));
        const s = (t - canyonWall) / (canyonWall * 1.2);
        return H + 0.03 * R * am * (1 - s) * (1 - s);
    }

    // ---- CRATERS: version 2's own list, from its own stream, so a world's craters are where they were ----
    const cr = makeRng((addr || '') + '#craters');
    const rhoMax = Math.max(3, refR * 0.35 * G.craterSize);
    const nCr = Math.round(craterDensity * 6 * refR * refR / 70 * G.craterCount * bT);
    const craters = [];                                    // [x, y, z, rho, 1/rho, depth, rim, peak, floor, outer²]
    for (let i = 0; i < nCr; i++) {
        const u = cr(), fresh = 0.55 + 0.45 * cr(), face = Math.floor(cr() * 6), a = cr(), b = cr();
        const rho = 2 + (rhoMax - 2) * u * u;
        const ax = face >> 1, sg = (face & 1) ? -1 : 1, p = [0, 0, 0];
        p[ax] = sg * refR; p[(ax + 1) % 3] = (a * 2 - 1) * refR; p[(ax + 2) % 3] = (b * 2 - 1) * refR;
        const depth = rho * (0.35 + 0.2 * fresh) * G.craterDepth;
        craters.push(p[0], p[1], p[2], rho, 1 / rho, depth, (0.9 + 0.1 * rho) * fresh * G.craterRim, rho > 7 ? depth * 0.45 : 0,
                     depth * 0.8, 2.56 * rho * rho);
    }
    const CN = 10;

    // ---- ROUND 2'S NEW IDEAS, each from a stream of its own, and nothing at all unless the genome turns it on ----
    // VOLCANOES: a few cones, concave like a stratovolcano's, each with a caldera at the top: a steep inner wall down to
    // a flat floor. Their centres keep to the middle of a face, so a cone is whole (an edge only trims the foot of one
    // that reaches it), and like the craters each is a distance in three dimensions, so a foot carries over an edge.
    const volc = [];                                       // [x, y, z, 1/rho, height, caldera q, caldera floor, rho²]
    if (G.volcanoes) {
        const vr = makeRng((addr || '') + '#volcano3');
        const n = Math.max(1, Math.round((1.5 + 3 * vr()) * R / 40 * G.volcanoCount));
        for (let i = 0; i < n; i++) {
            const face = Math.floor(vr() * 6), a = vr(), b = vr(), u = vr(), w = vr(), c = vr();   // six draws each, always
            const ax = face >> 1, sg = (face & 1) ? -1 : 1, p = [0, 0, 0];
            p[ax] = sg * refR; p[(ax + 1) % 3] = (a * 1.4 - 0.7) * refR; p[(ax + 2) % 3] = (b * 1.4 - 0.7) * refR;
            const rho = (0.17 + 0.11 * u) * refR * G.volcanoSize, h = (0.22 + 0.12 * w) * Rs * G.volcanoSize;
            volc.push(p[0], p[1], p[2], 1 / rho, h, 0.2 + 0.08 * c, h * (0.66 - 0.1 * c), rho * rho);
        }
    }
    // SPIRES: fields of rock pillars a column wide and several levels tall, hoodoos, standing out of whatever ground is
    // there (sea stacks, in a sea). Where a slow field runs high, each column is a spire by a hash of its own position.
    let spSeed = null;
    if (G.spires) { const r = makeRng((addr || '') + '#spire3'); spSeed = [r() * 1000, r() * 1000, r() * 1000, Math.floor(r() * 0x7fffffff)]; }
    // TABLELANDS: wide flat-topped plateaus behind sheer cliffs, a second tier here and there, cut through by gorges.
    // A plateau raises the ground to its top and never lowers it, so a range still stands above it and a crater or the
    // signature canyon still cuts into it; its cliffs show the crusts' strata.
    let tbSeed = null, tbTop = 0, tbThr = 0;
    if (G.tablelands) {
        const r = makeRng((addr || '') + '#table3');
        tbSeed = [r() * 1000, r() * 1000, r() * 1000, r() * 1000, r() * 1000, r() * 1000];
        tbTop = -G.sink * R + (0.12 + 0.06 * r()) * Rs * G.tableHeight;
        tbThr = 0.38 - 0.18 * G.tableCover;
    }
    // DUNES: transverse dunes in the lowlands of some regions, crests square to the wind: a gentle windward slope, a
    // steep slip face, crests that rise and fall along their length and bend. The wind is one direction for the world,
    // laid in the plane of each face (a face it blows straight into takes a second one), so a field of dunes lies
    // within a face and fades out before an edge, where two faces' winds would not meet.
    let duSeed = null, duDir = null;
    const duSpace = 8 * G.duneSpacing;
    if (G.dunes || (bm && bm.ids.indexOf('duneSea') >= 0)) {   // the dune sea lays its dunes in the same wind
        const r = makeRng((addr || '') + '#dune3');
        const dir = () => { const x = r() * 2 - 1, y = r() * 2 - 1, z = r() * 2 - 1, l = Math.sqrt(x * x + y * y + z * z) || 1; return [x / l, y / l, z / l]; };
        const w1 = dir(), w2 = dir();
        duSeed = [r() * 1000, r() * 1000, r() * 1000, r() * 1000, r() * 1000, r() * 1000];
        duDir = [];
        for (let a = 0; a < 3; a++) {                      // per face axis: the wind's two tangential components, unit length
            const t1 = (a + 1) % 3, t2 = (a + 2) % 3;
            let x = w1[t1], y = w1[t2], l = Math.sqrt(x * x + y * y);
            if (l < 0.35) { x = w2[t1]; y = w2[t2]; l = Math.sqrt(x * x + y * y) || 1; }
            duDir.push(x / l, y / l);
        }
    }
    // ROUND 3'S NEW IDEAS, each from a stream of its own, and nothing at all unless the genome turns it on.
    // BASALT COLUMNS: at the continents' coasts (where the lava stands), in patches, the ground cut into terraces
    // basaltStep levels high and raised in pillars one or two columns across, by a hash of each pillar's position.
    let baSeed = null;
    if (G.basaltColumns) { const r = makeRng((addr || '') + '#basalt3'); baSeed = [r() * 1000, r() * 1000, r() * 1000, Math.floor(r() * 0x7fffffff)]; }
    // ICE CREVASSES: the zero lines of a field of their own, about two columns wide, cut into the ice (makeSea).
    let cvSeed = null;
    if (G.iceCrevasses) { const r = makeRng((addr || '') + '#crevasse3'); cvSeed = [r() * 1000, r() * 1000, r() * 1000]; }
    // BIOMES: their ground's own draws, from a stream of its own.
    let bioSeed = null, bioCode = 0, bioFootH = 0;           // bioCode, bioFootH: what biomeGround last decided, for rawAt
    if (bm) { const r = makeRng((addr || '') + '#biome3');
              bioSeed = [r() * 1000, r() * 1000, r() * 1000, Math.floor(r() * 0x7fffffff), Math.floor(r() * 0x7fffffff), r() * 1000, r() * 1000, r() * 1000]; }

    // The surface height, in cells above (or below) the old surface R, in the direction of reference-shell point p.
    // `e` is p's distance to the nearest cube edge along the face.
    function relief(px, py, pz, e) {
        const ux = px * inv, uy = py * inv, uz = pz * inv;
        const wx = ux + noise(ux * 0.9 + W1[0], uy * 0.9 + W1[1], uz * 0.9 + W1[2]) * G.warp;   // the warp: features bend
        const wy = uy + noise(uy * 0.9 + W2[0], uz * 0.9 + W2[1], ux * 0.9 + W2[2]) * G.warp;
        const wz = uz + noise(uz * 0.9 + W3[0], ux * 0.9 + W3[1], uy * 0.9 + W3[2]) * G.warp;
        // CONTINENTS: the field itself, and a smoothstep of it that is steepest where it crosses zero, which is where a
        // coast falls when a sea fills the basins to about half.
        const cf = G.continentScale;
        const c = noise(wx * cf + OC[0], wy * cf + OC[1], wz * cf + OC[2]);
        const shelf = v2smooth(c * G.coastSteep + 0.5) * 2 - 1;
        let H = R * (G.continentAmp * c + G.coastAmp * shelf);
        // HILLS, rougher in some regions than in others.
        const rg = 0.6 + 0.3 * (1 + noise(ux * 1.3 + OG[0], uy * 1.3 + OG[1], uz * 1.3 + OG[2]));
        const hf = G.hillScale;
        H += Rs * G.hillAmp * bT * rg * noise(wx * hf + OH[0], wy * hf + OH[1], wz * hf + OH[2]);
        // THE SMALL SCALE, read as version 2 reads it (per cell, busier on a rough world) but gentler.
        const bumps = noise((px + OU[0]) * 0.09, (py + OU[1]) * 0.09, (pz + OU[2]) * 0.09);
        const det = noise((px + OD[0]) * 0.21, (py + OD[1]) * 0.21, (pz + OD[2]) * 0.21);
        const rn = noise(ux * 0.8 + OR[0], uy * 0.8 + OR[1], uz * 0.8 + OR[2]);
        const tr = Math.max(0, Math.min(1, (d.rough + rn * 8 * d.variance - 1) / 9));
        // THE LAND STANDS A LITTLE BELOW THE OLD SURFACE. At an edge nothing may stand above R, so ground above it is
        // bevelled down to the edge one level a column; with the plains below R the bevel is left to what towers (a
        // range, a mesa, a rim), where version 2's median at R bevelled half of every edge into a frame round each face.
        H += (bumps * (0.25 + 0.8 * tr) + det * (0.1 + 0.3 * tr)) * G.roughness * bT - 0.5 - G.sink * R;
        // VALLEYS: where a slow field crosses zero, in the lowlands only, fading out as the ground rises.
        if (valleys) {
            const lowW = v2smooth((0.02 * R - H) / (0.05 * R));
            if (lowW > 0) {
                const vv = noise(wx * 1.6 + OV[0], wy * 1.6 + OV[1], wz * 1.6 + OV[2]);
                const t = (vv < 0 ? -vv : vv) / valleyW;
                if (t < 1) { const s = 1 - t * t; H -= valleyD * s * s * lowW; }
            }
        }
        // RANGES, where version 2 dealt this world ranges: a broad crest on the zero lines of a slow field.
        if (mountAmp > 0) {
            const m = noise(ux * 0.9 + OM[0], uy * 0.9 + OM[1], uz * 0.9 + OM[2]);
            const mask = v2smooth((m + 0.25 - 0.6 * (1 - mountAmp)) * 2.2);
            if (mask > 0) {
                const n = noise(wx * 1.7 + ON[0], wy * 1.7 + ON[1], wz * 1.7 + ON[2]);
                const rr = 1 - (n < 0 ? -n : n);
                H += mask * mountAmp * Rs * G.rangeHeight * (0.2 * v2smooth((rr - 0.5) * G.rangeSharpness) + 0.04 * rr);
            }
        }
        // MESAS, where version 2 dealt this world mesas: its terraces, which only ever raise the ground.
        if (mesaAmt > 0) {
            const s = noise(ux * 1.8 + OS[0], uy * 1.8 + OS[1], uz * 1.8 + OS[2]);
            const k = (s - (0.55 - 0.35 * mesaAmt)) * 7;
            if (k > 0) {
                const lvl = noise(ux * 1.4 + OL[0], uy * 1.4 + OL[1], uz * 1.4 + OL[2]);
                const top = mesaTop + (lvl > 0.2 ? mesaStep : lvl < -0.2 ? -mesaStep : 0);
                const tn = G.mesaTerraces, terrace = k >= 1 ? 1 : Math.floor(k * tn) / tn;
                const lift = top * terrace;
                if (terrace > 0 && lift > H) H = lift;
            }
        }
        // TABLELANDS (a new idea): where a slow field runs high the ground is raised flat to the plateau's top, a tier
        // higher where it runs higher still, except along the gorges; the step where the field crosses is a sheer cliff.
        if (tbSeed) {
            const t = noise(wx * 1.3 + tbSeed[0], wy * 1.3 + tbSeed[1], wz * 1.3 + tbSeed[2]);
            if (t > tbThr) {
                const gz = noise(wx * 2.6 + tbSeed[3], wy * 2.6 + tbSeed[4], wz * 2.6 + tbSeed[5]);
                if ((gz < 0 ? -gz : gz) * refR >= 3.2) {       // outside a gorge about two columns wide
                    const top = tbTop + (t > tbThr + 0.28 ? mesaStep : 0);
                    if (top > H) H = top;
                }
            }
        }
        H = signature(ux, uy, uz, px, py, pz, H);
        // DUNES (a new idea), in the lowlands of the ergs, away from the edges.
        if (G.dunes && e > 4) {
            const erg = noise(ux * 1.5 + duSeed[0], uy * 1.5 + duSeed[1], uz * 1.5 + duSeed[2]);
            const lowW = v2smooth((0.01 * R - H) / (0.05 * R)) * v2smooth((erg + 0.05) * 5) * v2smooth((e - 4) / 6);
            if (lowW > 0) {
                const ax = (px < 0 ? -px : px) >= refR ? 0 : (py < 0 ? -py : py) >= refR ? 1 : 2;
                const t1 = ax === 0 ? py : ax === 1 ? pz : px, t2 = ax === 0 ? pz : ax === 1 ? px : py;
                const bend = noise(ux * 2.5 + duSeed[3], uy * 2.5 + duSeed[4], uz * 2.5 + duSeed[5]) * 2.5;
                const s = (t1 * duDir[ax * 2] + t2 * duDir[ax * 2 + 1]) / duSpace + bend, f = s - Math.floor(s);
                const prof = f < 0.72 ? f / 0.72 : (1 - f) / 0.28;
                const amp = (1.6 + 1.8 * v2smooth(noise(ux * 6 + duSeed[0], uy * 6 + duSeed[1], uz * 6 + duSeed[2]) + 0.5)) * G.duneHeight;
                H += amp * prof * lowW;
            }
        }
        for (let i = 0; i < volc.length; i += 8) {        // VOLCANOES (a new idea) stand on whatever is there
            const dx = px - volc[i], dy = py - volc[i + 1], dz = pz - volc[i + 2], d2 = dx * dx + dy * dy + dz * dz;
            if (d2 >= volc[i + 7]) continue;
            const q = Math.sqrt(d2) * volc[i + 3], qc = volc[i + 5];
            if (q < qc) H += volc[i + 6] + (volc[i + 4] - volc[i + 6]) * v2smooth((q / qc - 0.7) / 0.3);
            else { const s = (1 - q) / (1 - qc); H += volc[i + 4] * s * s; }
        }
        for (let i = 0; i < craters.length; i += CN) {     // craters punch through whatever is there
            const dx = px - craters[i], dy = py - craters[i + 1], dz = pz - craters[i + 2];
            const d2 = dx * dx + dy * dy + dz * dz;
            if (d2 >= craters[i + 9]) continue;
            const q = Math.sqrt(d2) * craters[i + 4];
            if (q < 1) {
                let h = -craters[i + 5] * (1 - q * q) + craters[i + 6] * q * q * q * q;
                if (craters[i + 7] > 0 && q < 0.3) { const s = 1 - q / 0.3; h += craters[i + 7] * s * s; }
                H += h < -craters[i + 8] ? -craters[i + 8] : h;
            } else {
                const s = (1.6 - q) / 0.6;
                H += craters[i + 6] * s * s;
            }
        }
        // SPIRES (a new idea). Only where no seam joins this column with the next face's (the join averages the two,
        // and would leave half a spire on each side): past V3_JOIN columns from an edge.
        if (spSeed && e > V3_JOIN) {
            const m = noise(ux * 2.2 + spSeed[0], uy * 2.2 + spSeed[1], uz * 2.2 + spSeed[2]);
            if (m > 0.15 && v3Hash(px * 2, py * 2, pz * 2, spSeed[3]) < (m - 0.15) * 0.45 * G.spireDensity)
                H += 2 + Math.round((0.05 + 0.1 * v3Hash(px * 2, py * 2, pz * 2, spSeed[3] + 1)) * Rs * G.spireHeight);
        }
        // BASALT COLUMNS (a new idea), away from the seams as the spires are.
        if (baSeed && e > V3_JOIN) {
            const coast = 1 - (c < 0 ? -c : c) / (0.2 * G.basaltCover);
            if (coast > 0 && noise(ux * 2.8 + baSeed[0], uy * 2.8 + baSeed[1], uz * 2.8 + baSeed[2]) > 0.35 - 0.45 * coast) {
                const st = G.basaltStep;
                const k = v3Hash(px, py, pz, baSeed[3] + 7) < 0.3 ? v3Hash(px, py, pz, baSeed[3])      // a pillar one column across
                    : v3Hash(px * 0.5, py * 0.5, pz * 0.5, baSeed[3]);                                  // …or two
                H = Math.floor(H / st) * st + (k < 0.4 ? 0 : k < 0.8 ? st : 2 * st);
            }
        }
        if (bm) H = biomeGround(ux, uy, uz, px, py, pz, e, H, c, shelf);
        // NO CAP AT AN EDGE (the owner's rule: land carries on over an edge as down goes, never as a pyramid). Version 2
        // brings every column down to R at an edge, a 45° ramp over the last columns, which on version 3's higher land
        // made each face a truncated pyramid and each corner a pit between three ramps. Here the land keeps its height to
        // the edge, the two faces' heights are joined there (sample), and isSolid rolls it over the edge (underGround).
        return H;
    }
    // THE GROUND A COLUMN'S BIOMES MAKE (WG-09), each by its weight there (biomes.mjs), so a boundary is a slope a few
    // cells wide rather than a step: a dune sea smooths the land and lays dunes on it, salt flats level it, a glacier
    // lays a sheet over it, volcanic fields spread basalt with lava pools in its dips, crystal fields raise clusters of
    // crystal spires, and lichen only colours it. The lowland weight is the continents' own field (the basins low), so a
    // biome of the lowlands keeps to them over the hills; and the plains are what they smooth, so a crater, a range or
    // the signature landform stands on through them. Leaves the column's look in bioCode (0 none, 1 to 6 a biome of the
    // catalog, BIO_LAVA, BIO_WARM, BIO_CRYSTAL) and a crystal spire's foot in bioFootH.
    function biomeGround(ux, uy, uz, px, py, pz, e, H, c, shelf) {
        bm.weights(ux, uy, uz, v2smooth(0.5 - c * 2.5), bw);
        const Hc = R * (G.continentAmp * c + G.coastAmp * shelf) - 0.5 - G.sink * R;   // the continents alone: no hills
        const dev = H - Hc, plain = v2smooth((0.07 * R + 2 - (dev < 0 ? -dev : dev)) / (0.04 * R + 1));
        const hx = px * 2, hy = py * 2, hz = pz * 2;          // the column's middle in half cells: whole numbers, for cellHash
        let pool = false, warm = false, spire = false;
        for (let k = 0; k < bw.length; k++) {
            const w = bw[k], b = bk[k];
            if (!(w > 0)) continue;
            if (b === 0) {                                    // THE DUNE SEA: the land smoothed, and dunes on it
                H += (Hc - H) * 0.6 * w * plain;
                if (e > 4) {                                  // the dunes' wind lies in a face, so they fade before an edge
                    const ax = (px < 0 ? -px : px) >= refR ? 0 : (py < 0 ? -py : py) >= refR ? 1 : 2;
                    const t1 = ax === 0 ? py : ax === 1 ? pz : px, t2 = ax === 0 ? pz : ax === 1 ? px : py;
                    const bend = noise(ux * 2.5 + duSeed[3], uy * 2.5 + duSeed[4], uz * 2.5 + duSeed[5]) * 2.5;
                    const s = (t1 * duDir[ax * 2] + t2 * duDir[ax * 2 + 1]) / duSpace + bend, f = s - Math.floor(s);
                    const prof = f < 0.72 ? f / 0.72 : (1 - f) / 0.28;
                    const amp = (1.6 + 1.8 * v2smooth(noise(ux * 6 + duSeed[0], uy * 6 + duSeed[1], uz * 6 + duSeed[2]) + 0.5)) * G.duneHeight;
                    H += amp * prof * w * v2smooth((e - 4) / 6);
                }
            } else if (b === 1) H += (Math.round(Hc) - H) * w * plain;   // SALT FLATS: level, a step where the land crosses a level
            else if (b === 2) H += (Hc + 1 - H) * 0.45 * w * plain;       // A GLACIER: a sheet over the land, its relief half buried
            else if (b === 3) {                               // VOLCANIC FIELDS: basalt plains, lava pools in their dips
                H += (Hc - H) * 0.3 * w * plain;
                const pn = noise(ux * 3.1 + bioSeed[0], uy * 3.1 + bioSeed[1], uz * 3.1 + bioSeed[2]);
                const pw = v2smooth((pn - 0.3) / 0.08) * v2smooth((w - 0.5) / 0.2) * plain;
                if (pw > 0) { H += (Math.floor(Hc) - 1 - H) * pw; if (pw >= 0.5) pool = true; }
                if (pn > 0.16 && w > 0.4) warm = true;
            } else if (b === 4 && e > V3_JOIN) {              // CRYSTAL FIELDS: spires in clusters, where no seam's join reaches
                const cl = noise(ux * 4.2 + bioSeed[5], uy * 4.2 + bioSeed[6], uz * 4.2 + bioSeed[7]);
                if (cellHash(hx, hy, hz, bioSeed[3]) < 0.35 * w * v2smooth((cl - 0.05) / 0.2)) {
                    spire = true; bioFootH = H;
                    H += 2 + Math.round((0.05 + 0.1 * cellHash(hx, hy, hz, bioSeed[3] + 1)) * Rs);
                }
            }
        }
        const pk = bm.pick(bw, cellHash(hx, hy, hz, bioSeed[4]));   // (v3Hash is nearly linear from one column to the next: a dither needs better)
        bioCode = pool ? BIO_LAVA : spire ? BIO_CRYSTAL : pk < 0 ? 0 : bk[pk] === 3 && warm ? BIO_WARM : bk[pk] + 1;
        return H;
    }

    // ---- THE HEIGHT TABLE, one sample per face column, and the seams joined: version 2's, copied ----
    const Wd = 2 * refR, WW = Wd * Wd;
    const table = new Int16Array(6 * WW).fill(V2_NONE);
    const raw = new Float64Array(6 * WW).fill(NaN);
    const bioTab = bm ? new Uint8Array(6 * WW) : null, bioFoot = bm ? new Int16Array(6 * WW) : null;   // per column: its look, a spire's foot
    function rawAt(fi, iu, iv) {
        const ti = fi * WW + iu * Wd + iv;
        let h = raw[ti];
        if (h !== h) {
            const ax = fi >> 1, tu = iu - refR + 0.5, tv = iv - refR + 0.5, p = [0, 0, 0];
            p[ax] = (fi & 1) ? -refR : refR; p[(ax + 1) % 3] = tu; p[(ax + 2) % 3] = tv;
            h = raw[ti] = R + relief(p[0], p[1], p[2], refR - Math.max(Math.abs(tu), Math.abs(tv)));
            if (bm) { bioTab[ti] = bioCode; bioFoot[ti] = bioCode === BIO_CRYSTAL ? Math.round(R + bioFootH) : 0; }
        }
        return h;
    }
    // THE SEAMS JOINED BY DISTANCE, AT ANY HEIGHT, AND WHERE THE LAND IS LOW. A column within V3_JOIN of an edge takes in
    // the column folded over the edge onto the next face (the same distance in from it), fully at the edge and not at
    // all V3_JOIN in, so the two faces stand at one height where they meet whatever that height is, and the land rolled
    // over the edge (underGround) has no step on the diagonal. Version 2's join is kept beside it, whichever is the
    // stronger: a column whose ground stands no higher than its distance from the face's centre is where two faces'
    // ground meets on the diagonal inside the cube (a basin reaching an edge), however far in that is, and there the two
    // must agree or one face's ground overhangs the other's.
    function sample(fi, iu, iv) {
        const ax = fi >> 1, sa = (fi & 1) ? -1 : 1, tu = iu - refR + 0.5, tv = iv - refR + 0.5;
        const h = rawAt(fi, iu, iv);
        let sum = h, wsum = 1;
        for (let k = 0; k < 2; k++) {
            const t = k === 0 ? tu : tv, at = t < 0 ? -t : t, dEdge = refR - at;   // 0.5 at the rim
            if (dEdge >= V3_JOIN && (h + lo) * 0.5 - 0.5 - at >= 2 + V2_SEAM) continue;
            const nAx = (ax + 1 + k) % 3, nFi = nAx * 2 + (t > 0 ? 0 : 1), c = [0, 0, 0];
            c[ax] = sa * at; c[3 - ax - nAx] = k === 0 ? tv : tu;
            const hm = rawAt(nFi, c[(nAx + 1) % 3] + refR - 0.5, c[(nAx + 2) % 3] + refR - 0.5);
            const g = (h + hm) * 0.5 - 0.5 - at;
            const wLow = g <= 2 ? 1 : 1 - v2smooth((g - 2) / V2_SEAM);
            const wEdge = dEdge >= V3_JOIN ? 0 : 1 - v2smooth((dEdge - 0.5) / (V3_JOIN - 0.5));
            const w = wLow > wEdge ? wLow : wEdge;
            if (w > 0) { sum += w * hm; wsum += w; }
        }
        const t = Math.round(sum / wsum);
        return t < lo ? lo : t > hi ? hi : t;
    }

    // ---- CAVES: version 2's worm tunnels, from its own stream, copied ----
    const bands = Math.max(12, Math.min(48, R));
    const ocR = Math.max(1, Math.round(bands / 4) - 2) + Math.ceil(bands / 8);
    const caveMinR = Math.max(ocR + 3, Math.round(R * 0.5));
    const roof = Math.max(3, Math.round(R * 0.08));
    const capR = R - roof - 1;
    const cv = makeRng((addr || '') + '#caves');
    const spheres = [];
    const nWorms = (caveAmt > 0 && capR - caveMinR >= 4) ? Math.round(caveAmt * 3 * R * R / 144) : 0;
    for (let w = 0; w < nWorms; w++) {
        const ax = Math.floor(cv() * 3), sg = cv() < 0.5 ? -1 : 1, tu = cv() * 2 - 1, tv = cv() * 2 - 1;
        const r0 = caveMinR + 2 + cv() * (capR - caveMinR - 4);
        const p = [0, 0, 0];
        p[ax] = sg * r0; p[(ax + 1) % 3] = tu * r0; p[(ax + 2) % 3] = tv * r0;
        let dx = cv() - 0.5, dy = cv() - 0.5, dz = cv() - 0.5;
        let rad = 1.1 + cv() * 0.8;
        const steps = 16 + Math.floor(cv() * (10 + R));
        const chamberAt = cv() < 0.4 ? Math.floor(cv() * steps) : -1;
        for (let i = 0; i < steps; i++) {
            const L = Math.max(Math.abs(p[0]), Math.abs(p[1]), Math.abs(p[2]));
            const pl = Math.sqrt(p[0] * p[0] + p[1] * p[1] + p[2] * p[2]) || 1;
            const nx = p[0] / pl, ny = p[1] / pl, nz = p[2] / pl;
            if (L < caveMinR + rad + 1.5) { dx += nx * 0.5; dy += ny * 0.5; dz += nz * 0.5; }
            else if (L > capR - rad - 0.5) { dx -= nx * 0.5; dy -= ny * 0.5; dz -= nz * 0.5; }
            dx += (cv() - 0.5) * 0.45; dy += (cv() - 0.5) * 0.45; dz += (cv() - 0.5) * 0.45;
            const dr = dx * nx + dy * ny + dz * nz;
            dx -= nx * dr * 0.35; dy -= ny * dr * 0.35; dz -= nz * dr * 0.35;
            const dl = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
            dx /= dl; dy /= dl; dz /= dl;
            p[0] += dx * 0.9; p[1] += dy * 0.9; p[2] += dz * 0.9;
            rad += (cv() - 0.5) * 0.25; rad = rad < 0.9 ? 0.9 : rad > 2.3 ? 2.3 : rad;
            spheres.push(p[0], p[1], p[2], rad * rad);
            if (i === chamberAt) { const c = 2.4 + cv() * 1.8; spheres.push(p[0], p[1], p[2], c * c); }
        }
    }
    const CB = 8, cbOff = Math.ceil((hi + 4) / CB), nb = 2 * cbOff + 1;
    const blocks = spheres.length ? new Array(nb * nb * nb) : null;
    for (let s = 0; s < spheres.length; s += 4) {
        const rr = Math.sqrt(spheres[s + 3]);
        const x0 = Math.floor((spheres[s] - rr) / CB), x1 = Math.floor((spheres[s] + rr) / CB);
        const y0 = Math.floor((spheres[s + 1] - rr) / CB), y1 = Math.floor((spheres[s + 1] + rr) / CB);
        const z0 = Math.floor((spheres[s + 2] - rr) / CB), z1 = Math.floor((spheres[s + 2] + rr) / CB);
        for (let bx = x0; bx <= x1; bx++) for (let by = y0; by <= y1; by++) for (let bz = z0; bz <= z1; bz++) {
            const k = ((bx + cbOff) * nb + (by + cbOff)) * nb + (bz + cbOff);
            (blocks[k] || (blocks[k] = [])).push(s);
        }
    }
    function inCave(cx, cy, cz) {
        const list = blocks[((Math.floor(cx / CB) + cbOff) * nb + (Math.floor(cy / CB) + cbOff)) * nb + (Math.floor(cz / CB) + cbOff)];
        if (!list) return false;
        for (let i = 0; i < list.length; i++) {
            const s = list[i], dx = cx - spheres[s], dy = cy - spheres[s + 1], dz = cz - spheres[s + 2];
            if (dx * dx + dy * dy + dz * dz <= spheres[s + 3]) return true;
        }
        return false;
    }
    const hasCaves = spheres.length > 0;
    const solidR = hasCaves ? Math.min(lo, caveMinR - 1) : lo;

    let colFi = 0, colU = 0, colV = 0;
    function columnOf(x, y, z, cx, cy, cz, ax, ay, az) {
        let fi, u, v;
        if (ax >= ay && ax >= az) { fi = cx > 0 ? 0 : 1; u = y; v = z; }
        else if (ay >= az) { fi = cy > 0 ? 2 : 3; u = z; v = x; }
        else { fi = cz > 0 ? 4 : 5; u = x; v = y; }
        colFi = fi;
        colU = (u < -refR ? -refR : u > refR - 1 ? refR - 1 : u) + refR;
        colV = (v < -refR ? -refR : v > refR - 1 ? refR - 1 : v) + refR;
        return fi * WW + colU * Wd + colV;
    }
    function heightOfColumn(ti) {
        let t = table[ti];
        if (t === V2_NONE) t = table[ti] = sample((ti / WW) | 0, ((ti % WW) / Wd) | 0, ti % Wd);
        return t;
    }
    function isSolid(x, y, z) {
        const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5;
        const ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
        const rF = ax > ay ? (ax > az ? ax : az) : (ay > az ? ay : az), r = Math.round(rF);
        if (r > hi) return false;
        if (r <= solidR) return true;
        const t = heightOfColumn(columnOf(x, y, z, cx, cy, cz, ax, ay, az));
        if (!underGround(r, t, ax, ay, az)) return false;
        if (!hasCaves || r < caveMinR || r > t - roof) return true;
        if (!inCave(cx, cy, cz)) return true;
        // UNDER EVERY FACE'S GROUND, NOT ONLY ITS OWN. Version 3's basins run deeper, and where one reaches down near an
        // edge, the wall round it is the next face's rock: a cell of it can be `roof` under its own face's ground and a
        // cell or two under the basin's rim on the other. So a cave is carved only where it is at least `roof` under the
        // ground of every face its position leans toward (a cell deep inside a face is far below all of them).
        return !(roofed(0, cx, y, z) && roofed(1, cy, z, x) && roofed(2, cz, x, y));
    }
    // WHETHER A CELL IS UNDER ITS OWNING COLUMN'S GROUND, t, the cell at L∞ radius r with its centre's |x|, |y|, |z|. On a
    // face, as ever, r ≤ t. PAST AN EDGE (two of its coordinates beyond the reference cube) the land ROLLS OVER it: a
    // quarter circle round the reference cube's edge line, and past a corner (all three) an eighth of a sphere round the
    // corner, a cell under it when its centre is inside. Its radius is how far the column stands above the reference cube
    // (t - refR, to the top cell's outer face) and half a cell more: the most that adds no cell above the face's own
    // height beside the edge (the first columns past the reference cube keep it, so rolled ground starts without a step),
    // and enough that land at R or R - 1 keeps a whole corner cell on the diagonal, the sharp edge version 2 has there
    // and the walker rounds without a notch. So land above R carries on over an edge the way a horizon does instead of
    // being cut to a 45° ramp, and "down" is still the owning face's (its gravity).
    function underGround(r, t, ax, ay, az) {
        const ex = ax - refR, ey = ay - refR, ez = az - refR;
        let past = 0, d2 = 0;
        if (ex > 0) { past++; d2 += ex * ex; }
        if (ey > 0) { past++; d2 += ey * ey; }
        if (ez > 0) { past++; d2 += ez * ez; }
        if (past < 2) return r <= t;
        const hh = t - refR + 0.5;
        return hh > 0 && d2 <= hh * hh;
    }
    // How far a cell under the ground (underGround) lies below its surface, in cells: t - r on a face, and past an edge
    // the rolled surface's radius less the cell's distance from the edge line, less the half cell from its centre to its
    // outer face, so the top cell reads about 0 there too.
    function depthUnder(r, t, ax, ay, az) {
        const ex = ax - refR, ey = ay - refR, ez = az - refR;
        let past = 0, d2 = 0;
        if (ex > 0) { past++; d2 += ex * ex; }
        if (ey > 0) { past++; d2 += ey * ey; }
        if (ez > 0) { past++; d2 += ez * ez; }
        return past < 2 ? t - r : t - refR - Math.sqrt(d2);
    }
    // Whether a cell whose centre lies at `c` along `axis` is `roof` below the ground of that face's column over it; (u, v)
    // are its other two coordinates in that face's column order (columnOf's). A cell no further out than caveMinR is
    // below every face's ground by construction (the lowest ground, lo, stands more than a roof above caveMinR).
    function roofed(axis, c, u, v) {
        const k = Math.round(c < 0 ? -c : c);
        if (k <= caveMinR) return true;
        const cu = (u < -refR ? -refR : u > refR - 1 ? refR - 1 : u) + refR;
        const cv = (v < -refR ? -refR : v > refR - 1 ? refR - 1 : v) + refR;
        return k <= heightOfColumn((axis * 2 + (c > 0 ? 0 : 1)) * WW + cu * Wd + cv) - roof;
    }

    // ---- THE LEVELS OF THE SURFACE, read off a fixed grid of columns (version 2's), for the seas and the crust ----
    const SG = Math.min(Wd, V2_SEA_GRID);
    let seaHist = null, seaN = 0;
    function seaHistogram() {
        if (seaHist) return seaHist;
        seaHist = new Int32Array(hi - lo + 1);
        for (let fi = 0; fi < 6; fi++) for (let i = 0; i < SG; i++) for (let j = 0; j < SG; j++) {
            const iu = Math.floor((i + 0.5) * Wd / SG), iv = Math.floor((j + 0.5) * Wd / SG);
            seaHist[heightOfColumn(fi * WW + iu * Wd + iv) - lo]++;
            seaN++;
        }
        return seaHist;
    }

    // ---- THE LOOK OF A COLUMN'S BIOME (WG-09), for the renderer ----
    // The biome a cell of the ground shows, by its biome's id, or 'lava' (a pool's top cell), 'warm' (the ground round a
    // pool) or 'crystal' (a spire, all the way up); null for a cell no biome colours: its column has none, the cell is
    // above the ground, or it is deeper than the biome's own depth (biomes.mjs look.depth), so a cliff keeps its strata.
    // The living biome a cell's column is (its id), however deep the cell and whatever look the biome draws (turf, a
    // pool, none): the column's own biome map, no water. For the animals' homes (system-view.js faunaColumnBiome).
    function columnBiome(x, y, z) {
        if (!bioTab) return null;
        const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5, ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
        const ti = columnOf(x, y, z, cx, cy, cz, ax, ay, az); heightOfColumn(ti);
        const c = bioTab[ti]; return c >= 1 && c <= BIOMES.length ? BIOMES[c - 1].id : null;
    }
    function surfaceBiome(x, y, z) {
        const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5;
        const ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
        const r = Math.round(ax > ay ? (ax > az ? ax : az) : (ay > az ? ay : az));
        const ti = columnOf(x, y, z, cx, cy, cz, ax, ay, az), t = heightOfColumn(ti), code = bioTab[ti];
        if (!code || !underGround(r, t, ax, ay, az)) return null;
        const dz = depthUnder(r, t, ax, ay, az);                // 0 at the top cell, on a face or rolled over an edge
        if (code === BIO_CRYSTAL) return r > bioFoot[ti] ? 'crystal' : null;
        if (code === BIO_LAVA) return dz < 1 ? 'lava' : null;
        if (code === BIO_WARM) return dz < 2 ? 'warm' : null;
        const b = BIOMES[code - 1];
        return dz < b.look.depth ? b.id : null;
    }
    // How much of the surface each of the world's biomes shows, on the sea's grid of columns: [{ id, cover }] in the
    // map's order (the dominant first), each a share of all the columns. A column under a sea shows none: `seaAt(x, y,
    // z)`, when given, says whether the cell just over a column's ground is sea.
    function biomeCover(seaAt) {
        if (!bm) return [];
        const n = new Float64Array(BIOMES.length);
        let N = 0;
        for (let fi = 0; fi < 6; fi++) for (let i = 0; i < SG; i++) for (let j = 0; j < SG; j++) {
            const iu = Math.floor((i + 0.5) * Wd / SG), iv = Math.floor((j + 0.5) * Wd / SG), ti = fi * WW + iu * Wd + iv;
            const t = heightOfColumn(ti), code = bioTab[ti];
            N++;
            if (!code) continue;
            if (seaAt) {
                const ax = fi >> 1, q = [0, 0, 0];
                q[ax] = (fi & 1) ? -t - 1 : t; q[(ax + 1) % 3] = iu - refR; q[(ax + 2) % 3] = iv - refR;
                if (seaAt(q[0], q[1], q[2]) && !(code <= BIOMES.length && BIOMES[code - 1].underSea)) continue;
            }
            n[code === BIO_LAVA || code === BIO_WARM ? 3 : code === BIO_CRYSTAL ? 4 : code - 1]++;
        }
        return bm.ids.map((id) => ({ id, cover: n[BIOME_IDS.indexOf(id)] / N }));
    }

    // ---- WHAT GROWS (WG-10): the organics (organics.mjs) and the flora (flora.mjs) of the world's living biomes ----
    // Both read the ground through the same questions (its columns' biomes and heights, the climate there, what is solid)
    // and the sea it holds, which is decided after the terrain: `sea` is surfaceSea's answer, or null. Only a liquid sea
    // is water to grow in; an ice sheet is ground nothing roots through. Each is worked out once per sea.
    function lifeCtx(sea) {
        const liquid = sea && sea.kind === 'liquid', cellOf = sea ? (x, y, z) => sea.at(x, y, z) : null;
        return {
            R, refR, HALF: 2 * R + 1, world: bioWorld, climate: bioClim, isSolid,
            biomeAt: (fi, iu, iv) => { const ti = fi * WW + iu * Wd + iv; heightOfColumn(ti); const c = bioTab[ti]; return c >= 1 && c <= BIOMES.length ? BIOMES[c - 1].id : null; },
            top: (fi, iu, iv) => heightOfColumn(fi * WW + iu * Wd + iv),
            tempAt: (fi, iu, iv) => { const q = columnDir(fi, iu, iv, refR); return fieldAt(bioClim, q[0], q[1], q[2]).tempC; },
            dir: (fi, iu, iv) => columnDir(fi, iu, iv, refR),
            seaAt: liquid ? (x, y, z) => cellOf(x, y, z) === SEA_CELL.LIQUID : null,
            blockedAt: sea && !liquid ? (x, y, z) => cellOf(x, y, z) !== SEA_CELL.DRY : null,
            seaLevel: liquid ? sea.level : null,
            caveRoofs,
        };
    }
    // Air cells under rock in the caves, one near the top of every third of their spheres: where a vine can hang.
    function caveRoofs() {
        const out = [];
        for (let s = 0; s < spheres.length; s += 12) {
            const x = spheres[s], y = spheres[s + 1], z = spheres[s + 2], rad = Math.sqrt(spheres[s + 3]);
            const ax = Math.abs(x) >= Math.abs(y) && Math.abs(x) >= Math.abs(z) ? 0 : Math.abs(y) >= Math.abs(z) ? 1 : 2, p = [x, y, z], sg = p[ax] < 0 ? -1 : 1;
            const c = [Math.floor(x), Math.floor(y), Math.floor(z)];
            c[ax] = Math.floor(p[ax] + sg * (rad - 0.6));
            const o = c.slice(); o[ax] += sg;
            if (!isSolid(c[0], c[1], c[2]) && isSolid(o[0], o[1], o[2])) out.push([c[0], c[1], c[2], ax * 2 + (sg < 0 ? 1 : 0)]);
        }
        return out;
    }
    const lifeMemo = new Map();
    function life(kind, sea) {
        if (!bm) return null;
        const key = kind + (sea ? ' ' + sea.kind + ' ' + sea.level : ' dry');
        if (!lifeMemo.has(key)) lifeMemo.set(key, (kind === 'organics' ? organicsOf : floraOf)(lifeCtx(sea)));
        return lifeMemo.get(key);
    }

    // ---- THE CRUST FOLLOWS THE LAND ----
    // A column's surface is one of the two crusts by how high it stands: the lowlands and basins (about
    // lowlandShare of the surface, 70% at the default, measured on the grid above) wear one and the highlands the other, with a slow
    // field nudging the line by a few levels so it is a coastline of patches rather than one contour. Under the surface
    // the ground is that same crust, except where a cliff exposes it: a cell with lower ground beside it (a mesa's side,
    // a crater's wall, a canyon's) shows the two crusts in strata a few cells thick, the same everywhere at the same
    // depth, as rock is laid down. So a plain is one colour, a range another, and a cliff is banded.
    //
    // THE CRUST THAT COVERS MOST OF THE SURFACE SHELL STAYS VERSION 2'S. A world's albedo, and so its temperature, its
    // air and the kind of sea it can hold, is worked out from whichever crust shows most on a fixed grid of the surface
    // shell (crustDominant in system-view.js, dominantCrustIndex in functions/share.js). The two crusts' roles are
    // swapped where needed so that the answer on that grid is version 2's, and a world is as warm at version 3 as it was.
    const strataT = G.strataThickness > 0 ? G.strataThickness : kStrataT < 0.5 ? 2 : 3, strataPh = Math.floor(kStrata * strataT);
    const cls = new Int8Array(6 * WW).fill(-1);            // per column: -1 not asked yet, 0 lowland, 1 highland
    const lowNb = new Int16Array(6 * WW).fill(V2_NONE);    // per column: the lowest ground of it and its four neighbours
    let thrH = 0, swap = -1;
    // A column's class reads the ground round it, not the column alone (the mean height over a square about a sixteenth
    // of R across each way): one column's bump or dip does not flip it, so the line between the crusts follows the lie
    // of the land instead of every terrace's edge, which on a small world is most of it. The square runs on over an
    // edge onto the next face's columns (colAcross), so the two columns either side of a seam read nearly the same
    // ground and wear the same crust as often as two neighbours on one face do.
    const clsReach = Math.max(1, Math.round(R / 16 * G.patchScale));
    // The column whose centre is (tu, tv) on face fi's plane, running on over an edge onto the face that tangent reaches
    // (the same fold as sample()'s mirror: past the edge by o, it is o in from the edge on the next face); -1 round a
    // corner, where two tangents are past it at once.
    function colAcross(fi, tu, tv) {
        const ax = fi >> 1, sa = (fi & 1) ? -1 : 1, au = tu < 0 ? -tu : tu, av = tv < 0 ? -tv : tv;
        if (au < refR && av < refR) return fi * WW + (tu + refR - 0.5) * Wd + (tv + refR - 0.5);
        if (au >= refR && av >= refR) return -1;
        const p = [0, 0, 0];
        p[ax] = sa * refR; p[(ax + 1) % 3] = tu; p[(ax + 2) % 3] = tv;
        const k = au >= refR ? (ax + 1) % 3 : (ax + 2) % 3, t = au >= refR ? tu : tv, o = (t < 0 ? -t : t) - refR;
        p[k] = t < 0 ? -refR : refR; p[ax] = sa * (refR - o);
        const nf = k * 2 + (t < 0 ? 1 : 0);
        return nf * WW + (p[(k + 1) % 3] + refR - 0.5) * Wd + (p[(k + 2) % 3] + refR - 0.5);
    }
    function classOf(ti) {
        let k = cls[ti];
        if (k < 0) {
            const fi = (ti / WW) | 0, ax = fi >> 1, s = (fi & 1) ? -1 : 1, iu = ((ti % WW) / Wd) | 0, iv = ti % Wd;
            const pu = iu - refR + 0.5, pv = iv - refR + 0.5, p = [0, 0, 0];
            p[ax] = s; p[(ax + 1) % 3] = pu * inv; p[(ax + 2) % 3] = pv * inv;
            const pn = noise(p[0] * 1.6 + OP[0], p[1] * 1.6 + OP[1], p[2] * 1.6 + OP[2]);
            let sum = 0, n = 0;
            for (let du = -clsReach; du <= clsReach; du++) for (let dv = -clsReach; dv <= clsReach; dv++) {
                const nt = colAcross(fi, pu + du, pv + dv);
                if (nt < 0) continue;
                sum += heightOfColumn(nt); n++;
            }
            k = cls[ti] = sum / n + pn * G.patchNoise * R > thrH ? 1 : 0;
        }
        return k;
    }
    function lowOf(ti) {
        let m = lowNb[ti];
        if (m === V2_NONE) {
            const base = ((ti / WW) | 0) * WW, iu = ((ti % WW) / Wd) | 0, iv = ti % Wd;
            m = heightOfColumn(ti);
            if (iu > 0) { const h = heightOfColumn(base + (iu - 1) * Wd + iv); if (h < m) m = h; }
            if (iu < Wd - 1) { const h = heightOfColumn(base + (iu + 1) * Wd + iv); if (h < m) m = h; }
            if (iv > 0) { const h = heightOfColumn(base + iu * Wd + iv - 1); if (h < m) m = h; }
            if (iv < Wd - 1) { const h = heightOfColumn(base + iu * Wd + iv + 1); if (h < m) m = h; }
            lowNb[ti] = m;
        }
        return m;
    }
    // A cell's crust: its column's, or on a cliff (below its column's top, above its lowest neighbour's) the strata. Land
    // rolled over an edge (underGround) is a column's ground, not a cliff, though its top cells stand below the column's
    // height: they wear the column's crust as the face does, and only what lies a cell or more under the rolled surface
    // shows strata. (On a face the depth under the surface is t - r, so there it is the rule it always was.)
    function rawMat(x, y, z) {
        const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5;
        const ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
        const r = Math.round(ax > ay ? (ax > az ? ax : az) : (ay > az ? ay : az));
        const ti = columnOf(x, y, z, cx, cy, cz, ax, ay, az), t = heightOfColumn(ti);
        if (G.strata && r < t && r > lowOf(ti) && depthUnder(r, t, ax, ay, az) >= 1) return Math.floor((r + strataPh) / strataT) & 1;   // a cliff: strata
        return classOf(ti);
    }
    function settleCrust() {
        // The lowland line: the level at or below which the genome's lowlandShare of the sampled columns stand.
        const h = seaHistogram();
        let L = lo, n = h[0];
        while (L < hi && n < G.lowlandShare * seaN) { L++; n += h[L - lo]; }
        // Version 2's majority on crustDominant's grid, and this field's, the line moved up a level while they tie
        // (a tie reads as crust 0 however the roles are dealt).
        let v2m1 = 0, np = 0;
        const probe = (fn) => {
            let m1 = 0;
            for (let f = 0; f < 6; f++) {
                const ax = f >> 1, sg = (f & 1) ? R : -R;
                for (let i = 0; i < V3_PROBE_N; i++) for (let j = 0; j < V3_PROBE_N; j++) {
                    const q = [0, 0, 0];
                    q[ax] = sg;
                    q[(ax + 1) % 3] = Math.round(-R + 2 * R * (i + 0.5) / V3_PROBE_N);
                    q[(ax + 2) % 3] = Math.round(-R + 2 * R * (j + 0.5) / V3_PROBE_N);
                    if (fn(q[0], q[1], q[2]) === 1) m1++;
                }
            }
            return m1;
        };
        np = 6 * V3_PROBE_N * V3_PROBE_N;
        v2m1 = probe((x, y, z) => voxSurfMat(noise, d.off, d.surfThresh, x, y, z));
        let m1 = 0;
        for (let tries = 0; tries < 4; tries++) {
            thrH = L + tries; cls.fill(-1);
            m1 = probe(rawMat);
            if (m1 * 2 !== np) break;
        }
        swap = ((v2m1 * 2 > np) === (m1 * 2 > np)) ? 0 : 1;
    }
    // With crustFollowsLand off, the crust is version 1's (and 2's) noise field, the patchwork the land replaced.
    const surfMat = G.crustFollowsLand ? (x, y, z) => {
        if (swap < 0) settleCrust();
        return rawMat(x, y, z) ^ swap;
    } : (x, y, z) => voxSurfMat(noise, d.off, d.surfThresh, x, y, z);

    // ---- SEAS: version 2's stream, levels and lineae, with version 3's amounts (VERSION 3 SEAS, below) ----
    const sr = makeRng((addr || '') + '#seas');
    const kSeaAmount = v3Boost(sr(), G.seaBoost), kSeaCover = sr(), kLineae = sr();
    const LIN = [];
    for (let i = 0; i < V2_LINEAE_MAX; i++) {
        const dx = sr() * 2 - 1, dy = sr() * 2 - 1, dz = sr() * 2 - 1, dl = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
        LIN.push(dx / dl, dy / dl, dz / dl, (sr() * 2 - 1) * 0.6, sr() * 10000, sr() * 10000, sr() * 10000);
    }
    const nLin = G.crackFamilies > 0 ? G.crackFamilies : 3 + Math.floor(kLineae * (V2_LINEAE_MAX - 2));
    let linSign = null;
    const fl = 1.5 * inv;
    function linSigns(fi, iu, iv) {
        const ti = fi * WW + iu * Wd + iv;
        let s = linSign[ti];
        if (s) return s;
        const ax = fi >> 1, p = [0, 0, 0];
        p[ax] = (fi & 1) ? -refR : refR; p[(ax + 1) % 3] = iu - refR + 0.5; p[(ax + 2) % 3] = iv - refR + 0.5;
        s = 0x80;
        for (let k = 0; k < nLin; k++) {
            const o = k * 7;
            const f0 = (p[0] * LIN[o] + p[1] * LIN[o + 1] + p[2] * LIN[o + 2]) * inv - LIN[o + 3];
            const f = (f0 > V2_LINEA_REACH || f0 < -V2_LINEA_REACH) ? f0
                    : f0 + noise((p[0] + LIN[o + 4]) * fl, (p[1] + LIN[o + 5]) * fl, (p[2] + LIN[o + 6]) * fl) * V2_LINEA_WARP;
            if (f > 0) s |= 1 << k;
        }
        return (linSign[ti] = s);
    }
    let crackTab = null;
    function cracked(fi, iu, iv) {
        const ti = fi * WW + iu * Wd + iv;
        if (crackTab[ti]) return crackTab[ti] === 2;
        const s = linSigns(fi, iu, iv) & 0x7f;
        let gone = 0;
        if (s) for (let du = -1; du <= 1; du++) for (let dv = -1; dv <= 1; dv++) {
            const nu = iu + du, nv = iv + dv;
            if ((du || dv) && nu >= 0 && nu < Wd && nv >= 0 && nv < Wd) gone |= s & ~linSigns(fi, nu, nv);
        }
        crackTab[ti] = (gone & 0x7f) !== 0 ? 2 : 1;
        return crackTab[ti] === 2;
    }

    const seas = new Map();
    function surfaceSea(desc) {
        const kind = desc && desc.kind;
        if (kind !== 'liquid' && kind !== 'ice' && kind !== 'lava') return null;
        const T = kind === 'lava' ? v3LavaTempOf(desc) : 0;
        const key = kind === 'lava' ? 'lava ' + T : kind;
        if (seas.has(key)) return seas.get(key);
        let ocean, want;
        if (kind === 'lava') {
            ocean = T >= G.lavaOceanFrom;
            want = v3LavaCover(T, G) + (kSeaCover - 0.5) * 0.06;
        } else {
            const [dryBelow, lakesBelow] = V3_SEA_AMOUNT[kind];
            if (kSeaAmount < dryBelow) { seas.set(key, null); return null; }
            ocean = kSeaAmount >= lakesBelow;
            want = ocean ? G.oceanCoverMin + (G.oceanCoverMax - G.oceanCoverMin) * kSeaCover : (0.03 + 0.12 * kSeaCover) * G.lakeScale;
        }
        const h = seaHistogram();
        let S = lo, below = 0;
        while (S <= hi && below < want * seaN) { below += h[S - lo]; S++; }
        if (S > lo) {
            const under = below - h[S - 1 - lo];
            if (kind === 'lava' || ocean) {
                // The level nearer the amount asked for by ratio (as the lakes' rule reads it: the one whose cover times
                // the other's is on its side of want squared), and never one that floods more than oceanCap (an ocean
                // of liquid or ice) or lavaLakeCap (lava lakes) while the level below holds any: a whole level of a
                // flat world can be a third of it.
                const cap = below > (kind !== 'lava' ? G.oceanCap : ocean ? 1 : G.lavaLakeCap) * seaN, w2 = want * seaN * want * seaN;
                if (under > 0 && (cap || under * below > w2)) { S--; below = under; }
            } else {
                // Lakes: version 2's rule, with a little more room. Version 3's plains are broad and flat, so on a small
                // world one level can be the difference between a few puddles and a quarter of the surface, and by version
                // 2's rule the frozen lake worlds the review liked came out nearly dry. So the level below is taken only
                // when it holds at least half of what was asked for (and is nearer it by ratio), or when this one floods
                // more than the genome's lakeCap (a third, at the default).
                const w2 = want * seaN * want * seaN;
                if (under > 0 && ((under * 2 >= want * seaN && under * below > w2) || below > G.lakeCap * seaN)) { S--; below = under; }
                else if (below > G.lakeCap * seaN) below = 0;
            }
        }
        const layer = below > 0 ? makeSea(kind, desc.name || null, ocean, S, below / seaN, T) : null;
        seas.set(key, layer);
        return layer;
    }
    function makeSea(kind, name, ocean, S, cover, T) {
        const ice = kind === 'ice', ridges = ice && ocean && G.iceRidges;
        // ROUND 3'S NEW IDEAS in the sea: crevasses and ice cliffs in the ice.
        const crev = ice && !!cvSeed, wallH = crev ? G.iceWallHeight : 0;
        if (ice && !linSign) { linSign = new Uint8Array(6 * WW); crackTab = new Uint8Array(6 * WW); }
        // PRESSURE RIDGES AND HUMMOCKS on an ice sheet (not on frozen lakes, which the review liked as they were): the
        // columns either side of a crack stand a level proud, broken into lengths, which is Europa's double ridge, and
        // a scattering of single hummocks a few columns further out. Both are ice (SEA_CELL.ICE) one level above the
        // sheet, a step the walker takes, and both are pure functions of their column, kept per column once known.
        const raisedTab = ridges ? new Uint8Array(6 * WW) : null;   // 0 not asked yet, 1 flat, 2 raised
        function raised(fi, iu, iv) {
            const ti = fi * WW + iu * Wd + iv;
            let k = raisedTab[ti];
            if (!k) {
                k = 1;
                if (!cracked(fi, iu, iv)) {
                    let dmin = 9;
                    for (let du = -3; du <= 3 && dmin > 1; du++) for (let dv = -3; dv <= 3; dv++) {
                        const nu = iu + du, nv = iv + dv, dd = Math.max(du < 0 ? -du : du, dv < 0 ? -dv : dv);
                        if (dd === 0 || dd >= dmin || nu < 0 || nu >= Wd || nv < 0 || nv >= Wd) continue;
                        if (heightOfColumn(fi * WW + nu * Wd + nv) < S && cracked(fi, nu, nv)) dmin = dd;
                    }
                    if (dmin <= 3) {
                        const ax = fi >> 1, p = [0, 0, 0];
                        p[ax] = (fi & 1) ? -1 : 1; p[(ax + 1) % 3] = (iu - refR + 0.5) * inv; p[(ax + 2) % 3] = (iv - refR + 0.5) * inv;
                        if (dmin === 1) { if (noise(p[0] * 6 + OK[2], p[1] * 6 + OK[0], p[2] * 6 + OK[1]) > -G.ridgeRate) k = 2; }
                        else if (noise(p[0] * 17 + OK[1], p[1] * 17 + OK[2], p[2] * 17 + OK[0]) > G.hummockRarity) k = 2;
                    }
                }
                raisedTab[ti] = k;
            }
            return k === 2;
        }
        let top = ridges ? S + 1 : S;
        if (wallH > 0 && S + wallH > top) top = S + wallH;
        // A column's point on the unit cube, computed as the height table computes it for relief().
        const unitOf = (fi, iu, iv) => {
            const ax = fi >> 1, p = [0, 0, 0];
            p[ax] = ((fi & 1) ? -refR : refR) * inv; p[(ax + 1) % 3] = (iu - refR + 0.5) * inv; p[(ax + 2) % 3] = (iv - refR + 0.5) * inv;
            return p;
        };
        // Per column, kept once known: 0 not asked yet, 1 no, 2 yes.
        const crevTab = crev ? new Uint8Array(6 * WW) : null, cvD = Math.max(2, Math.round(4 * G.crevasseDepth));
        const cvF = 1.5 * G.crevasseDensity, cvW = 3 * inv;
        function crevasse(fi, iu, iv) {
            if (!crevTab) return false;
            const ti = fi * WW + iu * Wd + iv;
            if (!crevTab[ti]) {
                const p = unitOf(fi, iu, iv), n = noise(p[0] * cvF + cvSeed[0], p[1] * cvF + cvSeed[1], p[2] * cvF + cvSeed[2]);
                crevTab[ti] = (n < 0 ? -n : n) < cvW ? 2 : 1;
            }
            return crevTab[ti] === 2;
        }
        // THE ICE CLIFF: an ice column with land (ground at or over the level) within two columns of it on its face stands
        // wallH over the rest, so where the ice meets the land it ends in a sheer wall.
        const wallTab = wallH > 0 ? new Uint8Array(6 * WW) : null;
        function margin(fi, iu, iv) {
            const ti = fi * WW + iu * Wd + iv;
            if (!wallTab[ti]) {
                let k = 1;
                for (let du = -2; du <= 2 && k === 1; du++) for (let dv = -2; dv <= 2; dv++) {
                    const nu = iu + du, nv = iv + dv;
                    if (nu < 0 || nu >= Wd || nv < 0 || nv >= Wd) continue;
                    if (heightOfColumn(fi * WW + nu * Wd + nv) >= S) { k = 2; break; }
                }
                wallTab[ti] = k;
            }
            return wallTab[ti] === 2;
        }
        function at(x, y, z) {
            const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5;
            const ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
            const rF = ax > ay ? (ax > az ? ax : az) : (ay > az ? ay : az), r = Math.round(rF);
            if (r > top || r <= lo) return SEA_CELL.DRY;
            const ti = columnOf(x, y, z, cx, cy, cz, ax, ay, az);
            const g = heightOfColumn(ti);
            if (underGround(r, g, ax, ay, az)) return SEA_CELL.DRY;
            if (r > S) {
                // Nothing over the level within a cell of the diagonal where two faces meet, where the cell under it along
                // its own face's axis is the next face's (open air, or a crack of that face's) and it would overhang.
                const mid = ax > ay ? (ay > az ? ay : (ax > az ? az : ax)) : (ax > az ? ax : (ay > az ? az : ay));
                if (rF - mid < 2) return SEA_CELL.DRY;
                if (g >= S) return SEA_CELL.DRY;
                // The cliff stands whole or not at all: the diagonal's rule read at its foot (S + 1), for every level of it.
                if (wallTab && r <= S + wallH && S + 0.5 - mid >= 2 && margin(colFi, colU, colV) && !cracked(colFi, colU, colV) && !crevasse(colFi, colU, colV))
                    return SEA_CELL.ICE;                                                                         // the ice cliff
                // A ridge on the sheet, but not over a crevasse, which leaves no sheet under it.
                return ridges && r === S + 1 && !crevasse(colFi, colU, colV) && raised(colFi, colU, colV) ? SEA_CELL.ICE : SEA_CELL.DRY;
            }
            if (!ice) return SEA_CELL.LIQUID;
            // A CREVASSE opens the ice from the level down, cvD deep, keeping a floor of ice over the rock.
            if (crevTab && crevasse(colFi, colU, colV) && r > (g + 1 > S - cvD ? g + 1 : S - cvD)) return SEA_CELL.DRY;
            if (r < S - 1 || !cracked(colFi, colU, colV)) return SEA_CELL.ICE;
            return r === S ? SEA_CELL.DRY : SEA_CELL.CRACK_FLOOR;
        }
        return Object.freeze({ kind, name, level: S, top, lowest: lo, amount: ocean ? 'ocean' : 'lakes', cover,
                               lineae: ice ? nLin : 0, tempC: kind === 'lava' ? T : null, at });
    }
    return { isSolid, surfMat, solidR, airR: hi, surfLoR: lo, caveMinR: hasCaves ? caveMinR : null, roof,
             craters: craters.length / CN, tunnels: nWorms, mountains: mountAmp > 0, mesas: mesaAmt > 0,
             signature: V3_SIGNATURES[sig], valleys, volcanoes: volc.length / 8, spires: !!spSeed, tablelands: !!tbSeed,
             dunes: !!G.dunes, basaltColumns: !!baSeed, iceCrevasses: !!cvSeed, boldSmall: bold,
             biomes: bm ? bm.ids.slice() : [], surfaceBiome: bm ? surfaceBiome : null, biomeCover, columnBiome: bm ? columnBiome : null,
             organics: (sea) => life('organics', sea), flora: (sea) => life('flora', sea),
             genome: G, surfaceSea };
}

// ── ROCKY WORLDS, VERSION 4 (WG-11): THE GROUND SIMULATED ─────────────────────────────────────────────────────────────
// The owner's call, Sep 29: world generation rewritten from scratch, the biomes kept. The ground is geology.mjs's
// simulation (uplift from heat, impacts by air and age, erosion by liquids, air and ice), sampled at every column and
// gentle by design; the rock follows it (loose ground one crust, bare rock the other, the dominant crust kept, so a
// world's albedo and temperature stay); caves only where physics makes them (karst, lava tubes). What version 3 built
// around its terrain stays as it was: the seams joined, the land rolled over edges and corners, the seas, the biomes'
// looks and ground, and what grows. Version 3's placed landforms (mesas, tablelands, spires, basalt columns, signature
// landforms, noise ranges and caves) are gone: ranges, volcanoes and craters come only from the simulation.
// opts.fill: VERSION 5 (the owner, Sep 30: biomes cover 100% of a world). Every world has a biome that is not capped, a
// speck is never the last one left, and ground no biome's region reaches goes to the biome that best suits its climate
// and its ground (groundWeights), so no column is the world's bare ground. Without it, this is version 4 to the bit.
// opts.version: VERSION 6 (the owner, Oct 2) is version 5 with the bare ground of airless and thin-air worlds (mineral
// hills, mineral mare, crater fields, regolith plains: biomes.mjs wave 5), so an airless moon is no longer one capped
// biome from pole to pole. The biomes a version did not have are never on its worlds (eligibleBiomes opts.version).
// (version 19's towns: the straight-line distance between two town sites, by products alone: exactly specified arithmetic)
const dist19 = (a, b) => { const x = a[0] - b[0], y = a[1] - b[1], z = a[2] - b[2]; return Math.sqrt(x * x + y * y + z * z); };
function rockyTerrainV4(d, R, addr, genome, world, opts) {
    const fillAll = !!(opts && opts.fill);
    const ver = (opts && opts.version) || (fillAll ? 5 : 4);
    // VERSION 12 (the owner, Oct 11): a turning world has one temperature, its mean (no poles, no warm belt, no lapse with
    // height; a locked world keeps its day and night faces), its woods take needle or broadleaf by that mean, its dunes run
    // to the poles, Biome Wave 6's twelve biomes join the late roll, and two of them cut into the ground (THE CUTS).
    const v12 = ver >= FLAT_CLIMATE_SINCE;
    // VERSION 13 (the owner, Oct 12): a locked world has one temperature too, its mean (climate.mjs opts.oneTemp). Its dunes
    // still ring its star's point and its life still leans to it: those are its light and its geometry, not its warmth.
    const climOf = (w) => climateOf(w, ver >= ONE_TEMP_SINCE ? ONE_TEMP_CLIMATE : v12 ? FLAT_CLIMATE : undefined);
    const G = v3Genome(genome);
    // ONE BOLD LANDFORM (round 3's new idea) acts on a small world only. Everywhere else its factors are 1, and x * 1 is x.
    const bold = G.boldSmall && R <= V3_BOLD_MAX_R, bS = bold ? G.boldScale : 1, bT = bold ? G.boldThin : 1;
    const noise = d.noise;
    const refR = Math.max(1, R - 2), inv = 1 / refR;
    // THE SURFACE BAND (the owner's rule, Sep 29): a world's ground stands between its full radius R and 0.9 R, never
    // above R. Only what grows on it or lies on it (flora, organics, fixtures, bits) rises higher. In whole cells that
    // is floor(R / 10) levels under R: 4 on a Ø96 world, 1 on a Ø24. The simulation's ground is compressed into the band
    // (THE BAND, below), and the kube-scale features that give it its look (a river's channel, a dune's ridge) are cut
    // into it afterwards, a kube each.
    // BIOMES DECIDE THE RELIEF (the owner, Sep 30, of the relief options: "ensure that all biomes operate within those
    // height variations"): the land has LB cells over the sea to spend (biome-relief.mjs), and the sea's floor or a dry
    // world's basins as much again under it, so the band is deeper than R/10 on worlds under Ø120.
    const LB = landBudget(R), DEPTH = bandDepth(R);
    const lo = R - DEPTH, hi = R, LAND = hi - LB;          // the surface is always within [lo, hi]; the land from LAND up
    // THE LANDFORMS' HEIGHT, a little under R on the smallest worlds. A landform is the same share of a face at every
    // size, so on a Ø24 or Ø32 world a range or a mesa is a few cells wide and as many tall, and read as big blocky
    // steps (W03 in the review). Their heights are scaled by Rs: 55% of R at Ø24, all of it from about Ø72.
    const kS = R >= 36 ? 1 : 0.55 + 0.45 * (R - 12) / 24;
    const Rs = R * (kS < 0.55 ? 0.55 : kS) * G.landformScale;
    // BIOMES (WG-09): the genome's biomes that the world's climate allows (biomes.mjs), or none: the genome turns none on,
    // or the caller did not say what world this is (its star, climate and sea). The sea their rules read is the one this
    // world holds: `world.sea` says what its basins would hold (phase.mjs worldSeaOf), and its basins are dry by the
    // same draw surfaceSea reads (a dry world has no water to freeze into a glacier, and room for dunes and salt flats).
    const bioOn = world ? BIOME_IDS.filter((id) => G[id]) : [];
    // VERSION 4'S OWN BIOMES (the atlas build-out, Sep 30): the atlas's later biomes are no gene of version 3's pool,
    // so each one the world's climate allows is on by its own roll (half the time), after the genes' own, in an order
    // of the world's own (so the catalog's last are as likely as its first); the map keeps the first four the climate
    // allows.
    if (world) {
        const clim4 = climOf(world), late = [];
        for (const id of eligibleBiomes(clim4, world, { version: ver })) {
            const b = biomeById(id);
            if (b && (b.since === 4 || b.since === 6 || b.since === 12) && makeRng((addr || '') + '#bio4-' + id)() < 0.5) late.push([makeRng((addr || '') + '#bio4o-' + id)(), id]);
        }
        late.sort((a, b) => a[0] - b[0]);
        for (const [, id] of late) bioOn.push(id);
    }
    const bioSea = !bioOn.length || !world.sea ? null : world.sea.kind === 'lava' ? world.sea
        : (world.sea.kind === 'liquid' || world.sea.kind === 'ice') && v3Boost(makeRng((addr || '') + '#seas')(), G.seaBoost) >= V3_SEA_AMOUNT[world.sea.kind][0] ? world.sea : null;
    let bioWorld = bioOn.length ? { ...world, sea: bioSea } : null, bioClim = bioWorld ? climOf(bioWorld) : null;
    // Version 4's map holds each biome's cap and spread (biomes.mjs maxCover, spread); a biome that would be a speck is
    // taken off it once the simulation can say so (SPECKS ARE LEFT OFF, below), so these three may be remade there.
    let bm = bioOn.length ? biomeMap(bioClim, bioWorld, bioOn, refR, { caps: true }) : null;
    // VERSION 5: A BIOME FOR ALL OF IT. A world whose genome turned no biome on, or only capped ones (relics, outcrops,
    // hollows: each held to a share of the world), takes the first biome its climate allows that is not capped, in an
    // order of the world's own, as its dominant one.
    // …AND TWO WHERE THE CLIMATE ALLOWS TWO: one biome filling a whole world is one colour from pole to pole, so a world
    // with fewer than two uncapped biomes takes the next its climate allows, in the same order, after its own.
    // A SEA THAT RULES EVERY ONE OUT: a hot or cold world with a liquid sea that is not water allows only the capped
    // biomes (the land's own, dunes and salt flats, want no liquid sea), so its map is drawn for its land without the sea,
    // as a world whose genome turned nothing on already is. Only a world that allows no uncapped biome either way (about
    // one in fifty: airless, neither hot nor cold) lets its first capped biome fill it, past its cap.
    if (fillAll && world) {
        const capped = (id) => { const b = biomeById(id); return !!(b && b.maxCover > 0 && b.maxCover < 1); };
        let bw5 = bioWorld || { ...world, sea: null }, bc5 = bioClim || climOf(bw5);
        if (bw5.sea && !eligibleBiomes(bc5, bw5, { anyRarity: true, version: ver }).some((id) => !capped(id))) {
            const dry = { ...bw5, sea: null }, dc = climOf(dry);
            if (eligibleBiomes(dc, dry, { anyRarity: true, version: ver }).some((id) => !capped(id))) { bw5 = dry; bc5 = dc; }
        }
        const elig = eligibleBiomes(bc5, bw5, { anyRarity: true, version: ver }).map((id) => [makeRng((addr || '') + '#bio5o-' + id)(), id]).sort((a, b) => a[0] - b[0]).map(([, id]) => id);
        const have = bm ? bm.ids.slice() : [], unc = have.filter((id) => !capped(id));
        const add = elig.filter((id) => !capped(id) && have.indexOf(id) < 0).slice(0, Math.max(0, 2 - unc.length));
        if (!unc.length && !add.length && elig.length && have.indexOf(elig[0]) < 0) add.push(elig[0]);
        if (add.length) {
            bioWorld = bw5; bioClim = bc5;
            const ids = unc.length ? [...have, ...add] : [add[0], ...have, ...add.slice(1)];   // a world with no uncapped biome takes the first as its dominant
            bm = biomeMap(bioClim, bioWorld, ids.slice(0, 4), refR, { caps: true });
        }
    }
    let bw = bm ? new Float64Array(bm.ids.length) : null, bk = bm ? bm.ids.map((id) => BIOME_IDS.indexOf(id)) : null;
    let fit5 = bm ? new Float64Array(bm.ids.length) : null, gm5 = bm ? new Float64Array(bm.ids.length) : null;   // version 5's fill (groundWeights)
    let fillK5 = -1, fillW5 = 0;                             // …and, per column, which biome took the rest and how much

    // ---- VERSION 3'S OWN DRAWS. Its large fields are read in the unit cube's space (a column's point divided by the
    // reference radius), so a feature is the same share of a face on every size of world. ----
    const rel = makeRng((addr || '') + '#relief3');
    const o3 = () => [rel() * 1000, rel() * 1000, rel() * 1000];
    const W1 = o3(), W2 = o3(), W3 = o3(), OC = o3(), OH = o3(), OG = o3(), OV = o3(), OM = o3(), ON = o3();
    const OS = o3(), OL = o3(), OP = o3(), OU = o3(), OD = o3(), OR = o3(), OB = o3(), OA = o3(), OK = o3();
    const kValley = rel(), kStrata = rel(), kStrataT = rel();
    let duSeed = null, duDir = null;
    const duSpace = 8 * G.duneSpacing;
    if (G.dunes || (bm && bm.ids.some((id) => reliefOf(id).style === 'dunes'))) {   // the dune sea (a firn field's ridges, an oasis's dunes) in the same wind
        const r = makeRng((addr || '') + '#dune3');
        const dir = () => { const x = r() * 2 - 1, y = r() * 2 - 1, z = r() * 2 - 1, l = Math.sqrt(x * x + y * y + z * z) || 1; return [x / l, y / l, z / l]; };
        const w1 = dir(), w2 = dir();
        duSeed = [r() * 1000, r() * 1000, r() * 1000, r() * 1000, r() * 1000, r() * 1000];
        duDir = [];
        for (let a = 0; a < 3; a++) {                      // per face axis: the wind's two tangential components, unit length
            const t1 = (a + 1) % 3, t2 = (a + 2) % 3;
            let x = w1[t1], y = w1[t2], l = Math.sqrt(x * x + y * y);
            if (l < 0.35) { x = w2[t1]; y = w2[t2]; l = Math.sqrt(x * x + y * y) || 1; }
            duDir.push(x / l, y / l);
        }
    }
    // DUNES BUILT BY THE WIND (round 2, the owner's pick). Their crests lie across the prevailing wind, as ONE pattern
    // over the whole world, so a dune field runs on over an edge and round a corner as the ground does: a turning world's
    // winds run east and west round its spin axis (the westerlies), so its crests lie along lines of longitude, a whole
    // number of them round the world; a locked world's run over the ground toward its day side, so its crests ring the
    // point under its star. A little bend, and none at the poles (or under the star and opposite it), where the lines
    // meet. Only air of some substance lifts sand (none under 2 mbar, all of it from 10: Mars, at 6, has its dune
    // fields), and only loose ground (geology.mjs cover) feeds them.
    const airMbar = world && !world.airless ? (+world.pressureMbar || 0) : 0;
    const windLift = v2smooth((airMbar - 2) / 8);
    const duRot = world ? rotationOf({ addr: addr || '', star: world.star || null }) : null;
    const duA = duRot ? duRot.dir : [1, 0, 0];
    // two directions square to the axis (b1, b2 = a x b1), for the longitude round it
    let duB1 = Math.abs(duA[2]) < 0.9 ? [duA[1], -duA[0], 0] : [0, duA[2], -duA[1]];
    { const l = Math.sqrt(duB1[0] * duB1[0] + duB1[1] * duB1[1] + duB1[2] * duB1[2]); duB1 = [duB1[0] / l, duB1[1] / l, duB1[2] / l]; }
    const duB2 = [duA[1] * duB1[2] - duA[2] * duB1[1], duA[2] * duB1[0] - duA[0] * duB1[2], duA[0] * duB1[1] - duA[1] * duB1[0]];
    const duRm = refR * 0.85, duK = Math.max(3, Math.round(2 * Math.PI * duRm / duSpace));   // dunes round a mid latitude
    // The angle of (x, y) in exact arithmetic: a quarter-turn reduction and atan(t) ~ t pi/4 - t (t - 1)(0.2447 + 0.0663 t),
    // good to about a thousandth of a radian, which a dune does not notice.
    const atan2e = (y, x) => {
        const ax = x < 0 ? -x : x, ay = y < 0 ? -y : y;
        if (ax === 0 && ay === 0) return 0;
        const t = ax > ay ? ay / ax : ax / ay;
        let r = t * (0.7853981633974483 - (t - 1) * (0.2447 + 0.0663 * t));
        if (ay > ax) r = 1.5707963267948966 - r;
        if (x < 0) r = 3.141592653589793 - r;
        return y < 0 ? -r : r;
    };
    // The crest phase at a direction (in wavelengths; its fractional part places the column on a dune), and how much of
    // the dune stands there (out.fade).
    const duOut = { fade: 0 };
    function duneAt(ux, uy, uz) {
        const l = Math.sqrt(ux * ux + uy * uy + uz * uz), qx = ux / l, qy = uy / l, qz = uz / l;
        if (duRot && duRot.regime === 'locked') {
            const c = qx * duA[0] + qy * duA[1] + qz * duA[2], ch = Math.sqrt(2 - 2 * c > 0 ? 2 - 2 * c : 0);   // 0 under the star, 2 opposite
            duOut.fade = v2smooth((ch - 0.2) / 0.25) * v2smooth((1.8 - ch) / 0.25);
            return ch * duRm / duSpace;
        }
        const x = qx * duB1[0] + qy * duB1[1] + qz * duB1[2], y = qx * duB2[0] + qy * duB2[1] + qz * duB2[2];
        duOut.fade = v2smooth((Math.sqrt(x * x + y * y) - 0.25) / 0.25);                                        // not at the poles
        return atan2e(y, x) * duK / (2 * Math.PI);
    }
    // A DUNE'S HEIGHT AT A DIRECTION, 0 to 1, with its bend. Before version 12 a turning world's dunes faded out toward its
    // poles, where the lines of longitude they lie along crowd together. VERSION 12'S DUNES RUN TO THE POLES (the owner, Oct
    // 11: no latitude): near each pole the crests turn into rings round it, as a locked world's ring its star's point, the
    // two patterns blended where the longitudes began to crowd. A locked world's are as they were.
    function duneShape(ux, uy, uz, bend) {
        const s = duneAt(ux, uy, uz) + bend, f = s - Math.floor(s), prof = f < 0.72 ? f / 0.72 : (1 - f) / 0.28, t = duOut.fade;
        if (!v12 || (duRot && duRot.regime === 'locked') || t >= 1) return prof * t;
        const l = Math.sqrt(ux * ux + uy * uy + uz * uz), c = (ux * duA[0] + uy * duA[1] + uz * duA[2]) / l, ac = c < 0 ? -c : c;
        const ch = Math.sqrt(2 - 2 * ac > 0 ? 2 - 2 * ac : 0), s2 = ch * duRm / duSpace + bend, f2 = s2 - Math.floor(s2);
        return prof * t + (f2 < 0.72 ? f2 / 0.72 : (1 - f2) / 0.28) * (1 - t);
    }
    // ICE CREVASSES: the zero lines of a field of their own, about two columns wide, cut into the ice (makeSea).
    let cvSeed = null;
    if (G.iceCrevasses) { const r = makeRng((addr || '') + '#crevasse3'); cvSeed = [r() * 1000, r() * 1000, r() * 1000]; }
    // BIOMES: their ground's own draws, from a stream of its own.
    let bioSeed = null, bioCode = 0, bioFootH = 0;           // bioCode, bioFootH: what biomeGround last decided, for rawAt
    if (bm) { const r = makeRng((addr || '') + '#biome3');
              bioSeed = [r() * 1000, r() * 1000, r() * 1000, Math.floor(r() * 0x7fffffff), Math.floor(r() * 0x7fffffff), r() * 1000, r() * 1000, r() * 1000]; }


    // ---- THE GROUND, SIMULATED (geology.mjs, WG-11). The rivers drain to the sea this world will hold, so its share is
    // worked out first, from the same '#seas' draws the sea below reads (VERSION 3 SEAS), and only a sea the draws keep
    // (or lava, which the temperature keeps) counts as the world's liquid.
    const s4 = makeRng((addr || '') + '#seas'), kSea4 = v3Boost(s4(), G.seaBoost), kCover4 = s4();
    const sea4 = world && world.sea ? world.sea : null;
    let seaShare = 0, seaKept = false, oceanShare = 0;
    if (sea4 && (sea4.kind === 'liquid' || sea4.kind === 'ice')) {
        const [dryBelow, lakesBelow] = V3_SEA_AMOUNT[sea4.kind];
        seaKept = kSea4 >= dryBelow;
        // An ocean of either kind splits the crust into continents and ocean floor (geology.mjs); lakes do not.
        if (seaKept && kSea4 >= lakesBelow) oceanShare = G.oceanCoverMin + (G.oceanCoverMax - G.oceanCoverMin) * kCover4;
        if (seaKept && sea4.kind === 'liquid')
            seaShare = kSea4 >= lakesBelow ? oceanShare : (0.03 + 0.12 * kCover4) * G.lakeScale;
    }
    const geoWorld = world ? { ...world, sea: sea4 && (sea4.kind === 'lava' || seaKept) ? sea4 : null } : null;
    const geo = geologyOf(addr, R, geoWorld, noise, seaShare, oceanShare);
    const rvOut = { cut: 0, chan: false };
    const gf = { h: 0, cover: 0, low: 0, vol: 0, river: 0, lake: 0, range: 0, lakeIn: 0, lakeLv: 0 };
    const gnd = { cover: 0, range: 0, vol: 0, lake: 0, river: 0 };   // the ground under a column, for its biome
    // Lava pools and their warm ground at a size a small world's columns can hold (at 3.1 across the unit cube a pool
    // was a column and a half on a Ø24 world, and the fields flickered column to column).
    const poolF = R < 30 ? 3.1 * (0.5 + 0.5 * (R - 12) / 18) : 3.1, SULFUR_B = BIOME_IDS.indexOf('sulfurSprings'), RIFT_B = BIOME_IDS.indexOf('voidRifts');
    const PITCH_B = BIOME_IDS.indexOf('pitch'), HALITE_B = BIOME_IDS.indexOf('halite'), MUD_B = BIOME_IDS.indexOf('mudpots');   // version 12
    const SLOTS_B = BIOME_IDS.indexOf('slots'), CENOTE_B = BIOME_IDS.indexOf('cenotes');
    // A pool's own code, and the biome whose crust it is let into where it cannot be (THE POOLS LET IN, below).
    const POOL_OWN = new Map([[BIO_SULFUR, SULFUR_B], [BIO_RIFT, RIFT_B], [BIO_PITCH, PITCH_B], [BIO_BRINE, HALITE_B], [BIO_MUD, MUD_B]]);
    const moltenWorld = !!(world && ((world.sea && world.sea.kind === 'lava') || (+world.tempC || 0) >= 700));   // volcanic all over
    const b4 = makeRng((addr || '') + '#biome4'), pickSeed = [b4() * 1000, b4() * 1000, b4() * 1000];
    // SPECKS ARE LEFT OFF (the owner, Sep 30: a biome that would cover under 1% of a world is left off it). Before any
    // column is built, the biomes are picked on an even grid over the faces as biomeGround will pick them (the
    // simulation's ground and lowlands, each biome's own ground, the same dither), and one picked on under SPECK of it
    // is taken off the map: its few columns go to the biomes round them, and the world's list names only what can be
    // found. The rest keep their order, so a dominant biome stays the dominant one unless it is the speck.
    // Like the world's list (biomeCover), ground the sea will cover counts only toward a biome that lives under it: the
    // coast is taken at the sea's share of the grid's geology heights, as THE GROUND'S HEIGHTS takes it of the columns'.
    if (bm) {
        const M = 6 * SPECK_GRID * SPECK_GRID, hs = new Float64Array(M), ks = new Int16Array(M);
        let t = 0;
        for (let f = 0; f < 6; f++) for (let i = 0; i < SPECK_GRID; i++) for (let j = 0; j < SPECK_GRID; j++) {
            const q = [0, 0, 0], ax = f >> 1;
            q[ax] = (f & 1) ? -1 : 1; q[(ax + 1) % 3] = (i + 0.5) / SPECK_GRID * 2 - 1; q[(ax + 2) % 3] = (j + 0.5) / SPECK_GRID * 2 - 1;
            geo.fieldsAt(q[0], q[1], q[2], gf);
            gnd.cover = gf.cover; gnd.range = gf.range; gnd.vol = gf.vol; gnd.lake = gf.lake; gnd.river = gf.river;
            groundWeights(q[0], q[1], q[2], gf.low, gnd);
            hs[t] = gf.h; ks[t++] = pickAt(q[0], q[1], q[2]);
        }
        const seaC = seaShare > 0 ? seaShare : oceanShare;
        const shore = seaC > 0 ? Float64Array.from(hs).sort()[Math.min(M - 1, Math.floor(seaC * M))] : -Infinity;
        const n = new Float64Array(bm.ids.length);
        for (let u = 0; u < M; u++) { const k = ks[u]; if (k >= 0 && (hs[u] >= shore || BIOMES[bk[k]].underSea)) n[k]++; }
        const keep = bm.ids.filter((id, k) => n[k] >= SPECK * M);
        // VERSION 5: the biome that will hold the rest is never a speck taken off: the uncapped one picked most stays.
        if (fillAll && !keep.some((id) => !bm.capped[bm.ids.indexOf(id)])) {
            let best = -1; for (let k = 0; k < bm.ids.length; k++) if (!bm.capped[k] && (best < 0 || n[k] > n[best])) best = k;
            if (best >= 0) keep.unshift(bm.ids[best]);
        }
        if (keep.length < bm.ids.length) {
            bm = keep.length ? biomeMap(bioClim, bioWorld, keep, refR, { caps: true }) : null;
            bw = bm ? new Float64Array(bm.ids.length) : null; bk = bm ? bm.ids.map((id) => BIOME_IDS.indexOf(id)) : null;
            fit5 = bm ? new Float64Array(bm.ids.length) : null; gm5 = bm ? new Float64Array(bm.ids.length) : null;
        }
    }
    let colChan = false, colDune = 0;
    // A column's relief (biome-relief.mjs), as its biomes blend it: the geology's share, the biomes' own, and their field.
    let colRelGeo = 1, colRelAmp = 0, colRelG = 0, duneG = 0;
    const rl4 = makeRng((addr || '') + '#relief4'), relSeed = [];
    for (let k = 0; k < 9; k++) relSeed.push(rl4() * 1000);
    const RELIEF_SEED = { hills: 0, hummocks: 3, swells: 6, terraces: 0 }, DUNE_SEA_RELIEF = reliefOf('duneSea');
    const CRATER_SEED = Math.floor(relSeed[8] * 4096);     // the craters' own placing, from a draw already made (no new one)
    let dunesHere = -1;                                       // the wind's dunes at this column, for any biome but the dune sea
    function reliefField(rl, px, py, pz) {
        if (rl.style === 'dunes') {                           // the dune sea's own (duneG, from loose ground); a firn field's or
            if (rl === DUNE_SEA_RELIEF) return duneG;         // an oasis's are the same wind's, wherever the biome is
            if (dunesHere < 0) {
                const ux = px * inv, uy = py * inv, uz = pz * inv;
                const bend = noise(ux * 2.2 + duSeed[3], uy * 2.2 + duSeed[4], uz * 2.2 + duSeed[5]) * 0.55;
                if (v12) dunesHere = duneShape(ux, uy, uz, bend);
                else { const s = duneAt(ux, uy, uz) + bend; dunesHere = duneProfile(s - Math.floor(s)) * duOut.fade; }
            }
            return dunesHere;
        }
        if (rl.style === 'craters') return craterField(px, py, pz, rl.size, CRATER_SEED);   // version 6's crater fields
        if (rl.style === 'flat' || !(rl.size > 0)) return 0;
        const s = 1 / rl.size, o = RELIEF_SEED[rl.style] || 0;
        const g = spread(0.5 + 0.5 * noise(px * s + relSeed[o], py * s + relSeed[o + 1], pz * s + relSeed[o + 2]));
        return rl.style === 'terraces' ? terraceOf(g) : g;   // terraces: hills flattened into steps
    }
    const RANGE_DETAIL = 0.6 + R * 0.06;                     // kubes: 1.3 on a Ø24 world's ranges, 3.5 on a Ø96's

    // The surface height, in cells above (or below) the old surface R, at reference-shell point p: the simulated ground,
    // the fresh small craters too small for its grid, and a little texture on bare rock (none on loose ground), under
    // half a cell either way so the ground stays gentle. `e` is p's distance to the nearest cube edge along the face.
    function relief(px, py, pz, e) {
        const ux = px * inv, uy = py * inv, uz = pz * inv;
        geo.fieldsAt(ux, uy, uz, gf);                         // every field of the simulation here, read at once
        const Hb = gf.h - 0.5;
        const l = Math.sqrt(ux * ux + uy * uy + uz * uz);
        let H = Hb + geo.cratersAt(ux / l, uy / l, uz / l);
        // RIVERS (round 2): the valley cut into the ground, and the channel down its middle, a kube deeper, that
        // holds still water on a world with a liquid (makeSea). colChan tells rawAt, as bioCode does.
        geo.riversAt(ux / l, uy / l, uz / l, rvOut);
        colChan = rvOut.chan; colDune = 0; colRelGeo = 1; colRelAmp = 0; colRelG = 0; duneG = 0; dunesHere = -1;
        if (rvOut.cut > 0) H -= rvOut.cut;                    // the valley; its channel's kube is cut after the band (sample)
        const rock = 1 - gf.cover;
        if (rock > 0.2) H += rock * 0.4 * noise(px * 0.23 + OU[0], py * 0.23 + OU[1], pz * 0.23 + OU[2]);   // (under a fifth rock: too little to show)
        // SPURS AND VALLEYS on the ranges (round 2): a ridged field, crests where it crosses zero,
        // as strong as the ground is mountainous (geology.mjs rangeAt), with their mean taken out so a range keeps
        // its height. The simulation's grid is two kubes a node on the biggest worlds, too coarse to cut these.
        const rw = gf.range;
        if (rw > 0.02) {
            const n1 = noise(px * 0.2 + OM[0], py * 0.2 + OM[1], pz * 0.2 + OM[2]);
            // Crests only: the ridges stand on the range and the valleys between them are the range's own ground, so
            // no pit is dug for a sea or lava to fill.
            const r1 = 1 - 2.6 * (n1 < 0 ? -n1 : n1), c1 = r1 > 0 ? r1 * r1 : 0;
            H += (rw > 1 ? 1 : rw) * RANGE_DETAIL * 1.3 * c1;
        }
        if (bm) {
            gnd.cover = 1 - rock; gnd.range = rw; gnd.vol = gf.vol; gnd.lake = gf.lake; gnd.river = gf.river;
            H = biomeGround(ux, uy, uz, px, py, pz, e, H, gf.low, Hb, gnd);
        }
        return H;
    }
    // Each biome's weight at a column, kept only where its ground is (below), into bw; and which one the column shows.
    function groundWeights(ux, uy, uz, lowW, g) {
        const cover = g.cover;
        bm.weights(ux, uy, uz, lowW, bw);   // the lowlands are the simulation's (geology.mjs lowAt)
        // BIOMES READ THE GROUND (round 2, the owner's pick). The climate says which biomes a world can have and the
        // regions where each may be; the simulation says where on that ground each one is: each keeps a column only
        // where its ground is, cut off sharply (the pick dithers between biomes by their weights, so a weight merely
        // lowered would scatter a biome's columns over the rest instead of making its patch smaller).
        //   dune sea         loose ground, and air that lifts sand (about a quarter of dry land at most)
        //   salt flats       closed basins and the flattest loose lowlands: dried lake beds and pans
        //   glacier          the uplands and ranges
        //   volcanic fields  fresh lava, round hotspots and along volcanic arcs
        //   woods, canopy,   moist ground: along rivers and on low ground near the water table
        //   gill towers
        //   bloom carpets    loose, moist flats (meadows)
        //   spirit gardens   bare rock and volcanic ground (silicon life, growing as crystal)
        const sed = v2smooth((cover - 0.3) / 0.35), sandy = windLift * sed;
        const moist = Math.max(g.river * 1.6, v2smooth((lowW - 0.35) / 0.35) * 0.85);
        fillK5 = -1; fillW5 = 0;
        if (fillAll) bm.fit(ux, uy, uz, lowW, fit5);
        for (let k = 0; k < bw.length; k++) {
            if (!(bw[k] > 0) && !fillAll) continue;
            const b = bk[k];
            let m = 1;
            if (b === 0) m = windLift > 0.12 ? v2smooth((sed - 0.74) / 0.1) : 0;
            else if (b === 1) m = Math.max(v2smooth((g.lake - 0.12) / 0.2), v2smooth((lowW - 0.8) / 0.1) * v2smooth((cover - 0.6) / 0.1));
            else if (b === 2) m = Math.max(v2smooth((0.45 - lowW) / 0.15), v2smooth((g.range - 0.15) / 0.15));
            else if (b === 3) m = moltenWorld ? 1 : Math.max(v2smooth((g.vol - 0.2) / 0.15), v2smooth((g.range - 0.25) / 0.15) * 0.8);
            else if (b === 6 || b === 11) m = v2smooth((moist - FOREST_MOIST) / 0.15);   // woods and canopy: forests, spread wider (Sep 30)
            else if (b === 8) m = v2smooth((moist - 0.22) / 0.15);
            else if (b === 7) m = v2smooth((cover - 0.45) / 0.15) * v2smooth((moist - 0.15) / 0.15);
            else if (b === 12) m = Math.max(v2smooth((0.6 - cover) / 0.2), v2smooth((g.vol - 0.2) / 0.15));
            else if (BIOMES[b].ground) {                       // the atlas's later biomes, by the ground each keeps to
                const gd = BIOMES[b].ground;
                const forest = BIOMES[b].spread > 0;           // a forest keeps to moist or dry ground more loosely (Sep 30)
                m = gd === 'moist' ? v2smooth((moist - (forest ? FOREST_MOIST : 0.22)) / 0.15)
                  : gd === 'dry' ? (forest ? v2smooth((0.45 - moist) / 0.15) * v2smooth((cover - 0.2) / 0.2)
                                           : v2smooth((0.3 - moist) / 0.15) * v2smooth((cover - 0.3) / 0.2))
                  : gd === 'coast' || gd === 'low' ? v2smooth((lowW - 0.6) / 0.2)   // 'low': the lowlands, sea or none (a mare)
                  : gd === 'rock' ? v2smooth((0.6 - cover) / 0.2)
                  : gd === 'volcanic' ? (moltenWorld ? 1 : Math.max(v2smooth((g.vol - 0.2) / 0.15), v2smooth((g.range - 0.25) / 0.15) * 0.8))
                  : gd === 'fire' ? (moltenWorld ? 1 : Math.max(v2smooth((g.vol - 0.2) / 0.15), v2smooth((g.range - 0.25) / 0.15) * 0.8, v2smooth((0.6 - cover) / 0.2)))
                  // version 12: a coast, the more so on volcanic ground (a column coast); a water world's worked sediment (cenotes)
                  : gd === 'coastVolcanic' ? v2smooth((lowW - 0.6) / 0.2) * (0.45 + 0.55 * (moltenWorld ? 1 : Math.max(v2smooth((g.vol - 0.2) / 0.15), v2smooth((g.range - 0.25) / 0.15) * 0.8)))
                  : gd === 'karst' ? v2smooth((cover - 0.45) / 0.15) * v2smooth((lowW - 0.4) / 0.2)
                  : gd === 'ice' || gd === 'high' ? v2smooth((0.45 - lowW) / 0.15) : 1;   // 'high': the uplands (mineral hills)
            }
            bw[k] *= m;
            if (fillAll) gm5[k] = m;
        }
        // VERSION 5: WHAT NO REGION REACHES. The rest of the column goes to the biome that suits it best: its climate here
        // (bm.fit) times its own ground (the m above). A capped biome takes none of it, or a cap would be no cap; if
        // neither climate nor ground favours any, the best by climate, then the first uncapped one.
        if (fillAll) {
            let ws = 0;
            for (let k = 0; k < bw.length; k++) ws += bw[k];
            if (ws < 1) {
                let best = -1, bv = 0;
                for (let k = 0; k < bw.length; k++) { const v = bm.capped[k] ? 0 : fit5[k] * gm5[k]; if (v > bv) { bv = v; best = k; } }
                if (best < 0) for (let k = 0; k < bw.length; k++) { const v = bm.capped[k] ? 0 : fit5[k]; if (v > bv) { bv = v; best = k; } }
                if (best < 0) { best = 0; for (let k = 0; k < bw.length; k++) if (!bm.capped[k]) { best = k; break; } }
                bw[best] += 1 - ws; fillK5 = best; fillW5 = 1 - ws;
            }
        }
    }
    function pickAt(ux, uy, uz) {
        // The pick reads a smooth field, not a hash of the column: where two biomes blend, the line between them wanders
        // instead of breaking into a speckle of both (round 1 found small worlds salt-and-peppered).
        const hn = 0.5 + 0.9 * noise(ux * 9 + pickSeed[0], uy * 9 + pickSeed[1], uz * 9 + pickSeed[2]);
        return bm.pick(bw, hn < 0 ? 0 : hn > 0.999 ? 0.999 : hn);
    }
    function biomeGround(ux, uy, uz, px, py, pz, e, H, lowW, Hc, g) {
        groundWeights(ux, uy, uz, lowW, g);
        const cover = g.cover, sed = v2smooth((cover - 0.3) / 0.35), sandy = windLift * sed;
        const dev = H - Hc, plain = v2smooth((0.07 * R + 2 - (dev < 0 ? -dev : dev)) / (0.04 * R + 1));
        const hx = px * 2, hy = py * 2, hz = pz * 2;          // the column's middle in half cells: whole numbers, for cellHash
        let pool = false, warm = false, spire = false, sulf = false, rift = false, pitchP = false, brineP = false, mudP = false;
        for (let k = 0; k < bw.length; k++) {
            const w = bw[k] - (k === fillK5 ? fillW5 : 0), b = bk[k];   // version 5's fill shapes no land (see the relief below)
            if (!(w > 0)) continue;
            if (b === 0) {                                    // THE DUNE SEA: the land smoothed, and dunes on it
                H += (Hc - H) * 0.6 * w * plain;
                if (sandy > 0) {                              // one pattern over the world: no edge stops it
                    const bend = noise(ux * 2.2 + duSeed[3], uy * 2.2 + duSeed[4], uz * 2.2 + duSeed[5]) * 0.55;
                    // along the wind: the gentle windward slope rises, the steep lee falls downwind
                    if (v12) { const sh = duneShape(ux, uy, uz, bend); colDune = sh * w * sandy; duneG = sh; }
                    else {
                    const s = duneAt(ux, uy, uz) + bend, f = s - Math.floor(s);
                    const prof = f < 0.72 ? f / 0.72 : (1 - f) / 0.28;
                    // a ridge a kube high where the profile stands, raised after the band (sample): colDune says how much
                    colDune = prof * w * duOut.fade * sandy;
                    duneG = prof * duOut.fade;                 // the dune sea's relief (biome-relief.mjs 'dunes')
                    }
                }
            } else if (b === 1) H += (Math.round(Hc) - H) * w * plain;   // SALT FLATS: level, a step where the land crosses a level
            else if (b === 2) H += (Hc + 1 - H) * 0.45 * w * plain;       // A GLACIER: a sheet over the land, its relief half buried
            else if (b === 3) {                               // VOLCANIC FIELDS: basalt plains, lava pools in their dips
                H += (Hc - H) * 0.3 * w * plain;
                const pn = noise(ux * poolF + bioSeed[0], uy * poolF + bioSeed[1], uz * poolF + bioSeed[2]);
                const pw = v2smooth((pn - 0.3) / 0.08) * v2smooth((w - 0.5) / 0.2) * plain;
                if (pw > 0) { H += (Math.floor(Hc) - 1 - H) * pw; if (pw >= 0.5) pool = true; }
                if (pn > 0.16 && w > 0.4) warm = true;
            } else if (b === SULFUR_B) {                      // SULFUR SPRINGS: acid-green pools in the crust, let in a cell
                const pn = noise(ux * poolF * 1.3 + bioSeed[5], uy * poolF * 1.3 + bioSeed[6], uz * poolF * 1.3 + bioSeed[7]);
                if (v2smooth((pn - 0.32) / 0.08) * v2smooth((w - 0.5) / 0.2) * plain >= 0.5) sulf = true;
            } else if (b === RIFT_B && w > 0.5) {               // VOID RIFTS: fissures along the zero lines of a field, glowing violet
                const rn = noise(ux * 7 + bioSeed[5], uy * 7 + bioSeed[6], uz * 7 + bioSeed[7]);
                if ((rn < 0 ? -rn : rn) < 0.05) rift = true;
            } else if (b === PITCH_B || b === HALITE_B || b === MUD_B) {   // VERSION 12'S POOLS, let into the crust a cell as a spring's are:
                // a pitch lake's tar in wide pools, a halite field's pink brine, a mudpot field's small puddles
                const f = b === MUD_B ? poolF * 2.4 : poolF * 1.1, o = b === PITCH_B ? 11.3 : b === HALITE_B ? 23.7 : 37.1, th = b === PITCH_B ? 0.22 : b === HALITE_B ? 0.24 : 0.34;
                const pn = noise(ux * f + bioSeed[0] + o, uy * f + bioSeed[1] + o, uz * f + bioSeed[2] + o);
                if (v2smooth((pn - th) / 0.08) * v2smooth((w - 0.5) / 0.2) * plain >= 0.5) { if (b === PITCH_B) pitchP = true; else if (b === HALITE_B) brineP = true; else mudP = true; }
            }
            // CRYSTAL FIELDS grow their crystals as bits in version 4 (organics.mjs, ctx.crystalBits): no spire of ground.
        }
        const pk = pickAt(ux, uy, uz);
        bioCode = pool ? BIO_LAVA : sulf ? BIO_SULFUR : rift ? BIO_RIFT : pitchP ? BIO_PITCH : brineP ? BIO_BRINE : mudP ? BIO_MUD
                : spire ? BIO_CRYSTAL : pk < 0 ? 0 : bk[pk] === 3 && warm ? BIO_WARM : bk[pk] + 1;
        // THE RELIEF THE BIOMES GIVE THEIR GROUND: their weights here blend their shares of the land's budget, and what
        // no biome holds goes to the geology (biome-relief.mjs).
        // VERSION 5'S FILL KEEPS THE GEOLOGY'S SHAPE: ground a biome holds only because no region reached it wears that
        // biome (its look, its materials, what grows) on the land as the simulation shaped it, as version 4 left it bare.
        // Shaping it by the biome flattened every world's landforms wherever the fill reached, which was most of them.
        let ws = 0, gW = 0, aW = 0, fW = 0;
        for (let k = 0; k < bw.length; k++) {
            const w = bw[k] - (k === fillK5 ? fillW5 : 0);
            if (!(w > 0)) continue;
            const rl = reliefOf(BIOME_IDS[bk[k]]);
            ws += w; gW += w * rl.geo; aW += w * rl.amp; fW += w * rl.amp * reliefField(rl, px, py, pz);
        }
        const tot = ws < 1 ? 1 : ws;
        colRelGeo = (gW + (ws < 1 ? 1 - ws : 0)) / tot; colRelAmp = aW / tot; colRelG = aW > 0 ? fW / aW : 0;
        return H;
    }

    // ---- THE HEIGHT TABLE, one sample per face column, and the seams joined: version 2's, copied ----
    const Wd = 2 * refR, WW = Wd * Wd;
    const table = new Int16Array(6 * WW).fill(V2_NONE);
    const raw = new Float64Array(6 * WW).fill(NaN);
    const bioTab = bm ? new Uint8Array(6 * WW) : null, bioFoot = bm ? new Int16Array(6 * WW) : null;   // per column: its look, a spire's foot
    // Per column, for still water (round 2): in a river's channel, and the lake level over it (0 none), in cells.
    const chanTab = new Uint8Array(6 * WW), lakeTab = new Int16Array(6 * WW), duneTab = new Uint8Array(6 * WW);
    const lakeNat = new Float64Array(6 * WW);                // a lake's natural level (before the band), 0 for none
    const relGeo = new Float32Array(6 * WW), relAmp = new Float32Array(6 * WW), relG = new Float32Array(6 * WW);
    const shaped = new Float64Array(6 * WW);                 // each column's height before its seams are joined (THE GROUND'S HEIGHTS)
    let cutTab = null;                                       // version 12: per column, how deep THE CUTS took it
    function rawAt(fi, iu, iv) {
        const ti = fi * WW + iu * Wd + iv;
        let h = raw[ti];
        if (h !== h) {
            const ax = fi >> 1, tu = iu - refR + 0.5, tv = iv - refR + 0.5, p = [0, 0, 0];
            p[ax] = (fi & 1) ? -refR : refR; p[(ax + 1) % 3] = tu; p[(ax + 2) % 3] = tv;
            h = raw[ti] = R + relief(p[0], p[1], p[2], refR - Math.max(Math.abs(tu), Math.abs(tv)));
            chanTab[ti] = colChan ? 1 : 0;
            duneTab[ti] = colDune >= 0.45 ? 1 : 0;
            // A lake's level where the column lies mostly in its basin (the level itself is flat out to the nodes round
            // it, which reach past the rim). relief() has just read the fields at this column's point.
            lakeNat[ti] = gf.lakeIn >= 0.45 ? R + gf.lakeLv : 0;   // mapped into the band with the ground, once it is known
            if (bm) { bioTab[ti] = bioCode; bioFoot[ti] = bioCode === BIO_CRYSTAL ? Math.round(R + bioFootH) : 0; }
            relGeo[ti] = colRelGeo; relAmp[ti] = colRelAmp; relG[ti] = colRelG;
        }
        return h;
    }
    // THE SEAMS JOINED BY DISTANCE, AT ANY HEIGHT, AND WHERE THE LAND IS LOW. A column within V3_JOIN of an edge takes in
    // the column folded over the edge onto the next face (the same distance in from it), fully at the edge and not at
    // all V3_JOIN in, so the two faces stand at one height where they meet whatever that height is, and the land rolled
    // over the edge (underGround) has no step on the diagonal. Version 2's join is kept beside it, whichever is the
    // stronger: a column whose ground stands no higher than its distance from the face's centre is where two faces'
    // ground meets on the diagonal inside the cube (a basin reaching an edge), however far in that is, and there the two
    // must agree or one face's ground overhangs the other's.
    function sample(fi, iu, iv) {
        const ax = fi >> 1, sa = (fi & 1) ? -1 : 1, tu = iu - refR + 0.5, tv = iv - refR + 0.5;
        rawAt(fi, iu, iv);
        const h = shaped[fi * WW + iu * Wd + iv];
        let sum = h, wsum = 1;
        for (let k = 0; k < 2; k++) {
            const t = k === 0 ? tu : tv, at = t < 0 ? -t : t, dEdge = refR - at;   // 0.5 at the rim
            if (dEdge >= V3_JOIN && (h + lo) * 0.5 - 0.5 - at >= 2 + V2_SEAM) continue;
            const nAx = (ax + 1 + k) % 3, nFi = nAx * 2 + (t > 0 ? 0 : 1), c = [0, 0, 0];
            c[ax] = sa * at; c[3 - ax - nAx] = k === 0 ? tv : tu;
            const nu = c[(nAx + 1) % 3] + refR - 0.5, nv = c[(nAx + 2) % 3] + refR - 0.5;
            rawAt(nFi, nu, nv);
            const hm = shaped[nFi * WW + nu * Wd + nv];
            const g = (h + hm) * 0.5 - 0.5 - at;
            const wLow = g <= 2 ? 1 : 1 - v2smooth((g - 2) / V2_SEAM);
            const wEdge = dEdge >= V3_JOIN ? 0 : 1 - v2smooth((dEdge - 0.5) / (V3_JOIN - 0.5));
            const w = wLow > wEdge ? wLow : wEdge;
            if (w > 0) { sum += w * hm; wsum += w; }
        }
        // the column's own kube-scale features, cut into the band: a dune's ridge up, a river's channel down
        const ti = fi * WW + iu * Wd + iv;
        const t = Math.round(sum / wsum) - chanTab[ti];      // (a dune's ridge is its biome's relief now)
        return t < lo ? lo : t > hi ? hi : t;
    }

    // THE BAND: every column's natural height (the simulation's ground and what the column adds to it) is known first,
    // then one affine map for the whole world takes its high ground (the 99.5th percentile) to R and its low ground (the
    // 1st) to R - DEPTH. It compresses, never stretches; the little over or under those is held at the band's edges
    // (sample). One map for all six faces keeps every edge and corner continuous.
    let bandTop = 0, bandK = 1;
    {
        const nat = new Float64Array(6 * WW);
        for (let ti = 0; ti < 6 * WW; ti++) nat[ti] = rawAt((ti / WW) | 0, ((ti % WW) / Wd) | 0, ti % Wd);
        nat.sort();
        const qLo = nat[Math.floor(nat.length * 0.01)], qHi = nat[Math.floor(nat.length * 0.995)];
        bandTop = qHi; bandK = qHi - qLo > DEPTH ? DEPTH / (qHi - qLo) : 1;
        // THE GROUND'S HEIGHTS (Sep 30): the coast is the geology's, at the share of the surface the world's sea will
        // cover (a dry world has none: all its ground is land, its biomes shaping it all); under it the floor falls smoothly to the band's bottom; over
        // it the land spends its LB cells as its biomes say, their own relief eased up from the shore. In a lake's basin
        // the geology alone, so the basin holds its lake.
        const n = nat.length, q = (p) => nat[Math.min(n - 1, Math.floor(p * n))];
        const cover = seaShare > 0 ? seaShare : oceanShare;
        const rs = cover > 0 ? q(cover) : q(0.01), rhi = q(0.995), rlo = q(0.001);   // a dry world: all its ground is land
        const up = (v) => { const x = (v - rs) / (rhi - rs > 1e-9 ? rhi - rs : 1e-9); return x < 0 ? 0 : x > 1 ? 1 : x; };
        for (let ti = 0; ti < 6 * WW; ti++) {
            const r = raw[ti];
            if (r < rs) { const y = (rs - r) / (rs - rlo > 1e-9 ? rs - rlo : 1e-9); shaped[ti] = LAND - 1 - (LAND - 1 - lo) * v2smooth(y > 1 ? 1 : y); continue; }
            const x = up(r), lake = lakeNat[ti] > 0;
            shaped[ti] = LAND + reliefHeight(lake ? reliefOf('none') : { geo: relGeo[ti], amp: relAmp[ti] }, x, relG[ti], v2smooth(x / 0.12), LB);
            if (lake) lakeTab[ti] = Math.floor(LAND + LB * up(lakeNat[ti]));
        }
    }
    function band(v) { return hi - (bandTop - v) * bandK; }
    // SINGLE STEPS (the owner's options): a land column stands at most one cell over the lowest of its four neighbours,
    // over an edge too, so a slope is a staircase and no hill has a cliff. The sea's floor and the coast stay where they
    // are; a lava pool is let into its plain by one cell.
    function neighbourColumn(ti, d) {
        const fi = (ti / WW) | 0, ax = fi >> 1, s = (fi & 1) ? -1 : 1, p = [0, 0, 0];
        p[ax] = s * refR; p[(ax + 1) % 3] = ((ti % WW) / Wd | 0) - refR + 0.5; p[(ax + 2) % 3] = ti % Wd - refR + 0.5;
        const t = (ax + 1 + (d >> 1)) % 3;
        p[t] += (d & 1) ? -1 : 1;
        if (p[t] > refR || p[t] < -refR) {                   // over an edge, onto the face that axis points to
            const ns = p[t] > 0 ? 1 : -1;
            p[t] = ns * refR; p[ax] = s * (refR - 0.5);
            return (t * 2 + (ns > 0 ? 0 : 1)) * WW + (p[(t + 1) % 3] + refR - 0.5) * Wd + (p[(t + 2) % 3] + refR - 0.5);
        }
        return fi * WW + (p[(ax + 1) % 3] + refR - 0.5) * Wd + (p[(ax + 2) % 3] + refR - 0.5);
    }
    {
        const n = 6 * WW, nb = new Int32Array(4 * n);
        for (let ti = 0; ti < n; ti++) { heightOfColumn(ti); for (let d = 0; d < 4; d++) nb[4 * ti + d] = neighbourColumn(ti, d); }
        for (let it = 0; it < 4 * DEPTH; it++) {
            let moved = 0;
            for (let ti = 0; ti < n; ti++) {
                const t = table[ti];
                if (t <= LAND) continue;
                let m = t;
                for (let d = 0; d < 4; d++) { const u = table[nb[4 * ti + d]] + 1; if (u < m) m = u; }
                if (m < LAND) m = LAND;
                if (m < t) { table[ti] = m; moved++; }
            }
            if (!moved) break;
        }
        // A RIVER STANDS LEVEL (version 8, the owner, Oct 4: river water "should remain level - more of a skinny basin than
        // the varying heights that currently generate"). Each river (its channel's columns, joined over edges as the ground
        // is) takes one floor, its lowest column's, and is cut down to it wherever its ground stands no more than
        // RIVER_LEVEL_RISE over that; so its water (a kube over the floor, makeSea) is one level its whole length, a long
        // narrow basin. Higher up its course it is a dry valley: no channel, and not the river biome. A bank lower than the
        // water is raised to hold it, so the level does not run out over the bank (makeSea lowers water that would).
        if (ver >= 8) {
            // Over the land only: a channel under the coast (LAND, where a sea world's sea stands) is the sea's, as before,
            // and a river that reaches the sea is levelled at the sea's own height, an inlet.
            const RIVER_LEVEL_RISE = 2, comp = new Uint8Array(n), list = [], onLand = (ti) => chanTab[ti] && table[ti] >= LAND - 1;
            for (let t0 = 0; t0 < n; t0++) {
                if (!onLand(t0) || comp[t0]) continue;
                list.length = 0; list.push(t0); comp[t0] = 1;
                let floor = table[t0];
                for (let k = 0; k < list.length; k++) {
                    const ti = list[k];
                    if (table[ti] < floor) floor = table[ti];
                    for (let d = 0; d < 4; d++) { const tn = nb[4 * ti + d]; if (tn >= 0 && onLand(tn) && !comp[tn]) { comp[tn] = 1; list.push(tn); } }
                }
                const water = floor + 1;
                for (const ti of list) {
                    if (table[ti] - floor > RIVER_LEVEL_RISE) { chanTab[ti] = 0; table[ti]++; continue; }   // upstream: a dry valley, its ditch filled
                    table[ti] = floor;
                }
                for (const ti of list) {                       // (at a river's mouth these banks are under the sea anyway)
                    if (!chanTab[ti]) continue;
                    for (let d = 0; d < 4; d++) { const tn = nb[4 * ti + d]; if (tn >= 0 && !chanTab[tn] && table[tn] < water) table[tn] = water; }
                }
            }
        }
        if (bioTab) for (let ti = 0; ti < n; ti++) {
            if (bioTab[ti] === BIO_LAVA) { if (table[ti] > lo) table[ti]--; continue; }
            const own = POOL_OWN.get(bioTab[ti]);
            if (own === undefined) continue;
            // A sulfur pool or a rift's fissure (and version 12's tar, brine and mud) is let in only where no neighbour
            // stands over it (a spring's terraces would make a rim of two); elsewhere the column is its biome's crust.
            let flat = table[ti] > lo;
            for (let d = 0; d < 4 && flat; d++) if (table[nb[4 * ti + d]] > table[ti]) flat = false;
            if (flat) table[ti]--; else bioTab[ti] = own + 1;
        }
        // ---- THE CUTS (version 12, the owner's picks of Oct 11): slot canyons and cenotes, cut into the ground after the band
        // and its single steps, so their walls are sheer. They are the one place a world's ground goes under its band's floor
        // (biome-relief.mjs bandDepth): "twisting cuts 10-20 kubes deep" and "deep round water holes with sheer sides".
        //   * SLOT CANYONS follow the zero lines of a warped field, about two columns wide, opening into small basins where a
        //     slower field runs high; ten to twenty kubes deep on a world of Ø80 and up, less in proportion on smaller ones.
        //     A canyon climbs out at its ends in single steps (each column at most one under its shallowest cut neighbour, a
        //     tip at one, the ground past the biome's edge at none), so whoever walks in can walk out.
        //   * CENOTES are round holes (biome-relief.mjs holeField) four to nine kubes deep, their water a cell under the
        //     rim (a lake at that level: makeSea's lakes), on limestone flats.
        // Never into a river or a lake, and never deeper than 55% of R, well over the caves' floor.
        if (v12 && bioTab && (SLOTS_B >= 0 || CENOTE_B >= 0)) {
            const SL = SLOTS_B + 1, CE = CENOTE_B + 1, cr = makeRng((addr || '') + '#cuts12');
            const cs = [cr() * 1000, cr() * 1000, cr() * 1000, cr() * 1000, cr() * 1000, cr() * 1000, Math.floor(cr() * 0x7fffffff)];
            const floorMin = Math.ceil(R * 0.55), scale = R >= 40 ? 1 : R / 40, F = 2.6, th0 = 1.2 * F / refR;
            const cut = new Int16Array(n), list = [];
            for (let ti = 0; ti < n; ti++) {
                const code = bioTab[ti];
                if ((code !== SL && code !== CE) || chanTab[ti] || lakeTab[ti]) continue;
                const fi = (ti / WW) | 0, ax = fi >> 1, q = [0, 0, 0], iu = (ti % WW) / Wd | 0, iv = ti % Wd;
                if (code === CE && (iu < 2 || iv < 2 || iu > Wd - 3 || iv > Wd - 3)) continue;   // a lake keeps off a face's rim (makeSea): no dry pit
                q[ax] = (fi & 1) ? -refR : refR; q[(ax + 1) % 3] = ((ti % WW) / Wd | 0) - refR + 0.5; q[(ax + 2) % 3] = ti % Wd - refR + 0.5;
                const ux = q[0] * inv, uy = q[1] * inv, uz = q[2] * inv;
                let d = 0;
                if (code === SL) {
                    const wx = noise(ux * 7 + cs[3], uy * 7 + cs[4], uz * 7 + cs[5]) * 0.12;
                    const ln = noise((ux + wx) * F + cs[0], (uy + wx) * F + cs[1], (uz - wx) * F + cs[2]);
                    const bs = noise(ux * 1.3 + cs[2], uy * 1.3 + cs[0], uz * 1.3 + cs[1]), th = th0 * (1 + 2.6 * v2smooth((bs - 0.32) / 0.12));
                    if ((ln < 0 ? -ln : ln) < th) d = Math.round(scale * (10 + 10 * (0.5 + 0.5 * noise(ux * 2 + cs[5], uy * 2 + cs[3], uz * 2 + cs[4]))));
                } else if (holeField(q[0], q[1], q[2], 1.6 + R * 0.04, cs[6]) > 0) d = Math.max(4, Math.min(9, Math.round(R * 0.12) + 3));
                if (d > table[ti] - floorMin) d = table[ti] - floorMin;
                if (d > 0) { cut[ti] = d; list.push(ti); }
            }
            // The slots climb out: each no deeper than one under its shallowest cut neighbour (diagonals too), a tip (two cut
            // neighbours or fewer of eight) one deep, and the ground past the biome's edge counting as no cut at all.
            const near = (ti, out) => { for (let d = 0; d < 4; d++) out[d] = nb[4 * ti + d]; for (let d = 0; d < 2; d++) { const a = nb[4 * ti + d]; out[4 + 2 * d] = nb[4 * a + 2]; out[5 + 2 * d] = nb[4 * a + 3]; } return out; };
            const n8 = new Int32Array(8);
            for (let it = 0; it < 24; it++) {
                let moved = 0;
                for (const ti of list) {
                    if (bioTab[ti] !== SL || !cut[ti]) continue;
                    near(ti, n8);
                    let m = cut[ti], nc = 0;
                    for (let k = 0; k < 8; k++) { const a = n8[k]; if (cut[a] > 0) { nc++; if (cut[a] + 1 < m) m = cut[a] + 1; } else if (bioTab[a] !== SL && 1 < m) m = 1; }
                    if (nc <= 2 && m > 1) m = 1;
                    if (m < cut[ti]) { cut[ti] = m; moved++; }
                }
                if (!moved) break;
            }
            for (const ti of list) {
                const was = table[ti];
                table[ti] = was - cut[ti];
                if (bioTab[ti] === CE) lakeTab[ti] = was - 1;            // the cenote's water, a cell under its rim (a swimmer can climb out)
            }
            cutTab = cut;
        }
    }

    // ---- THE TOWNS (version 17, ALIEN PEOPLES: the owner's confirmed plan, Oct 13) ----
    // One rocky world in a hundred whose climate fits a group (alien-peoples.mjs peopleOf), and that nobody holds
    // (`world.free`: no claim and no public record), belongs to a people. Its towns (one to three by its size) are chosen
    // here, once the columns' heights are final: each on a face of its own, its plot and the ramp round it wholly inside
    // the face (so no town meets an edge), on dry land above any sea a world of its kind can hold, where the ground is
    // flattest. The plot is levelled to its median height and the ramp steps from that level back to the ground a cell
    // a column; the settlement biome rings it. NONE OF THIS TOUCHES THE COLUMNS' OWN HEIGHTS: the levelling and the
    // buildings are an overlay on the cells (THE TOWN OVERLAY, at the end), so the seas, the crust, the life and the turf
    // are worked out from the same ground as version 16, and a world with no people is version 16's to the bit.
    let townPeople = null, townList = null, townBiomeCode = 0, townCols = null;
    if (ver >= ALIENS_SINCE && world && world.free === true) townPeople = peopleOf(addr, world, climOf(world), ver);
    if (townPeople && ver >= TOWNS19_SINCE) {
        // ---- VERSION 19, CITY LAYOUTS (the owner's confirmed proposal, Oct 19; alien-towns.mjs) ----
        // The built town takes at most a tenth of the world's surface (cap = built; fields a twentieth more), shared out as
        // version 17's sizes were (a village; a town and a village; a city and two towns). Each settlement's SITE is the best
        // ground, over all six faces, for the better of its people's two forms (formpick = land: alien-towns.mjs formFit19
        // reads the water, shore, crest, dome, bowl, slope, spread and cliffs round it), its reach the room its share needs.
        // A site whose reach comes within 16 kubes of a city's JOINS it as another district (join = near, up to four); the
        // city's plot then holds all its districts. A plot may carry over one cube edge (edge = walk) but never round a
        // corner: CITY19.cornerOpen columns by every corner stay open land.
        const holds = !!(sea4 && (seaKept || sea4.kind === 'lava'));
        const wetC = (ti) => holds && (chanTab[ti] || lakeTab[ti]);
        let minH = -1e9;
        if (sea4 && (seaKept || sea4.kind === 'lava')) {
            if (oceanShare > 0) minH = LAND + 1;
            else { const hs = new Int16Array(6 * WW); for (let ti = 0; ti < 6 * WW; ti++) hs[ti] = heightOfColumn(ti); hs.sort();
                const cover = sea4.kind === 'lava' ? 0.42 : seaShare > 0 ? Math.min(0.5, seaShare + 0.08) : 0.3; minH = hs[Math.min(hs.length - 1, Math.floor(cover * hs.length))] + 1; }
        }
        const tr = makeRng((addr || '') + '#towns19'), surface = 6 * WW;
        const builtCap = Math.floor((ver >= TOWNS21_SINCE ? TOWN21.builtShare : CITY19.builtShare) * surface), fieldCap = Math.floor(CITY19.fieldShare * surface);
        const sz21 = ver >= TOWNS21_SINCE ? townShares21(2 * R, builtCap) : null;   // (21: fewer towns where a share would be too small for two blocks and a square)
        const sizes = sz21 ? sz21.sizes : ver >= TOWNS20_SINCE ? townSizes20(2 * R) : townSizesFor(2 * R), shares = sz21 ? sz21.shares : sizes.length === 1 ? [1] : sizes.length === 2 ? [0.65, 0.35] : [0.5, 0.25, 0.25];   // (20: no villages)
        const used = new Uint8Array(6 * WW);
        const clampT = (v) => (v < -refR ? -refR : v > refR - 1 ? refR - 1 : v);
        const colOfPlot = (fr, i, j) => { const c = fr.cell(i, j, 0), fiP = fr.part(i, j) ? fr.fiB : fr.fiA, aP = fiP >> 1;
            return fiP * WW + (clampT(c[(aP + 1) % 3]) + refR) * Wd + (clampT(c[(aP + 2) % 3]) + refR); };
        const ws = world && world.sea, liq = ws && seaKept && (ws.kind === 'liquid' || ws.kind === 'ice') && /^(Water|Water Ice|Liquid Methane)$/.test(ws.name || '') ? ws.name : null;
        const radiusOf = (T) => Math.ceil(Math.sqrt(1.6 * T / Math.PI)) + 3;
        // A SQUARE WINDOW of half-size n about face column (fi, cu, cv), over the edge it reaches past, or null where it would
        // reach past two (a corner) or into a corner's open columns
        const windowOf = (fi, cu, cv, n) => {
            const over = [cu - n < 0, cu + n > Wd, cv - n < 0, cv + n > Wd], cnt = over.filter(Boolean).length;
            if (cnt > 1) return null;
            if (cnt === 0) return { fi, cu, cv, N: n, P: R, v: ver, edge: null };
            const sd = over.indexOf(true), oc = sd < 2 ? cv : cu;
            if (oc - n < CITY19.cornerOpen || oc + n > Wd - CITY19.cornerOpen) return null;
            const qc = sd < 2 ? cu : cv, past = (sd & 1) ? qc + n - Wd : n - qc;
            if (past > Wd - CITY19.cornerOpen) return null;
            return { fi, cu, cv, N: n, P: R, v: ver, edge: { s: sd, e: (sd & 1) ? Wd - qc : -qc } };
        };
        // (21) A FOLDED WINDOW for a world whose face is too narrow for two blocks each way (TOWN21.foldBelow): half-size n
        // along the face's v axis, past the edge on side sd (2 -j, 3 +j: the plot's j runs over it), its i (the face's u)
        // held to the face, two columns clear of either side (oLim, the plot rows a town may use)
        const foldOf = (fi, cu, cv, n, sd) => {
            if (sd === 3 ? !(cv + n > Wd && cv - n >= 0) : !(cv - n < 0 && cv + n <= Wd)) return null;
            const past = sd === 3 ? cv + n - Wd : n - cv; if (past > Wd - 2) return null;
            const oLim = [Math.max(-n, 2 - cu), Math.min(n - 1, Wd - 3 - cu)]; if (oLim[1] - oLim[0] + 1 < TOWN21.foldRows) return null;
            return { fi, cu, cv, N: n, P: R, v: ver, edge: { s: sd, e: sd === 3 ? Wd - cv : -cv }, oLim };
        };
        const inW = (w, i) => !w || !w.oLim || (i >= w.oLim[0] && i <= w.oLim[1]);
        // the columns either side of a window's edge stand above the reference cube, so the edge is convex and the ground rolls over it
        const seamOK = (fr, n, w) => { const e = fr.edge; if (!e) return true; const sAx = e.s < 2 ? 0 : 1; let up = 0, all = 0; for (let o = -n; o < n; o += 2) for (const q of [e.e - 1, e.e]) { const i = sAx === 0 ? q : o, j = sAx === 0 ? o : q; if (!inW(w, i)) continue; all++; if (heightOfColumn(colOfPlot(fr, i, j)) >= refR) up++; } return up * 2 >= all; };   // (the plan raises the rest: rec.seamMin)
        const siteStats = (fr, N, w) => {
            let n = 0, wN = 0, sh = 0, sum = 0, sq = 0, low = 0, cl = 0, stp = 0, cIn = 0, nIn = 0, cOut = 0, nOut = 0, xp = 0, xn = 0, yp = 0, yn = 0, bandI = 0, nbI = 0, bandJ = 0, nbJ = 0, overlap = 0;
            const st = Math.max(1, N >> 3);
            for (let i = -N + 1; i < N - 1; i += st) for (let j = -N + 1; j < N - 1; j += st) {
                if (!inW(w, i)) continue;
                const ti = colOfPlot(fr, i, j); if (used[ti]) overlap++;
                const h = heightOfColumn(ti) - R, wt = wetC(ti);
                n++; if (wt) { wN++; continue; } if (h + R < minH) low++;
                sum += h; sq += h * h;
                const t2 = colOfPlot(fr, i + 1, j), t3 = colOfPlot(fr, i, j + 1), dh = Math.max(Math.abs(heightOfColumn(t2) - h - R), Math.abs(heightOfColumn(t3) - h - R));
                if (dh >= 3) cl++; else if (dh === 2) stp++;
                if (wetC(t2) || wetC(t3)) sh++;
                const r = Math.max(Math.abs(i), Math.abs(j));
                if (r < N / 3) { cIn += h; nIn++; } else if (r > (2 * N) / 3) { cOut += h; nOut++; }
                if (i > 0) xp += h; else xn += h; if (j > 0) yp += h; else yn += h;
                if (Math.abs(i) < N / 4) { bandI += h; nbI++; } if (Math.abs(j) < N / 4) { bandJ += h; nbJ++; }
            }
            const land = Math.max(1, n - wN), mean = sum / land, v = sq / land - mean * mean, inM = nIn ? cIn / nIn : mean, outM = nOut ? cOut / nOut : mean;
            return { water: wN / Math.max(1, n), shore: sh / land, spread: 2.5 * Math.sqrt(v > 0 ? v : 0), dome: inM - outM, bowl: outM - inM, cliff: cl / land, steep: stp / land,
                     slope: (Math.abs(xp - xn) + Math.abs(yp - yn)) / Math.max(1, land / 2) / Math.max(1, N), crest: Math.max(nbI ? bandI / nbI - mean : 0, nbJ ? bandJ / nbJ - mean : 0),
                     trough: Math.max(nbI ? mean - bandI / nbI : 0, nbJ ? mean - bandJ / nbJ : 0), low: low / land, overlap: overlap / Math.max(1, n), mean };
        };
        const cities = [];
        for (let s = 0; s < sizes.length; s++) {
            const size = sizes[s], T = Math.max(60, Math.floor(builtCap * shares[s])), Fd = Math.floor(fieldCap * shares[s]);
            let r = Math.min(Math.floor((Wd - 4) / 2), radiusOf(T)), best = null;
            const step = Math.max(2, Math.round(Wd / 10));
            // (21: no site for the plot, a smaller one, down to the 18 that still holds the core square and a block each way)
            const fold = ver >= TOWNS21_SINCE && Wd < TOWN21.foldBelow;   // (21: a narrow face's town folds over an edge)
            if (fold) r = Math.min(TOWN21.foldHalf, Wd - 1);
            for (const r21 = r; !best && r >= (ver >= TOWNS21_SINCE ? (fold ? Math.min(r21, 20) : Math.min(r21, 18)) : r21); r--) for (let fi = 0; fi < 6; fi++) for (let cu = 1 + (step >> 1); cu < Wd - 1; cu += step) for (let cv = 1 + (step >> 1); cv < Wd - 1; cv += (fold ? 1 : step)) for (const sd of (fold ? [2, 3] : [-1])) {
                const w0 = fold ? foldOf(fi, cu, cv, r, sd) : windowOf(fi, cu, cv, r); if (!w0) continue;
                const fr = townFrame19(w0, refR), core = colOfPlot(fr, 0, 0);
                if (wetC(core) || heightOfColumn(core) < minH) continue;
                if (w0.edge && !seamOK(fr, r, w0)) continue;   // (over an edge only where the ground stands clear of the reference cube)
                const stt = siteStats(fr, r, w0);
                if (stt.overlap > 0.02) continue;
                const pf = (ver >= TOWNS20_SINCE ? pickForm20 : pickForm19)(townPeople.id, stt);
                const score = (stt.low > 0 ? 300 + stt.low * 4000 : 0) + stt.spread * 40 + (pf.form !== 'cliff' ? stt.cliff * 300 : 0) - pf.fit * 260 + stt.water * (pf.form === 'harbour' || pf.form === 'valley' ? 0 : 400) + (w0.edge ? -30 : 0) + tr() * 20;
                if (!best || score < best.score) best = { score, w0, fr, form: pf.form, stt };
            }
            if (!best) continue;
            r = best.w0.N;
            // mark the site's room taken (no other site's reach overlaps it)
            for (let i = -r; i < r; i++) if (inW(best.w0, i)) for (let j = -r; j < r; j++) used[colOfPlot(best.fr, i, j)] = 1;
            const at = best.fr.cell(0, 0, 0), site = { size, T, Fd, r, at: [at[0] + 0.5, at[1] + 0.5, at[2] + 0.5], fi: best.w0.fi, cu: best.w0.cu, cv: best.w0.cv, form: best.form, ...(best.w0.oLim ? { w0: best.w0 } : {}) };
            // JOIN a city whose reach comes within 16 kubes (and whose plot can take this district: one edge, no corner)
            let joined = false;
            for (const C of cities) {
                if (C.sites.length >= CITY19.maxDistricts || site.w0 || C.sites[0].w0) continue;   // (21: a folded town joins none)
                const S0 = C.sites[0], d = dist19(site.at, S0.at);
                if (!C.sites.some((S1) => dist19(site.at, S1.at) <= S1.r + r + CITY19.joinKubes)) continue;
                const fr0 = townFrame19(windowOf(S0.fi, S0.cu, S0.cv, Math.min(Wd, d + r + 4)) || { fi: S0.fi, cu: S0.cu, cv: S0.cv, N: 1, P: R, v: ver, edge: null }, refR);
                const loc = fr0.local(site.at), need = Math.max(C.N, Math.ceil(Math.max(Math.abs(loc[0]), Math.abs(loc[1]))) + Math.ceil(r * 0.6));
                if (need > 56) continue;   // (a plot at most 112 kubes across)
                const w1 = windowOf(S0.fi, S0.cu, S0.cv, need); if (!w1) continue;
                const fr1 = townFrame19(w1, refR); if (!fr1.toPlan(Math.floor(site.at[0]), Math.floor(site.at[1]), Math.floor(site.at[2]))) continue;
                C.sites.push(site); C.N = need; joined = true; break;
            }
            if (!joined) cities.push({ sites: [site], N: r });
        }
        townList = [];
        for (const C of cities) {
            const S0 = C.sites[0], w = S0.w0 || windowOf(S0.fi, S0.cu, S0.cv, Math.max(8, C.N)) || windowOf(S0.fi, S0.cu, S0.cv, S0.r);
            if (!w) continue;
            const fr1 = townFrame19(w, refR), N = w.N;
            const P = Math.max(heightOfColumn(colOfPlot(fr1, 0, 0)), minH);
            const W2 = 2 * N, rel = new Int16Array(W2 * W2), wetc = new Uint8Array(W2 * W2);
            for (let i = -N; i < N; i++) for (let j = -N; j < N; j++) { const ti = colOfPlot(fr1, i, j), q = (i + N) * W2 + (j + N); rel[q] = heightOfColumn(ti) - P; wetc[q] = wetC(ti) ? 1 : 0; }
            const cores = C.sites.map((st2, n2) => { if (!n2) return [0, 0]; const l = fr1.local(st2.at); return [Math.round(l[0] - 0.5), Math.round(l[1] - 0.5)]; });
            // a city's second district: its plot's best flat dry ground at least districtApart from its cores
            if (S0.size === 'city' && cores.length < CITY19.maxDistricts) { let b2 = null; const lim = N - 7;
                for (let i = -lim; i < lim; i += 2) for (let j = -lim; j < lim; j += 2) { if (cores.some((c) => Math.abs(c[0] - i) + Math.abs(c[1] - j) < CITY19.districtApart)) continue; const q = (i + N) * W2 + (j + N); if (wetc[q] || Math.abs(i) + Math.abs(j) > S0.r + 6) continue;
                    let sd2 = 0; for (const [a, b] of [[1, 0], [0, 1], [-1, 0], [0, -1]]) sd2 += Math.abs(rel[(i + a + N) * W2 + (j + b + N)] - rel[q]); const sc = sd2 + Math.abs(rel[q]) * 0.3 + tr() * 0.5; if (!b2 || sc < b2[2]) b2 = [i, j, sc]; }
                if (b2) cores.push([b2[0], b2[1]]); }
            const T = C.sites.reduce((a, x) => a + x.T, 0), Fd = C.sites.reduce((a, x) => a + x.Fd, 0);
            townList.push(Object.freeze({ k: townList.length, size: S0.size, N, M: 0, fi: w.fi, cu: w.cu, cv: w.cv, P, seed: (addr || '') + '#town' + townList.length, v: ver, terr: encodeTerr(rel, wetc), liq,
                                          form: S0.form, cores, edge: w.edge ? { ...w.edge } : null, target: T, fields: Fd, ...(w.edge ? { seamMin: refR + 1 - P } : {}), ...(w.oLim ? { oLim: w.oLim.slice() } : {}) }));
        }
        // A TOWN'S OWN GROUND grows nothing of the world's (its built columns, and any its buildings hang over): the land
        // round it, inside its plot or out, keeps its own life
        townCols = new Uint8Array(6 * WW);
        for (const t of townList) { const pl = planFor(townPeople.id, t), fr = townFrame19(t, refR), N = t.N, W2 = 2 * N, HRp = pl.hhi - pl.hlo;
            for (let i = -N; i < N; i++) for (let j = -N; j < N; j++) { const q = (i + N) * W2 + (j + N); let mine = !pl.natural[q];
                if (!mine) for (let h = pl.g[q] + 1; h < pl.hhi && !mine; h++) if (pl.kcell[q * HRp + (h - pl.hlo)]) mine = true;
                if (mine) townCols[colOfPlot(fr, i, j)] = 1; } }
        townBiomeCode = 0;
    } else if (townPeople) {
        // A plot never takes in water (a river's channel, a lake's basin); a slot canyon's or a cenote's cut, a pool or a
        // spire it may (the overlay fills or levels it), at a cost in the score.
        const holds = !!(sea4 && (seaKept || sea4.kind === 'lava'));   // (a dry world's channels and basins hold nothing)
        const wet = (ti) => holds && (chanTab[ti] || lakeTab[ti]);
        const rough = (ti) => (cutTab && cutTab[ti] > 0) || (bioTab && (bioTab[ti] > BIOMES.length || POOL_OWN.has(bioTab[ti])));
        const okCol = (ti) => !wet(ti) && !rough(ti);   // (the settlement biome keeps off both)
        // The lowest a plot may stand: over the coast on an ocean world (an ocean's floor is shaped under LAND), and over
        // a little more than the share a world's lakes, ice or lava lakes can cover (the sea fills the lowest ground first).
        let minH = -1e9;
        if (sea4 && (seaKept || sea4.kind === 'lava')) {
            if (oceanShare > 0) minH = LAND + 1;
            else {
                const hs = new Int16Array(6 * WW);
                for (let ti = 0; ti < 6 * WW; ti++) hs[ti] = heightOfColumn(ti);
                hs.sort();
                const cover = sea4.kind === 'lava' ? 0.42 : seaShare > 0 ? Math.min(0.5, seaShare + 0.08) : 0.3;
                minH = hs[Math.min(hs.length - 1, Math.floor(cover * hs.length))] + 1;
            }
        }
        const tr = makeRng((addr || '') + '#towns17'), TM = marginFor(Wd);   // the ramp's width
        const used = new Set();
        townList = [];
        let k = 0;
        // A candidate's ground: how much of it is low or wet (none, at best), its heights' spread (two and a half standard
        // deviations, about the 10th to the 90th percentile), the columns a cut or a pool takes, and how far from its face's
        // centre it is. Read off summed-area tables a face (one pass over its columns), so a candidate costs four reads.
        const SW = Wd + 1, satB = new Float64Array(6 * SW * SW), satR = new Float64Array(6 * SW * SW), satH = new Float64Array(6 * SW * SW), satQ = new Float64Array(6 * SW * SW);
        for (let fi = 0; fi < 6; fi++) for (let iu = 0; iu < Wd; iu++) for (let iv = 0; iv < Wd; iv++) {
            const ti = fi * WW + iu * Wd + iv, h = heightOfColumn(ti) - R, o = fi * SW * SW, a = o + (iu + 1) * SW + (iv + 1), up = a - SW, lf = a - 1, ul = a - SW - 1;
            const b = (h + R < minH || wet(ti)) ? 1 : 0, r2 = !b && rough(ti) ? 1 : 0;
            satB[a] = b + satB[up] + satB[lf] - satB[ul]; satR[a] = r2 + satR[up] + satR[lf] - satR[ul];
            satH[a] = h + satH[up] + satH[lf] - satH[ul]; satQ[a] = h * h + satQ[up] + satQ[lf] - satQ[ul];
        }
        const box = (T, fi, u0, v0, u1, v1) => { const o = fi * SW * SW; return T[o + u1 * SW + v1] - T[o + u0 * SW + v1] - T[o + u1 * SW + v0] + T[o + u0 * SW + v0]; };
        for (const size of townSizesFor(2 * R)) {
            // Every plot size from the town's own down, each candidate scored; a dry, high, flat site at the full size
            // wins, a smaller one only where the full one has none, and a site over low or wet ground only where there is
            // nothing else (its plaza is then raised over the water: P, below).
            let best = null;
            const N0 = plotHalfFor(size, Wd);
            for (let N = N0; N >= 4; N -= 2) {
                const ext = N + TM, m = ext + (Wd >= 60 ? 3 : Wd >= 30 ? 1 : 0);   // (off the face's rim by a cell or three)
                if (Wd - 2 * m < 1) continue;
                for (let fi = 0; fi < 6; fi++) {
                    if (used.has(fi)) continue;
                    const step = Math.max(2, Math.round((Wd - 2 * m) / 7));
                    for (let cu = m; cu <= Wd - m; cu += step) for (let cv = m; cv <= Wd - m; cv += step) {
                        const u0 = cu - ext, v0 = cv - ext, u1 = cu + ext, v1 = cv + ext, n = (2 * ext) * (2 * ext);
                        const nb = box(satB, fi, u0, v0, u1, v1), nr = box(satR, fi, u0, v0, u1, v1);
                        const mh = box(satH, fi, u0, v0, u1, v1) / n, vh = box(satQ, fi, u0, v0, u1, v1) / n - mh * mh;
                        const spread = 2.5 * Math.sqrt(vh > 0 ? vh : 0);
                        const dc = (cu - refR) * (cu - refR) + (cv - refR) * (cv - refR);
                        const score = (nb > 0 ? 300 + nb * 4000 / n : 0) + (N0 - N) * 250 + spread * 100 + nr * 400 / n + dc / (refR * refR) * 10 + tr();
                        if (!best || score < best.score) best = { score, fi, cu, cv, N };
                    }
                }
            }
            if (!best) continue;
            used.add(best.fi);
            const hsP = [];
            for (let iu = best.cu - best.N; iu < best.cu + best.N; iu++) for (let iv = best.cv - best.N; iv < best.cv + best.N; iv++) hsP.push(heightOfColumn(best.fi * WW + iu * Wd + iv));
            hsP.sort((a, b) => a - b);
            // …over any water its plot takes in, the way a filled lake or channel would stand (a cell over its surface).
            let waterTop = -1e9;
            if (holds) for (let iu = best.cu - best.N - TM; iu < best.cu + best.N + TM; iu++) for (let iv = best.cv - best.N - TM; iv < best.cv + best.N + TM; iv++) {
                const ti = best.fi * WW + iu * Wd + iv;
                if (lakeTab[ti] > waterTop) waterTop = lakeTab[ti];
                if (chanTab[ti] && heightOfColumn(ti) + 1 > waterTop) waterTop = heightOfColumn(ti) + 1;
            }
            const P = Math.max(hsP[hsP.length >> 1], minH, waterTop + 1);
            if (ver >= TOWNS18_SINCE) {
                // VERSION 18: the town is laid out on its own ground, so its record carries that ground (alien-peoples.mjs
                // encodeTerr: each plot column's height over the plaza, and whether water stands on it) and the liquid its
                // world's water is (its canal's), for every side to lay the same town from the record alone.
                const W2 = 2 * best.N, rel = new Int16Array(W2 * W2), wetc = new Uint8Array(W2 * W2);
                for (let i = -best.N; i < best.N; i++) for (let j = -best.N; j < best.N; j++) {
                    const ti = best.fi * WW + (best.cu + i) * Wd + (best.cv + j), q = (i + best.N) * W2 + (j + best.N);
                    rel[q] = heightOfColumn(ti) - P; wetc[q] = wet(ti) ? 1 : 0;
                }
                // (Oct 13: only a sea the world's draws keep: the canal is that sea's own water, laid into it below)
                const ws = world && world.sea, liq = ws && seaKept && (ws.kind === 'liquid' || ws.kind === 'ice') && /^(Water|Water Ice|Liquid Methane)$/.test(ws.name || '') ? ws.name : null;
                townList.push(Object.freeze({ k, size, N: best.N, M: TM, fi: best.fi, cu: best.cu, cv: best.cv, P, seed: (addr || '') + '#town' + k, v: ver, terr: encodeTerr(rel, wetc), liq }));
            } else townList.push(Object.freeze({ k, size, N: best.N, M: TM, fi: best.fi, cu: best.cu, cv: best.cv, P, seed: (addr || '') + '#town' + k }));
            k++;
        }
        // (version 18) A TOWN'S OWN GROUND grows nothing of the world's: no tree or flora of a natural biome stands in its lanes
        // (with no settlement biome round a plot, its columns are the world's biomes', so life is told to keep off them)
        if (ver >= TOWNS18_SINCE) { townCols = new Uint8Array(6 * WW); for (const t of townList) for (let iu = t.cu - t.N; iu < t.cu + t.N; iu++) for (let iv = t.cv - t.N; iv < t.cv + t.N; iv++) if (iu >= 0 && iu < Wd && iv >= 0 && iv < Wd) townCols[t.fi * WW + iu * Wd + iv] = 1; }
        // THE SETTLEMENT BIOME: every column within RING_WIDTH of a plot's ramp, on its face, but a river's, a lake's or a
        // pool's (the water keeps its own).
        // (version 18: none; the owner, Oct 13: towns border right up against the natural landscape, no clearing round them)
        townBiomeCode = ver >= TOWNS18_SINCE ? 0 : BIOME_IDS.indexOf(townPeople.biome) + 1;
        if (bioTab && townBiomeCode > 0) for (const t of townList) {
            const reach = t.N + t.M + RING_WIDTH;
            for (let iu = Math.max(0, t.cu - reach); iu < Math.min(Wd, t.cu + reach); iu++)
                for (let iv = Math.max(0, t.cv - reach); iv < Math.min(Wd, t.cv + reach); iv++) {
                    const ti = t.fi * WW + iu * Wd + iv;
                    if (okCol(ti)) bioTab[ti] = townBiomeCode;
                }
        }
    }

    // ---- CAVES WHERE PHYSICS MAKES THEM (the owner's pick): karst where water has worked through a water world's
    // sediment in its lowlands, and lava tubes under a world's volcanic ground, each a tunnel of spheres as version 2's
    // worms were, from a stream of its own. Karst wanders and opens into chambers; a tube runs level, a roof under the
    // ground, in one direction. Nowhere else has any.
    const bands = Math.max(12, Math.min(48, R));
    const ocR = Math.max(1, Math.round(bands / 4) - 2) + Math.ceil(bands / 8);
    const caveMinR = Math.max(ocR + 3, Math.round(R * 0.5));
    const roof = Math.max(3, Math.round(R * 0.08));
    const capR = R - roof - 1;
    const cv = makeRng((addr || '') + '#caves4');
    const spheres = [];
    const karstWorld = !!(geoWorld && geoWorld.sea && geoWorld.sea.kind === 'liquid' && /water/i.test(geoWorld.sea.name || 'Water'));
    const sites = capR - caveMinR >= 4 ? Math.round(4 * R * R / 144) : 0;
    let nWorms = 0;
    for (let w = 0; w < sites; w++) {
        const ax = Math.floor(cv() * 3), sg = cv() < 0.5 ? -1 : 1, tu = cv() * 1.6 - 0.8, tv = cv() * 1.6 - 0.8;
        const kDepth = cv(), kLen = cv(), kChamber = cv();
        let dx = cv() - 0.5, dy = cv() - 0.5, dz = cv() - 0.5;
        const u = [0, 0, 0];
        u[ax] = sg; u[(ax + 1) % 3] = tu; u[(ax + 2) % 3] = tv;
        const tube = geo.volcanicAt(u[0], u[1], u[2]) > 0.35;
        const karst = !tube && karstWorld && geo.coverAt(u[0], u[1], u[2]) > 0.55 && geo.lowAt(u[0], u[1], u[2]) > 0.5;
        if (!tube && !karst) continue;
        const ground = band(R + geo.heightAt(u[0], u[1], u[2]) - 0.5);
        let r0 = tube ? ground - roof - 1.6 : ground - roof - 3 - kDepth * R * 0.18;
        if (r0 < caveMinR + 2.5) r0 = caveMinR + 2.5;
        // Volcanic ground stands at the world's mean level, where a tube's depth under it can reach past the ceiling a
        // cave is steered under (capR): it starts at the ceiling instead. Carving keeps a roof under every column's own
        // ground on every face (isSolid), so no cave opens at the surface either way.
        if (r0 > capR - 1) r0 = capR - 1;
        if (r0 < caveMinR + 2.5) continue;
        nWorms++;
        const p = [0, 0, 0];
        p[ax] = sg * r0; p[(ax + 1) % 3] = tu * r0; p[(ax + 2) % 3] = tv * r0;
        let rad = tube ? 1.2 + kLen * 0.4 : 1 + kDepth * 0.6;
        const steps = tube ? 10 + Math.floor(kLen * (6 + R / 2)) : 14 + Math.floor(kLen * (8 + R));
        const chamberAt = !tube && kChamber < 0.5 ? Math.floor(kChamber * 2 * steps) : -1;
        for (let i = 0; i < steps; i++) {
            const L = Math.max(Math.abs(p[0]), Math.abs(p[1]), Math.abs(p[2]));
            const pl = Math.sqrt(p[0] * p[0] + p[1] * p[1] + p[2] * p[2]) || 1;
            const nx = p[0] / pl, ny = p[1] / pl, nz = p[2] / pl;
            if (L < caveMinR + rad + 1.5) { dx += nx * 0.5; dy += ny * 0.5; dz += nz * 0.5; }
            else if (L > capR - rad - 0.5) { dx -= nx * 0.5; dy -= ny * 0.5; dz -= nz * 0.5; }
            const wander = tube ? 0.12 : 0.45;
            dx += (cv() - 0.5) * wander; dy += (cv() - 0.5) * wander; dz += (cv() - 0.5) * wander;
            const dr = dx * nx + dy * ny + dz * nz, keep = tube ? 0.95 : 0.6;   // level: most of the climb or fall taken out
            dx -= nx * dr * keep; dy -= ny * dr * keep; dz -= nz * dr * keep;
            const dl = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
            dx /= dl; dy /= dl; dz /= dl;
            p[0] += dx * 0.9; p[1] += dy * 0.9; p[2] += dz * 0.9;
            if (!tube) { rad += (cv() - 0.5) * 0.25; rad = rad < 0.9 ? 0.9 : rad > 2.2 ? 2.2 : rad; }
            spheres.push(p[0], p[1], p[2], rad * rad);
            if (i === chamberAt) { const c = 2.2 + kChamber * 2.4; spheres.push(p[0], p[1], p[2], c * c); }
        }
    }
    const CB = 8, cbOff = Math.ceil((hi + 4) / CB), nb = 2 * cbOff + 1;
    const blocks = spheres.length ? new Array(nb * nb * nb) : null;
    for (let s = 0; s < spheres.length; s += 4) {
        const rr = Math.sqrt(spheres[s + 3]);
        const x0 = Math.floor((spheres[s] - rr) / CB), x1 = Math.floor((spheres[s] + rr) / CB);
        const y0 = Math.floor((spheres[s + 1] - rr) / CB), y1 = Math.floor((spheres[s + 1] + rr) / CB);
        const z0 = Math.floor((spheres[s + 2] - rr) / CB), z1 = Math.floor((spheres[s + 2] + rr) / CB);
        for (let bx = x0; bx <= x1; bx++) for (let by = y0; by <= y1; by++) for (let bz = z0; bz <= z1; bz++) {
            const k = ((bx + cbOff) * nb + (by + cbOff)) * nb + (bz + cbOff);
            (blocks[k] || (blocks[k] = [])).push(s);
        }
    }
    function inCave(cx, cy, cz) {
        const list = blocks[((Math.floor(cx / CB) + cbOff) * nb + (Math.floor(cy / CB) + cbOff)) * nb + (Math.floor(cz / CB) + cbOff)];
        if (!list) return false;
        for (let i = 0; i < list.length; i++) {
            const s = list[i], dx = cx - spheres[s], dy = cy - spheres[s + 1], dz = cz - spheres[s + 2];
            if (dx * dx + dy * dy + dz * dz <= spheres[s + 3]) return true;
        }
        return false;
    }
    const hasCaves = spheres.length > 0;
    let solidR = hasCaves ? Math.min(lo, caveMinR - 1) : lo;   // tightened to the ground as built, below

    let colFi = 0, colU = 0, colV = 0;
    // Whether a cell's column is one THE CUTS lowered (version 12): a slot canyon's floor or a cenote's.
    function cutAt(x, y, z) {
        if (!cutTab) return false;
        const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5, ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
        return cutTab[columnOf(x, y, z, cx, cy, cz, ax, ay, az)] > 0;
    }
    function columnOf(x, y, z, cx, cy, cz, ax, ay, az) {
        let fi, u, v;
        if (ax >= ay && ax >= az) { fi = cx > 0 ? 0 : 1; u = y; v = z; }
        else if (ay >= az) { fi = cy > 0 ? 2 : 3; u = z; v = x; }
        else { fi = cz > 0 ? 4 : 5; u = x; v = y; }
        colFi = fi;
        colU = (u < -refR ? -refR : u > refR - 1 ? refR - 1 : u) + refR;
        colV = (v < -refR ? -refR : v > refR - 1 ? refR - 1 : v) + refR;
        return fi * WW + colU * Wd + colV;
    }
    function heightOfColumn(ti) {
        let t = table[ti];
        if (t === V2_NONE) t = table[ti] = sample((ti / WW) | 0, ((ti % WW) / Wd) | 0, ti % Wd);
        return t;
    }
    function isSolid(x, y, z) {
        const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5;
        const ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
        const rF = ax > ay ? (ax > az ? ax : az) : (ay > az ? ay : az), r = Math.round(rF);
        if (r > hi) return false;
        if (r <= solidR) return true;
        const t = heightOfColumn(columnOf(x, y, z, cx, cy, cz, ax, ay, az));
        if (!underGround(r, t, ax, ay, az)) return false;
        if (!hasCaves || r < caveMinR || r > t - roof) return true;
        if (!inCave(cx, cy, cz)) return true;
        // UNDER EVERY FACE'S GROUND, NOT ONLY ITS OWN. Version 3's basins run deeper, and where one reaches down near an
        // edge, the wall round it is the next face's rock: a cell of it can be `roof` under its own face's ground and a
        // cell or two under the basin's rim on the other. So a cave is carved only where it is at least `roof` under the
        // ground of every face its position leans toward (a cell deep inside a face is far below all of them).
        return !(roofed(0, cx, y, z) && roofed(1, cy, z, x) && roofed(2, cz, x, y));
    }
    // WHETHER A CELL IS UNDER ITS OWNING COLUMN'S GROUND, t, the cell at L∞ radius r with its centre's |x|, |y|, |z|. On a
    // face, as ever, r ≤ t. PAST AN EDGE (two of its coordinates beyond the reference cube) the land ROLLS OVER it: a
    // quarter circle round the reference cube's edge line, and past a corner (all three) an eighth of a sphere round the
    // corner, a cell under it when its centre is inside. Its radius is how far the column stands above the reference cube
    // (t - refR, to the top cell's outer face) and half a cell more: the most that adds no cell above the face's own
    // height beside the edge (the first columns past the reference cube keep it, so rolled ground starts without a step),
    // and enough that land at R or R - 1 keeps a whole corner cell on the diagonal, the sharp edge version 2 has there
    // and the walker rounds without a notch. So land above R carries on over an edge the way a horizon does instead of
    // being cut to a 45° ramp, and "down" is still the owning face's (its gravity).
    function underGround(r, t, ax, ay, az) {
        const ex = ax - refR, ey = ay - refR, ez = az - refR;
        let past = 0, d2 = 0;
        if (ex > 0) { past++; d2 += ex * ex; }
        if (ey > 0) { past++; d2 += ey * ey; }
        if (ez > 0) { past++; d2 += ez * ez; }
        if (past < 2) return r <= t;
        const hh = t - refR + 0.5;
        return hh > 0 && d2 <= hh * hh;
    }
    // How far a cell under the ground (underGround) lies below its surface, in cells: t - r on a face, and past an edge
    // the rolled surface's radius less the cell's distance from the edge line, less the half cell from its centre to its
    // outer face, so the top cell reads about 0 there too.
    function depthUnder(r, t, ax, ay, az) {
        const ex = ax - refR, ey = ay - refR, ez = az - refR;
        let past = 0, d2 = 0;
        if (ex > 0) { past++; d2 += ex * ex; }
        if (ey > 0) { past++; d2 += ey * ey; }
        if (ez > 0) { past++; d2 += ez * ez; }
        return past < 2 ? t - r : t - refR - Math.sqrt(d2);
    }
    // Whether a cell whose centre lies at `c` along `axis` is `roof` below the ground of that face's column over it; (u, v)
    // are its other two coordinates in that face's column order (columnOf's). A cell no further out than caveMinR is
    // below every face's ground by construction (the lowest ground, lo, stands more than a roof above caveMinR).
    function roofed(axis, c, u, v) {
        const k = Math.round(c < 0 ? -c : c);
        if (k <= caveMinR) return true;
        const cu = (u < -refR ? -refR : u > refR - 1 ? refR - 1 : u) + refR;
        const cv = (v < -refR ? -refR : v > refR - 1 ? refR - 1 : v) + refR;
        return k <= heightOfColumn((axis * 2 + (c > 0 ? 0 : 1)) * WW + cu * Wd + cv) - roof;
    }

    // ---- THE LEVELS OF THE SURFACE, read off a fixed grid of columns (version 2's), for the seas and the crust ----
    const SG = Math.min(Wd, V2_SEA_GRID);
    let seaHist = null, seaN = 0;
    function seaHistogram() {
        if (seaHist) return seaHist;
        seaHist = new Int32Array(hi - lo + 1);
        for (let fi = 0; fi < 6; fi++) for (let i = 0; i < SG; i++) for (let j = 0; j < SG; j++) {
            const iu = Math.floor((i + 0.5) * Wd / SG), iv = Math.floor((j + 0.5) * Wd / SG);
            const hc = heightOfColumn(fi * WW + iu * Wd + iv);
            seaHist[(hc < lo ? lo : hc) - lo]++;               // (a cut column, version 12, counts at the band's floor)
            seaN++;
        }
        return seaHist;
    }

    // ---- THE LOOK OF A COLUMN'S BIOME (WG-09), for the renderer ----
    // The biome a cell of the ground shows, by its biome's id, or 'lava' (a pool's top cell), 'warm' (the ground round a
    // pool) or 'crystal' (a spire, all the way up); null for a cell no biome colours: its column has none, the cell is
    // above the ground, or it is deeper than the biome's own depth (biomes.mjs look.depth), so a cliff keeps its strata.
    // THE WATER BIOMES (Sep 30, the owner): the ground under a sea, a lake or a river shows its water biome (ocean on an
    // ocean world, lake on a lake world or in a lake's basin, river in a river's channel), whatever the liquid, where no
    // land biome of the seabed (tidal gardens, methane mats) holds it. No lava river: a channel holds water only where
    // the world's liquid is not lava (the owner's rule of Sep 29).
    let lastSea = null;
    // VERSION 14 KNOWS ITS OWN SEA (DRAWN KUBE, below): the one its world record carries (world.sea, recordedWorldOf: the
    // sea the page's seaFor and both servers' recordedSeaOf ask surfaceSea for), laid in before the first look is read, so
    // its looks, and the kube each look's cells are, never wait on a caller having asked for the sea first. Before 14 the
    // water biomes appear once a caller asks (rockySurfaceFields asks, for the servers), exactly as they always have.
    let seaToPrime = ver >= DRAWN_KUBE_SINCE && world && world.sea ? world.sea : null;
    const primeSea = () => { const desc = seaToPrime; seaToPrime = null; surfaceSea(desc); };
    const W_OCEAN = BIOME_IDS.indexOf('ocean') + 1, W_LAKE = BIOME_IDS.indexOf('lake') + 1, W_RIVER = BIOME_IDS.indexOf('river') + 1;
    function waterBiomeCode(ti, t, sea) {
        const sw = sea || lastSea;
        if (!sw) return 0;
        const code = bm ? bioTab[ti] : 0;
        if (code >= 1 && code <= BIOMES.length && BIOMES[code - 1].underSea) return 0;
        if (t < sw.level) return sw.amount === 'ocean' ? W_OCEAN : W_LAKE;
        if (sw.kind === 'lava') return 0;
        if (lakeTab[ti] > t) return W_LAKE;
        if (chanTab[ti]) return W_RIVER;
        return 0;
    }
    // The living biome a cell's column is (its id), however deep the cell and whatever look the biome draws (turf, a
    // pool, none): the column's own biome map, no water. For the animals' homes (system-view.js faunaColumnBiome).
    function columnBiome(x, y, z) {
        if (!bioTab) return null;
        const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5, ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
        const ti = columnOf(x, y, z, cx, cy, cz, ax, ay, az); heightOfColumn(ti);
        const c = bioTab[ti]; return c >= 1 && c <= BIOMES.length ? BIOMES[c - 1].id : null;
    }
    function surfaceBiome(x, y, z) {
        if (seaToPrime) primeSea();                             // version 14: its own sea first (above)
        const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5;
        const ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
        const r = Math.round(ax > ay ? (ax > az ? ax : az) : (ay > az ? ay : az));
        const ti = columnOf(x, y, z, cx, cy, cz, ax, ay, az), t = heightOfColumn(ti);
        const wc = waterBiomeCode(ti, t, null), code = wc || bioTab && bioTab[ti];
        if (wc) return underGround(r, t, ax, ay, az) && depthUnder(r, t, ax, ay, az) < 1 ? BIOMES[wc - 1].id : null;
        if (!code || !underGround(r, t, ax, ay, az)) return null;
        const dz = depthUnder(r, t, ax, ay, az);                // 0 at the top cell, on a face or rolled over an edge
        if (code === BIO_CRYSTAL) return r > Math.round(band(bioFoot[ti])) ? 'crystal' : null;
        if (code === BIO_LAVA) return dz < 1 ? 'lava' : null;
        if (code === BIO_WARM) return dz < 2 ? 'warm' : null;
        if (code === BIO_SULFUR) return dz < 1 ? 'sulfurPool' : null;
        if (code === BIO_RIFT) return dz < 1 ? 'riftGlow' : null;
        if (code === BIO_PITCH) return dz < 1 ? 'pitchPool' : null;
        if (code === BIO_BRINE) return dz < 1 ? 'brinePool' : null;
        if (code === BIO_MUD) return dz < 1 ? 'mudPool' : null;
        const b = BIOMES[code - 1];
        if (ver >= TURF_SINCE && BIOME_TURF[b.id]) return null;   // version 9: its ground is regolith, capped with turf (TURF, below)
        // A slot canyon's plain is buff, and its walls under it are banded red and buff, two cells to a band, the same at
        // the same depth everywhere.
        if (code === SLOTS_B + 1) return dz < 1 ? 'slotBand' : dz < b.look.depth ? (((r + strataPh) >> 1) & 1 ? 'slotBand' : 'slots') : null;
        return dz < b.look.depth ? b.id : null;
    }
    // How much of the surface each of the world's biomes shows, on the sea's grid of columns: [{ id, cover }] in the
    // map's order (the dominant first), each a share of all the columns. A column under a sea shows none: `seaAt(x, y,
    // z)`, when given, says whether the cell just over a column's ground is sea.
    // …AND, GIVEN seaAt, THE WATER BIOMES (Sep 30, for the world's info panel): a column under the sea counts toward its
    // ocean, lake or river (waterBiomeCode), listed after the land's biomes, the ones with any cover only.
    function biomeCover(seaAt) {
        if (!bm && !(seaAt && lastSea)) return [];
        const n = new Float64Array(BIOMES.length);
        let N = 0;
        for (let fi = 0; fi < 6; fi++) for (let i = 0; i < SG; i++) for (let j = 0; j < SG; j++) {
            const iu = Math.floor((i + 0.5) * Wd / SG), iv = Math.floor((j + 0.5) * Wd / SG), ti = fi * WW + iu * Wd + iv;
            const t = heightOfColumn(ti), code = bm ? bioTab[ti] : 0;
            N++;
            if (seaAt) {
                const ax = fi >> 1, q = [0, 0, 0];
                q[ax] = (fi & 1) ? -t - 1 : t; q[(ax + 1) % 3] = iu - refR; q[(ax + 2) % 3] = iv - refR;
                if (seaAt(q[0], q[1], q[2])) {
                    if (!(code && code <= BIOMES.length && BIOMES[code - 1].underSea)) { const wc = waterBiomeCode(ti, t, null); if (wc) n[wc - 1]++; continue; }
                }
            }
            if (!code) continue;
            n[code === BIO_LAVA || code === BIO_WARM ? 3 : code === BIO_CRYSTAL ? 4 : POOL_OWN.has(code) ? POOL_OWN.get(code) : code - 1]++;
        }
        // A land biome under SPECK is left off the list: SPECKS ARE LEFT OFF takes nearly all of them off the map before
        // the world is built, but a lava sea, settled only after it, can squeeze one onto a few columns of its islands.
        const landIds = bm && townBiomeCode > 0 ? bm.ids.concat([BIOME_IDS[townBiomeCode - 1]]) : bm ? bm.ids : [];   // (version 17: a people's settlement biome)
        const land = landIds.map((id) => ({ id, cover: n[BIOME_IDS.indexOf(id)] / N })).filter((c) => c.cover >= SPECK || (townBiomeCode > 0 && c.id === BIOME_IDS[townBiomeCode - 1] && c.cover > 0));
        const water = seaAt ? [W_OCEAN, W_LAKE, W_RIVER].filter((c) => n[c - 1] > 0).map((c) => ({ id: BIOMES[c - 1].id, cover: n[c - 1] / N })) : [];
        return land.concat(water);
    }

    // ---- WHAT GROWS (WG-10): the organics (organics.mjs) and the flora (flora.mjs) of the world's living biomes ----
    // Both read the ground through the same questions (its columns' biomes and heights, the climate there, what is solid)
    // and the sea it holds, which is decided after the terrain: `sea` is surfaceSea's answer, or null. Only a liquid sea
    // is water to grow in; an ice sheet is ground nothing roots through. Each is worked out once per sea.
    function lifeCtx(sea) {
        const liquid = sea && sea.kind === 'liquid', cellOf = sea ? (x, y, z) => sea.at(x, y, z) : null;
        return {
            R, refR, HALF: 2 * R + 1, world: bioWorld || world, climate: bioClim || climOf(world), isSolid,
            biomeAt: (fi, iu, iv) => { const ti = fi * WW + iu * Wd + iv; if (townCols && townCols[ti]) return null;
                const t = heightOfColumn(ti), w = waterBiomeCode(ti, t, sea);
                const c = w || (bioTab ? bioTab[ti] : 0); return c >= 1 && c <= BIOMES.length ? BIOMES[c - 1].id : null; },
            top: (fi, iu, iv) => heightOfColumn(fi * WW + iu * Wd + iv),
            tempAt: (fi, iu, iv) => { const q = columnDir(fi, iu, iv, refR); return fieldAt(bioClim, q[0], q[1], q[2]).tempC; },
            dir: (fi, iu, iv) => columnDir(fi, iu, iv, refR),
            seaAt: liquid ? (x, y, z) => cellOf(x, y, z) === SEA_CELL.LIQUID : null,
            blockedAt: sea && !liquid ? (x, y, z) => cellOf(x, y, z) !== SEA_CELL.DRY : null,
            seaLevel: liquid ? sea.level : null,
            caveRoofs,
            crystalBits: true,                              // a crystal field's crystals are bits in version 4
            atlas4: true,                                   // the atlas's later biomes, and their parents' core kinds
            openSea: ver >= 6,                              // version 6: an ocean's leaves float on open water (flora.mjs OCEAN_OPEN)
            trees7: ver >= 7,                               // version 7: the owner's tree forms (organics.mjs BIOME_ORGANICS v7)
            edgeUp: ver >= 10,                              // version 10: what grows is planned on its up side (organics.mjs planUpSide, flora.mjs)
            v12,                                            // version 12: Biome Wave 6's growers, and woods by the world's average
            treeT: v12 ? (+(bioWorld || world).tempC || 0) : null,   // …that average, the one temperature its trees read
            shoreReach: ver >= ONE_TEMP_SINCE,              // version 13: what floats grows only near a shore (flora.mjs FLOAT_SHORE_REACH)
            turfAt: ver >= TUFT_TEXTURE_SINCE ? (x, y, z) => (turf ? turf.at(x, y, z) : null) : null,   // version 15: no grass tuft on turf (flora.mjs)
            lifePigments: ver >= LIFE_PIGMENTS_SINCE,       // version 16: life of the six pigments nothing grew (organics.mjs pigmentsOf)
            bark: ver >= BARK_SINCE,                        // version 23: each tree form's bark by its face's light, not its leaves (organics.mjs barkOf)
            noRime: ver >= NO_RIME_SINCE,                   // version 24: a conifer's rime rings are its own leaves (organics.mjs NO_RIME_SINCE)
        };
    }
    // Air cells under rock in the caves, one near the top of every third of their spheres: where a vine can hang.
    function caveRoofs() {
        const out = [];
        for (let s = 0; s < spheres.length; s += 12) {
            const x = spheres[s], y = spheres[s + 1], z = spheres[s + 2], rad = Math.sqrt(spheres[s + 3]);
            const ax = Math.abs(x) >= Math.abs(y) && Math.abs(x) >= Math.abs(z) ? 0 : Math.abs(y) >= Math.abs(z) ? 1 : 2, p = [x, y, z], sg = p[ax] < 0 ? -1 : 1;
            const c = [Math.floor(x), Math.floor(y), Math.floor(z)];
            c[ax] = Math.floor(p[ax] + sg * (rad - 0.6));
            const o = c.slice(); o[ax] += sg;
            if (!isSolid(c[0], c[1], c[2]) && isSolid(o[0], o[1], o[2])) out.push([c[0], c[1], c[2], ax * 2 + (sg < 0 ? 1 : 0)]);
        }
        return out;
    }
    const lifeMemo = new Map();
    function life(kind, sea) {
        if (!bm && !sea) return null;                        // (a sea brings its water biome's life with it)
        const key = kind + (sea ? ' ' + sea.kind + ' ' + sea.level : ' dry');
        if (!lifeMemo.has(key)) lifeMemo.set(key, (kind === 'organics' ? organicsOf : floraOf)(lifeCtx(sea)));
        return lifeMemo.get(key);
    }

    // ---- THE CRUST FOLLOWS THE LAND ----
    // A column's surface is one of the two crusts by how high it stands: the lowlands and basins (about
    // lowlandShare of the surface, 70% at the default, measured on the grid above) wear one and the highlands the other, with a slow
    // field nudging the line by a few levels so it is a coastline of patches rather than one contour. Under the surface
    // the ground is that same crust, except where a cliff exposes it: a cell with lower ground beside it (a mesa's side,
    // a crater's wall, a canyon's) shows the two crusts in strata a few cells thick, the same everywhere at the same
    // depth, as rock is laid down. So a plain is one colour, a range another, and a cliff is banded.
    //
    // THE CRUST THAT COVERS MOST OF THE SURFACE SHELL STAYS VERSION 2'S. A world's albedo, and so its temperature, its
    // air and the kind of sea it can hold, is worked out from whichever crust shows most on a fixed grid of the surface
    // shell (crustDominant in system-view.js, dominantCrustIndex in functions/share.js). The two crusts' roles are
    // swapped where needed so that the answer on that grid is version 2's, and a world is as warm at version 3 as it was.
    const strataT = G.strataThickness > 0 ? G.strataThickness : kStrataT < 0.5 ? 2 : 3, strataPh = Math.floor(kStrata * strataT);
    const cls = new Int8Array(6 * WW).fill(-1);            // per column: -1 not asked yet, 0 lowland, 1 highland
    const lowNb = new Int16Array(6 * WW).fill(V2_NONE);    // per column: the lowest ground of it and its four neighbours
    let thrH = 0, swap = -1;
    // A column's class reads the ground round it, not the column alone (the mean height over a square about a sixteenth
    // of R across each way): one column's bump or dip does not flip it, so the line between the crusts follows the lie
    // of the land instead of every terrace's edge, which on a small world is most of it. The square runs on over an
    // edge onto the next face's columns (colAcross), so the two columns either side of a seam read nearly the same
    // ground and wear the same crust as often as two neighbours on one face do.
    const clsReach = Math.max(1, Math.round(R / 16 * G.patchScale));
    // The column whose centre is (tu, tv) on face fi's plane, running on over an edge onto the face that tangent reaches
    // (the same fold as sample()'s mirror: past the edge by o, it is o in from the edge on the next face); -1 round a
    // corner, where two tangents are past it at once.
    function colAcross(fi, tu, tv) {
        const ax = fi >> 1, sa = (fi & 1) ? -1 : 1, au = tu < 0 ? -tu : tu, av = tv < 0 ? -tv : tv;
        if (au < refR && av < refR) return fi * WW + (tu + refR - 0.5) * Wd + (tv + refR - 0.5);
        if (au >= refR && av >= refR) return -1;
        const p = [0, 0, 0];
        p[ax] = sa * refR; p[(ax + 1) % 3] = tu; p[(ax + 2) % 3] = tv;
        const k = au >= refR ? (ax + 1) % 3 : (ax + 2) % 3, t = au >= refR ? tu : tv, o = (t < 0 ? -t : t) - refR;
        p[k] = t < 0 ? -refR : refR; p[ax] = sa * (refR - o);
        const nf = k * 2 + (t < 0 ? 1 : 0);
        return nf * WW + (p[(k + 1) % 3] + refR - 0.5) * Wd + (p[(k + 2) % 3] + refR - 0.5);
    }
    function classOf(ti) {
        let k = cls[ti];
        if (k < 0) {
            const fi = (ti / WW) | 0, ax = fi >> 1, s = (fi & 1) ? -1 : 1, iu = ((ti % WW) / Wd) | 0, iv = ti % Wd;
            const pu = iu - refR + 0.5, pv = iv - refR + 0.5, p = [0, 0, 0];
            p[ax] = s; p[(ax + 1) % 3] = pu * inv; p[(ax + 2) % 3] = pv * inv;
            // LOOSE GROUND OR BARE ROCK, as the simulation left it (geology.mjs cover): sediment, soil or regolith one
            // crust, rock the other, the line between them broken a little by a slow field so it is not a contour.
            const pn = noise(p[0] * 1.6 + OP[0], p[1] * 1.6 + OP[1], p[2] * 1.6 + OP[2]);
            k = cls[ti] = geo.coverAt(p[0], p[1], p[2]) + pn * 0.12 > 0.5 ? 1 : 0;
        }
        return k;
    }
    function lowOf(ti) {
        let m = lowNb[ti];
        if (m === V2_NONE) {
            const base = ((ti / WW) | 0) * WW, iu = ((ti % WW) / Wd) | 0, iv = ti % Wd;
            m = heightOfColumn(ti);
            if (iu > 0) { const h = heightOfColumn(base + (iu - 1) * Wd + iv); if (h < m) m = h; }
            if (iu < Wd - 1) { const h = heightOfColumn(base + (iu + 1) * Wd + iv); if (h < m) m = h; }
            if (iv > 0) { const h = heightOfColumn(base + iu * Wd + iv - 1); if (h < m) m = h; }
            if (iv < Wd - 1) { const h = heightOfColumn(base + iu * Wd + iv + 1); if (h < m) m = h; }
            lowNb[ti] = m;
        }
        return m;
    }
    // A cell's crust: its column's, or on a cliff (below its column's top, above its lowest neighbour's) the strata. Land
    // rolled over an edge (underGround) is a column's ground, not a cliff, though its top cells stand below the column's
    // height: they wear the column's crust as the face does, and only what lies a cell or more under the rolled surface
    // shows strata. (On a face the depth under the surface is t - r, so there it is the rule it always was.)
    function rawMat(x, y, z) {
        const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5;
        const ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
        const r = Math.round(ax > ay ? (ax > az ? ax : az) : (ay > az ? ay : az));
        const ti = columnOf(x, y, z, cx, cy, cz, ax, ay, az), t = heightOfColumn(ti);
        if (G.strata && r < t && r > lowOf(ti) && depthUnder(r, t, ax, ay, az) >= 1) return Math.floor((r + strataPh) / strataT) & 1;   // a cliff: strata
        return classOf(ti);
    }
    function settleCrust() {
        // Version 2's majority on crustDominant's grid, and this field's, the line moved up a level while they tie
        // (a tie reads as crust 0 however the roles are dealt).
        let v2m1 = 0, np = 0;
        const probe = (fn) => {
            let m1 = 0;
            for (let f = 0; f < 6; f++) {
                const ax = f >> 1, sg = (f & 1) ? R : -R;
                for (let i = 0; i < V3_PROBE_N; i++) for (let j = 0; j < V3_PROBE_N; j++) {
                    const q = [0, 0, 0];
                    q[ax] = sg;
                    q[(ax + 1) % 3] = Math.round(-R + 2 * R * (i + 0.5) / V3_PROBE_N);
                    q[(ax + 2) % 3] = Math.round(-R + 2 * R * (j + 0.5) / V3_PROBE_N);
                    if (fn(q[0], q[1], q[2]) === 1) m1++;
                }
            }
            return m1;
        };
        np = 6 * V3_PROBE_N * V3_PROBE_N;
        v2m1 = probe((x, y, z) => voxSurfMat(noise, d.off, d.surfThresh, x, y, z));
        let m1 = 0;
        cls.fill(-1);
        m1 = probe(rawMat);
        swap = ((v2m1 * 2 > np) === (m1 * 2 > np)) ? 0 : 1;
    }
    // With crustFollowsLand off, the crust is version 1's (and 2's) noise field, the patchwork the land replaced.
    const surfMat = G.crustFollowsLand ? (x, y, z) => {
        if (swap < 0) settleCrust();
        return rawMat(x, y, z) ^ swap;
    } : (x, y, z) => voxSurfMat(noise, d.off, d.surfThresh, x, y, z);

    // ---- SEAS: version 2's stream, levels and lineae, with version 3's amounts (VERSION 3 SEAS, below) ----
    const sr = makeRng((addr || '') + '#seas');
    const kSeaAmount = v3Boost(sr(), G.seaBoost), kSeaCover = sr(), kLineae = sr();
    const LIN = [];
    for (let i = 0; i < V2_LINEAE_MAX; i++) {
        const dx = sr() * 2 - 1, dy = sr() * 2 - 1, dz = sr() * 2 - 1, dl = Math.sqrt(dx * dx + dy * dy + dz * dz) || 1;
        LIN.push(dx / dl, dy / dl, dz / dl, (sr() * 2 - 1) * 0.6, sr() * 10000, sr() * 10000, sr() * 10000);
    }
    const nLin = G.crackFamilies > 0 ? G.crackFamilies : 3 + Math.floor(kLineae * (V2_LINEAE_MAX - 2));
    let linSign = null;
    const fl = 1.5 * inv;
    function linSigns(fi, iu, iv) {
        const ti = fi * WW + iu * Wd + iv;
        let s = linSign[ti];
        if (s) return s;
        const ax = fi >> 1, p = [0, 0, 0];
        p[ax] = (fi & 1) ? -refR : refR; p[(ax + 1) % 3] = iu - refR + 0.5; p[(ax + 2) % 3] = iv - refR + 0.5;
        s = 0x80;
        for (let k = 0; k < nLin; k++) {
            const o = k * 7;
            const f0 = (p[0] * LIN[o] + p[1] * LIN[o + 1] + p[2] * LIN[o + 2]) * inv - LIN[o + 3];
            const f = (f0 > V2_LINEA_REACH || f0 < -V2_LINEA_REACH) ? f0
                    : f0 + noise((p[0] + LIN[o + 4]) * fl, (p[1] + LIN[o + 5]) * fl, (p[2] + LIN[o + 6]) * fl) * V2_LINEA_WARP;
            if (f > 0) s |= 1 << k;
        }
        return (linSign[ti] = s);
    }
    let crackTab = null;
    function cracked(fi, iu, iv) {
        const ti = fi * WW + iu * Wd + iv;
        if (crackTab[ti]) return crackTab[ti] === 2;
        const s = linSigns(fi, iu, iv) & 0x7f;
        let gone = 0;
        if (s) for (let du = -1; du <= 1; du++) for (let dv = -1; dv <= 1; dv++) {
            const nu = iu + du, nv = iv + dv;
            if ((du || dv) && nu >= 0 && nu < Wd && nv >= 0 && nv < Wd) gone |= s & ~linSigns(fi, nu, nv);
        }
        crackTab[ti] = (gone & 0x7f) !== 0 ? 2 : 1;
        return crackTab[ti] === 2;
    }

    const seas = new Map();
    function surfaceSea(desc) {
        const kind = desc && desc.kind;
        if (kind !== 'liquid' && kind !== 'ice' && kind !== 'lava') return null;
        const T = kind === 'lava' ? v3LavaTempOf(desc) : 0;
        const key = kind === 'lava' ? 'lava ' + T : kind;
        if (seas.has(key)) return seas.get(key);
        let ocean, want;
        if (kind === 'lava') {
            ocean = T >= G.lavaOceanFrom;
            want = v3LavaCover(T, G) + (kSeaCover - 0.5) * 0.06;
        } else {
            const [dryBelow, lakesBelow] = V3_SEA_AMOUNT[kind];
            if (kSeaAmount < dryBelow) { seas.set(key, null); return null; }
            ocean = kSeaAmount >= lakesBelow;
            want = ocean ? G.oceanCoverMin + (G.oceanCoverMax - G.oceanCoverMin) * kSeaCover : (0.03 + 0.12 * kSeaCover) * G.lakeScale;
        }
        const h = seaHistogram();
        let S = lo, below = 0;
        while (S <= hi && below < want * seaN) { below += h[S - lo]; S++; }
        // No sea stands over R (the surface band): at most level R, which wets every column under the band's top.
        if (S > hi) { below -= h[hi - lo]; S = hi; }
        if (S > lo) {
            const under = below - h[S - 1 - lo];
            if (kind === 'lava' || ocean) {
                // The level nearer the amount asked for by ratio (as the lakes' rule reads it: the one whose cover times
                // the other's is on its side of want squared), and never one that floods more than oceanCap (an ocean
                // of liquid or ice) or lavaLakeCap (lava lakes) while the level below holds any: a whole level of a
                // flat world can be a third of it.
                const cap = below > (kind !== 'lava' ? G.oceanCap : ocean ? 1 : G.lavaLakeCap) * seaN, w2 = want * seaN * want * seaN;
                // Version 4's lava plains are flat, so a whole level of them can pass the lakes' cap while the level below
                // holds a puddle (a Ø24 world went from 30% lava to 5%). Lava lakes step down past the cap only when the
                // level below still holds half of what was asked for, or when this one floods more than half the world.
                const lakesCapOnly = kind === 'lava' && !ocean && cap && under * 2 < want * seaN && below <= 0.5 * seaN;
                if (under > 0 && ((cap && !lakesCapOnly) || under * below > w2)) { S--; below = under; }
            } else {
                // Lakes: version 2's rule, with a little more room. Version 3's plains are broad and flat, so on a small
                // world one level can be the difference between a few puddles and a quarter of the surface, and by version
                // 2's rule the frozen lake worlds the review liked came out nearly dry. So the level below is taken only
                // when it holds at least half of what was asked for (and is nearer it by ratio), or when this one floods
                // more than the genome's lakeCap (a third, at the default).
                const w2 = want * seaN * want * seaN;
                if (under > 0 && ((under * 2 >= want * seaN && under * below > w2) || below > G.lakeCap * seaN)) { S--; below = under; }
                else if (below > G.lakeCap * seaN) below = 0;
            }
        }
        const layer = below > 0 ? makeSea(kind, desc.name || null, ocean, S, below / seaN, T) : null;
        seas.set(key, layer);
        if (layer) lastSea = layer;                          // the water biomes read it (waterBiomeCode)
        return layer;
    }
    function makeSea(kind, name, ocean, S, cover, T) {
        const ice = kind === 'ice', ridges = ice && ocean && G.iceRidges;
        // ROUND 3'S NEW IDEAS in the sea: crevasses and ice cliffs in the ice.
        const crev = ice && !!cvSeed, wallH = crev ? G.iceWallHeight : 0;
        if (ice && !linSign) { linSign = new Uint8Array(6 * WW); crackTab = new Uint8Array(6 * WW); }
        // PRESSURE RIDGES AND HUMMOCKS on an ice sheet (not on frozen lakes, which the review liked as they were): the
        // columns either side of a crack stand a level proud, broken into lengths, which is Europa's double ridge, and
        // a scattering of single hummocks a few columns further out. Both are ice (SEA_CELL.ICE) one level above the
        // sheet, a step the walker takes, and both are pure functions of their column, kept per column once known.
        const raisedTab = ridges ? new Uint8Array(6 * WW) : null;   // 0 not asked yet, 1 flat, 2 raised
        function raised(fi, iu, iv) {
            const ti = fi * WW + iu * Wd + iv;
            let k = raisedTab[ti];
            if (!k) {
                k = 1;
                if (!cracked(fi, iu, iv)) {
                    let dmin = 9;
                    for (let du = -3; du <= 3 && dmin > 1; du++) for (let dv = -3; dv <= 3; dv++) {
                        const nu = iu + du, nv = iv + dv, dd = Math.max(du < 0 ? -du : du, dv < 0 ? -dv : dv);
                        if (dd === 0 || dd >= dmin || nu < 0 || nu >= Wd || nv < 0 || nv >= Wd) continue;
                        if (heightOfColumn(fi * WW + nu * Wd + nv) < S && cracked(fi, nu, nv)) dmin = dd;
                    }
                    if (dmin <= 3) {
                        const ax = fi >> 1, p = [0, 0, 0];
                        p[ax] = (fi & 1) ? -1 : 1; p[(ax + 1) % 3] = (iu - refR + 0.5) * inv; p[(ax + 2) % 3] = (iv - refR + 0.5) * inv;
                        if (dmin === 1) { if (noise(p[0] * 6 + OK[2], p[1] * 6 + OK[0], p[2] * 6 + OK[1]) > -G.ridgeRate) k = 2; }
                        else if (noise(p[0] * 17 + OK[1], p[1] * 17 + OK[2], p[2] * 17 + OK[0]) > G.hummockRarity) k = 2;
                    }
                }
                raisedTab[ti] = k;
            }
            return k === 2;
        }
        let top = ridges ? S + 1 : S;
        if (wallH > 0 && S + wallH > top) top = S + wallH;
        // STILL WATER OVER THE SEA'S LEVEL (round 2, the owner's pick: rivers that do not flow): a river's channel
        // holds a kube of the sea's own liquid (ice on a frozen world), and a closed basin a lake up to its spill
        // level. It stands where the generator puts it, as the sea does.
        // Water stays in: a lake's column keeps its water only where every neighbour stands as high (ground or water),
        // and a channel's only where its banks do (the channel's own columns step down along it, as a still river's
        // surface must). Lowered until nothing changes. A river runs on over an edge (its neighbours there are the next
        // face's columns, colAcross, and its water lies on the ground as the ground rolls over the edge: at()); a lake
        // keeps two columns off a face's edge, where the diagonal leaves no room for a level.
        const fresh = kind === 'liquid' || ice;
        const wtTab = fresh ? new Int16Array(6 * WW) : null;
        const nbCol = (fi, iu, iv, k) => {
            const nu = iu + (k === 0 ? 1 : k === 1 ? -1 : 0), nv = iv + (k === 2 ? 1 : k === 3 ? -1 : 0);
            if (nu >= 0 && nu < Wd && nv >= 0 && nv < Wd) return fi * WW + nu * Wd + nv;
            return colAcross(fi, nu - refR + 0.5, nv - refR + 0.5);   // over the edge, or -1 round a corner
        };
        if (fresh) {
            const q = [], inQ = new Uint8Array(6 * WW);
            for (let ti = 0; ti < 6 * WW; ti++) {
                const iu = ((ti % WW) / Wd) | 0, iv = ti % Wd, edge = iu < 2 || iv < 2 || iu > Wd - 3 || iv > Wd - 3;
                const g = heightOfColumn(ti), lk = edge ? 0 : lakeTab[ti];
                let wt = chanTab[ti] ? g + 1 : 0;
                if (lk > g && lk > wt) wt = lk;
                if (wt > hi) wt = hi;                                    // no water over R either
                if (wt > S && wt > g) { wtTab[ti] = wt; q.push(ti); inQ[ti] = 1; }
            }
            while (q.length) {
                const ti = q.pop(); inQ[ti] = 0;
                let wt = wtTab[ti];
                if (!wt) continue;
                const fi = (ti / WW) | 0, iu = ((ti % WW) / Wd) | 0, iv = ti % Wd, g = heightOfColumn(ti);
                const river = chanTab[ti] && wt === g + 1;
                for (let k = 0; k < 4; k++) {
                    const tn = nbCol(fi, iu, iv, k);
                    if (tn < 0) { if (!river) wt = 0; continue; }          // a lake at a corner: none
                    if (river && chanTab[tn]) continue;
                    const gn = heightOfColumn(tn), hold = wtTab[tn] > gn ? wtTab[tn] : gn;
                    if (hold < wt) wt = hold;
                }
                if (wt < wtTab[ti]) {
                    wtTab[ti] = wt > g && wt > S ? wt : 0;
                    for (let k = 0; k < 4; k++) {
                        const tn = nbCol(fi, iu, iv, k);
                        if (tn >= 0 && wtTab[tn] && !inQ[tn]) { q.push(tn); inQ[tn] = 1; }
                    }
                }
            }
            for (let ti = 0; ti < 6 * WW; ti++) if (wtTab[ti] > top) top = wtTab[ti];
        }
        if (top > hi) top = hi;                                           // ice cliffs and ridges stop at R too
        const seaLo = tMin < lo ? tMin : lo;                              // (version 12: down into THE CUTS; never under lo before)
        {
        }
        // A column's point on the unit cube, computed as the height table computes it for relief().
        const unitOf = (fi, iu, iv) => {
            const ax = fi >> 1, p = [0, 0, 0];
            p[ax] = ((fi & 1) ? -refR : refR) * inv; p[(ax + 1) % 3] = (iu - refR + 0.5) * inv; p[(ax + 2) % 3] = (iv - refR + 0.5) * inv;
            return p;
        };
        // Per column, kept once known: 0 not asked yet, 1 no, 2 yes.
        const crevTab = crev ? new Uint8Array(6 * WW) : null, cvD = Math.max(2, Math.round(4 * G.crevasseDepth));
        const cvF = 1.5 * G.crevasseDensity, cvW = 3 * inv;
        function crevasse(fi, iu, iv) {
            if (!crevTab) return false;
            const ti = fi * WW + iu * Wd + iv;
            if (!crevTab[ti]) {
                const p = unitOf(fi, iu, iv), n = noise(p[0] * cvF + cvSeed[0], p[1] * cvF + cvSeed[1], p[2] * cvF + cvSeed[2]);
                crevTab[ti] = (n < 0 ? -n : n) < cvW ? 2 : 1;
            }
            return crevTab[ti] === 2;
        }
        // THE ICE CLIFF: an ice column with land (ground at or over the level) within two columns of it on its face stands
        // wallH over the rest, so where the ice meets the land it ends in a sheer wall.
        const wallTab = wallH > 0 ? new Uint8Array(6 * WW) : null;
        function margin(fi, iu, iv) {
            const ti = fi * WW + iu * Wd + iv;
            if (!wallTab[ti]) {
                let k = 1;
                for (let du = -2; du <= 2 && k === 1; du++) for (let dv = -2; dv <= 2; dv++) {
                    const nu = iu + du, nv = iv + dv;
                    if (nu < 0 || nu >= Wd || nv < 0 || nv >= Wd) continue;
                    if (heightOfColumn(fi * WW + nu * Wd + nv) >= S) { k = 2; break; }
                }
                wallTab[ti] = k;
            }
            return wallTab[ti] === 2;
        }
        function at(x, y, z) {
            const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5;
            const ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
            const rF = ax > ay ? (ax > az ? ax : az) : (ay > az ? ay : az), r = Math.round(rF);
            if (r > top || r <= seaLo) return SEA_CELL.DRY;
            const ti = columnOf(x, y, z, cx, cy, cz, ax, ay, az);
            const g = heightOfColumn(ti);
            if (underGround(r, g, ax, ay, az)) return SEA_CELL.DRY;
            if (r > S) {
                // Nothing over the level within a cell of the diagonal where two faces meet, where the cell under it along
                // its own face's axis is the next face's (open air, or a crack of that face's) and it would overhang.
                const mid = ax > ay ? (ay > az ? ay : (ax > az ? az : ax)) : (ax > az ? ax : (ay > az ? az : ay));
                // A RIVER'S WATER lies a cell deep on its channel's ground, rolled over an edge as the ground is
                // (depthUnder reads a cell's height over the rolled surface), so it runs on to the next face.
                if (fresh && wtTab[ti] && chanTab[ti] && wtTab[ti] === g + 1) {
                    const du = depthUnder(r, g, ax, ay, az);
                    if (du >= -1) return ice ? SEA_CELL.ICE : SEA_CELL.LIQUID;
                }
                if (rF - mid < 2) return SEA_CELL.DRY;
                if (fresh && r <= wtTab[ti] && !(chanTab[ti] && wtTab[ti] === g + 1)) return ice ? SEA_CELL.ICE : SEA_CELL.LIQUID;
                if (g >= S) return SEA_CELL.DRY;
                // The cliff stands whole or not at all: the diagonal's rule read at its foot (S + 1), for every level of it.
                if (wallTab && r <= S + wallH && S + 0.5 - mid >= 2 && margin(colFi, colU, colV) && !cracked(colFi, colU, colV) && !crevasse(colFi, colU, colV))
                    return SEA_CELL.ICE;                                                                         // the ice cliff
                // A ridge on the sheet, but not over a crevasse, which leaves no sheet under it.
                return ridges && r === S + 1 && !crevasse(colFi, colU, colV) && raised(colFi, colU, colV) ? SEA_CELL.ICE : SEA_CELL.DRY;
            }
            if (!ice) return SEA_CELL.LIQUID;
            // A CREVASSE opens the ice from the level down, cvD deep, keeping a floor of ice over the rock.
            if (crevTab && crevasse(colFi, colU, colV) && r > (g + 1 > S - cvD ? g + 1 : S - cvD)) return SEA_CELL.DRY;
            if (r < S - 1 || !cracked(colFi, colU, colV)) return SEA_CELL.ICE;
            return r === S ? SEA_CELL.DRY : SEA_CELL.CRACK_FLOOR;
        }
        return Object.freeze({ kind, name, level: S, top, lowest: seaLo, amount: ocean ? 'ocean' : 'lakes', cover,
                               lineae: ice ? nLin : 0, tempC: kind === 'lava' ? T : null, at });
    }
    // THE SHELL AS BUILT. Every column's ground, once: a consumer scans only between solidR and airR, and the clamp's
    // bounds (lo, hi) are far wider than a gentle world's ground, so from the ground's own extent the occupancy fill asks
    // isSolid about less than half the cells. Nothing is solid above the highest column (rolled ground past an edge stays
    // under its column's height), and a cell no higher than both the lowest column and the reference cube is under every
    // face's ground and past no edge, so isSolid answers the same for every cell either way.
    let tMin = hi, tMax = lo;
    for (let ti = 0; ti < 6 * WW; ti++) { const t = heightOfColumn(ti); if (t < tMin) tMin = t; if (t > tMax) tMax = t; }
    const floorR = Math.max(tMin < lo ? 1 : lo, Math.min(tMin, refR) - 1);   // (only THE CUTS, version 12, take a column under lo)
    solidR = hasCaves ? Math.min(floorR, caveMinR - 1) : floorR;
    // THE OCCUPANCY BY COLUMN RUNS (round 2: the ground stands taller and the caves run deeper, so the shell asked
    // cell by cell grew to 2M cells on a Ø96 world). Bit for bit what isSolid gives every cell (tests/voxel-occupancy
    // holds it to that): a column well inside its face owns every cell along its axis from where that axis dominates
    // (the cell's other two coordinates are its own, under the reference cube, so it is past no edge and r <= t is the
    // whole test), and only its cave band is asked about. Every other cell of the shell (near the diagonal planes
    // between faces, and round the edges) is asked as before.
    function fill(solid, HALF) {
        const D = 2 * HALF, DD = D * D, done = new Uint8Array(D * DD);
        const ilo = -solidR, ihi = solidR - 1, top = tMax < hi ? tMax : hi;
        for (let fi = 0; fi < 6; fi++) {
            const ax = fi >> 1, neg = (fi & 1) === 1, ua = (ax + 1) % 3, va = (ax + 2) % 3, c = [0, 0, 0];
            const sa = ax === 0 ? 1 : ax === 1 ? D : DD, su = ua === 0 ? 1 : ua === 1 ? D : DD, sv = va === 0 ? 1 : va === 1 ? D : DD;
            for (let u = -refR + 1; u <= refR - 2; u++) for (let v = -refR + 1; v <= refR - 2; v++) {
                const t = heightOfColumn(fi * WW + (u + refR) * Wd + (v + refR));
                const au = u < 0 ? -u - 0.5 : u + 0.5, av = v < 0 ? -v - 0.5 : v + 0.5, m = au > av ? au : av;
                let k = m + 0.5;                                  // the first k whose axis strictly dominates
                if (k < solidR) k = solidR;                       // (r = k + 1 > solidR from here: the box below is solid)
                const cav = hasCaves ? caveMinR : 1e9, capT = t - roof;
                for (; k + 1 <= top; k++) {
                    const a = neg ? -k - 1 : k, i = (a + HALF) * sa + (u + HALF) * su + (v + HALF) * sv;
                    done[i] = 1;
                    const r = k + 1;
                    if (r > t) continue;
                    if (r >= cav && r <= capT) {                  // the cave band: solid unless a cave takes it (isSolid's own test)
                        c[ax] = a; c[ua] = u; c[va] = v;
                        if (!inCave(c[0] + 0.5, c[1] + 0.5, c[2] + 0.5) || isSolid(c[0], c[1], c[2])) solid[i] = 1;
                    }
                    else solid[i] = 1;
                }
            }
        }
        const lo2 = -tMax, hi2 = tMax - 1;
        for (let z = lo2; z <= hi2; z++) for (let y = lo2; y <= hi2; y++) {
            const base = HALF + (y + HALF) * D + (z + HALF) * DD;
            const inner = y >= ilo && y <= ihi && z >= ilo && z <= ihi;
            for (let x = lo2; x <= hi2; x++) {
                if (inner && x === ilo) { solid.fill(1, base + ilo, base + ihi + 1); x = ihi; continue; }
                const i = base + x;
                if (done[i]) continue;
                if (isSolid(x, y, z)) solid[i] = 1;
            }
        }
        return solid;
    }
    // ---- TURF ON REGOLITH (version 9, the owner's spec of Oct 5, turf.mjs) ----
    // In a living biome the surface ground is always regolith: the top two cells along each cell's up (depthUnder < 2),
    // so a one-cell scrape still leaves regolith. surfMat names it as 2 + the crust it would have been: crust n's
    // same-colour twin (turf.mjs soilNamesOf, laid in by whoever holds the crust pair: system-view.js crustFor,
    // genDensity.mjs worldGen). Its top cell grows the turf of the biome's life type, in the one colour the world shows
    // for it (turfColourOf): `turf.at` says which, before anything covers it (a sea, an edit: turf.mjs evaluateTurf).
    let turf = null, soilSurfMat = surfMat;
    if (ver >= TURF_SINCE && bm) {
        const famOf = new Array(BIOMES.length + 1).fill(null);
        for (let i = 0; i < BIOMES.length; i++) famOf[i + 1] = BIOME_TURF[BIOMES[i].id] || null;
        const tw = bioWorld || world, tc = bioClim || (tw ? climOf(tw) : null);
        const cls = tw ? starClassOf(tw.star) : null, chem = tw && tc ? lifeChemistryOf(tw, tc) : null;
        const colour = {};
        for (const f of new Set(famOf.filter(Boolean))) colour[f] = turfColourOf(addr, cls, f);
        // How deep under its column's living ground a cell lies, or -1 where it is not living ground.
        const livingDepth = (x, y, z) => {
            const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5;
            const ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
            const r = Math.round(ax > ay ? (ax > az ? ax : az) : (ay > az ? ay : az));
            const ti = columnOf(x, y, z, cx, cy, cz, ax, ay, az), code = bioTab[ti];
            if (!code || code > BIOMES.length || !famOf[code]) return -1;
            const t = heightOfColumn(ti);
            if (!underGround(r, t, ax, ay, az)) return -1;
            const dz = depthUnder(r, t, ax, ay, az);
            if (dz < 0) return ver >= ROLLED_TURF_SINCE ? 0 : -1;   // the top cell of rolled ground (version 11: see ROCKY_GENERATORS)
            return dz < 2 ? dz : -1;
        };
        const famAt = (x, y, z) => { const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5, ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
            return famOf[bioTab[columnOf(x, y, z, cx, cy, cz, ax, ay, az)]]; };
        soilSurfMat = (x, y, z) => { const m = surfMat(x, y, z); return livingDepth(x, y, z) >= 0 ? 2 + m : m; };
        turf = Object.freeze({
            at: (x, y, z) => { const dd = livingDepth(x, y, z); return dd >= 0 && dd < 1 && isSolid(x, y, z) ? colour[famAt(x, y, z)] : null; },
            soil: (x, y, z) => livingDepth(x, y, z) >= 0 && isSolid(x, y, z),
            family: (x, y, z) => (livingDepth(x, y, z) >= 0 ? famAt(x, y, z) : null),
            colours: Object.freeze({ ...colour }), cls, chem,
            tempAt: (x, y, z) => (tc ? fieldAt(tc, x + 0.5, y + 0.5, z + 0.5).tempC : 0),
        });
    }
    // ---- THE DRAWN KUBE (version 14, "MINE WHAT'S DRAWN": the owner, Oct 12) ----
    // The renderer drew a cell whose biome look was a TINT (biome-looks.mjs's old kube 'crust' looks, or a biome with no
    // look of its own: mineral hills, crater fields, a sulfur spring's pool, an ocean's, a lake's or a river's floor) on the
    // world's FIRST crust kube, crust[0] (system-view.js naturalMatIdAt, before Oct 12), whatever crust the cell is; and
    // until version 14 the cell was still that other crust (the second one, or a regolith), which is what it mined as and
    // what the aim pill named. From version 14 the cell IS crust[0]: exactly the cells the renderer draws with a tint, and
    // no other, so what is drawn, named, mined and credited are one kube. That is every cell surfaceBiome gives a look that
    // is not a real kube (BIOME_LOOK_KUBES: a glacier's Water Ice, salt flats' Halite and the rest mine as themselves), to
    // the look's own depth (biomes.mjs look.depth), less a cell the renderer draws as TURF first: living ground's regolith
    // with the seed's turf on a top open to the sky (system-view.js turfNatural: nothing solid and no sea over it). The
    // shape, the seas, the biomes, the life, the ore, the rocks and the turf are version 13's to the bit; only these
    // cells' kube differs. The sea is the world's own (seaToPrime), so the page and both servers agree under water too.
    let surfMatOut = soilSurfMat;
    if (ver >= DRAWN_KUBE_SINCE) {
        const openTop = (x, y, z) => {
            const u = upOf(x, y, z), a = [x, y, z];
            a[u.a] += u.s;
            return !isSolid(a[0], a[1], a[2]) && !(lastSea && lastSea.at(a[0], a[1], a[2]) !== SEA_CELL.DRY);
        };
        surfMatOut = (x, y, z) => {
            const m = soilSurfMat(x, y, z), v = surfaceBiome(x, y, z);
            if (m === 0 || !v || BIOME_LOOK_KUBES[v]) return m;                  // drawn as itself, or as a look that is a kube
            if (m >= 2 && turf && turf.at(x, y, z) && openTop(x, y, z)) return m;   // drawn as its turf, on its regolith
            return 0;                                                          // drawn as the tint over crust[0]
        };
    }
    // ---- THE TOWN OVERLAY (version 17, ALIEN PEOPLES; THE TOWNS, above) ----
    // Over each plot and its ramp, a column's ground stands at its town level (hNew: the plot's P, the ramp a step a
    // column back toward the ground) instead of its own height: cells over it are open but for the town's buildings, cells
    // between its own top and hNew are filled. Every cell of a plot's top is paved by its role (a street, the plaza, a
    // floor, a field), a filled ramp's top is the people's ground, what is filled under a top is their fill, and every
    // building cell is its kube (alien-peoples.mjs PEOPLE_MATERIALS). isSolid and fill answer the overlay; townAt names it.
    let townOver = null, turfOut = turf;
    if (townList && townList.length && townList[0].v >= TOWNS19_SINCE) {
        // ---- THE TOWN OVERLAY, VERSION 19 (alien-towns.mjs planTown19) ----
        // A plan column inside the town's outline stands at its own level (plan.g), filled or cut from the land, whole for
        // TOWN_FLOOR19 kubes under it; outside the outline (plan.natural) the land is the land, but for what the town builds
        // over it or cuts into it (a façade's bit hanging out, a room cut into a cliff). A plot over a cube edge lies on two
        // faces (alien-peoples.mjs townFrame19): its columns past the edge are the next face's, and the ground between the two
        // rolls over the edge as the world's own ground does (underGround), at the town's level.
        const TOWN_FLOOR19 = 7, mats = PEOPLE_MATERIALS[townPeople.id], regions = [], byFace = [[], [], [], [], [], []];
        let topR = tMax;
        const clampT = (v) => (v < -refR ? -refR : v > refR - 1 ? refR - 1 : v);
        const colOfPlot = (fr, i, j) => { const c = fr.cell(i, j, 0), fiP = fr.part(i, j) ? fr.fiB : fr.fiA, aP = fiP >> 1;
            return fiP * WW + (clampT(c[(aP + 1) % 3]) + refR) * Wd + (clampT(c[(aP + 2) % 3]) + refR); };
        for (const t of townList) {
            const plan = planFor(townPeople.id, t), fr = townFrame19(t, refR), N = t.N, W2 = 2 * N, HRp = plan.hhi - plan.hlo;
            const orig = new Int16Array(W2 * W2);
            for (let i = -N; i < N; i++) for (let j = -N; j < N; j++) orig[(i + N) * W2 + (j + N)] = heightOfColumn(colOfPlot(fr, i, j));
            let carveLo = 0; for (const key of plan.carve) { const hh = +key.split(',')[2]; if (hh < carveLo) carveLo = hh; }
            const reg = { t, plan, fr, N, W2, HR: HRp, orig, carveLo };
            byFace[fr.fiA].push(reg); if (fr.edge) byFace[fr.fiB].push(reg);
            regions.push(reg);
            if (t.P + plan.top > topR) topR = t.P + plan.top;
        }
        // A TOWN IN ITS WORLD'S OWN COLOURS (version 18's rule): its life in the world's pigments, its glass the world's
        const pg18 = pigmentsOf(bioWorld || world || {}, ver >= LIFE_PIGMENTS_SINCE);
        const glassPg = (plan) => { const own = world && typeof world.regolith === 'string' && /^[A-Z][a-z]+$/.test(world.regolith) ? world.regolith : null;
            if (own) return own; const g0 = /^([A-Z][a-z]+) Glass$/.exec(plan.names.find((n) => / Glass$/.test(n) && n !== 'Reinforced Glass') || ''); if (g0) return g0[1];
            const gr = /^([A-Z][a-z]+) Regolith$/.exec(mats.ground || ''); return gr ? gr[1] : 'Lull'; };
        for (const reg of regions) { const gp = glassPg(reg.plan);
            reg.names = Object.freeze(reg.plan.names.map((n) => {
                if (/ Glass$/.test(n)) return gp + ' Glass';
                if (!pg18) return n;
                if (/ Leaf Biomass$/.test(n)) return (n === 'Bloom Leaf Biomass' ? pg18.leaf2 : pg18.leaf) + ' Leaf Biomass';
                if (/ Petal$/.test(n)) return pg18.petal + ' Petal';
                if (/ Glow Biomass$/.test(n)) return pg18.glow + ' Glow Biomass';
                if (/^[A-Z][a-z]+ Wood$/.test(n)) return pg18.leaf + ' Wood';
                return n; })); }
        const q19 = { reg: null, i: 0, j: 0, r: 0, k: 0 };
        function townCol(x, y, z) {
            const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5, ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
            const fi = ax >= ay && ax >= az ? (cx > 0 ? 0 : 1) : ay >= az ? (cy > 0 ? 2 : 3) : (cz > 0 ? 4 : 5);
            const L = byFace[fi]; if (!L.length) return null;
            for (const reg of L) { const c = reg.fr.toPlan(x, y, z); if (!c) continue; q19.reg = reg; q19.i = c[0]; q19.j = c[1]; q19.r = c[2]; q19.k = (c[0] + reg.N) * reg.W2 + (c[1] + reg.N); return q19; }
            return null;
        }
        const cellOf = (c, hh) => { const pl = c.reg.plan; return hh < pl.hlo || hh >= pl.hhi ? 0 : pl.kcell[c.k * c.reg.HR + (hh - pl.hlo)]; };
        const ckey = (c, hh) => c.i + ',' + c.j + ',' + hh;
        const absC = (x, y, z) => { const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5; return [cx < 0 ? -cx : cx, cy < 0 ? -cy : cy, cz < 0 ? -cz : cz]; };
        const pileAt = (i, j) => ((i & 1) === 0 && (j & 1) === 0);
        function isSolidT(x, y, z) {
            const c = townCol(x, y, z);
            if (!c) return isSolid(x, y, z);
            const pl = c.reg.plan, P = c.reg.t.P, hh = c.r - P;
            if (pl.natural[c.k]) { if (cellOf(c, hh)) return true; if (pl.carve.size && pl.carve.has(ckey(c, hh))) return false; return isSolid(x, y, z); }
            const h = P + pl.g[c.k], o = c.reg.orig[c.k], [ax, ay, az] = absC(x, y, z);
            if (!underGround(c.r, h, ax, ay, az)) return cellOf(c, hh) !== 0;
            if (pl.carve.size && pl.carve.has(ckey(c, hh))) return cellOf(c, hh) !== 0;
            if (c.r > o) return !pl.wet[c.k] || c.r >= h || pileAt(c.i, c.j);   // (over water: a deck on piles)
            if (c.r > h - TOWN_FLOOR19) return true;
            return isSolid(x, y, z);
        }
        const levelAt = (reg, i, j) => { const N = reg.N; if (i < -N || i >= N || j < -N || j >= N) return 99; const k = (i + N) * reg.W2 + (j + N); return reg.plan.natural[k] ? reg.orig[k] - reg.t.P : reg.plan.g[k]; };
        function townAt(x, y, z) {
            const c = townCol(x, y, z);
            if (!c) return null;
            const pl = c.reg.plan, names = c.reg.names, nm = (code) => (code > 0 ? names[code - 1] : null), P = c.reg.t.P, hh = c.r - P;
            const fromCell = (v) => { if (v > 0) return nm(v); if (v < 0) { const b0 = (-v - 1) * 8; for (let k2 = 0; k2 < 8; k2++) if (pl.bits[b0 + k2]) return nm(pl.bits[b0 + k2]); } return null; };
            if (pl.natural[c.k]) { const v = cellOf(c, hh); if (v) return fromCell(v); if (pl.under.size && pl.under.has(ckey(c, hh))) return nm(pl.under.get(ckey(c, hh))); return null; }
            const h = P + pl.g[c.k], o = c.reg.orig[c.k], [ax, ay, az] = absC(x, y, z);
            if (!underGround(c.r, h, ax, ay, az)) return fromCell(cellOf(c, hh));
            const key = ckey(c, hh);
            if (pl.carve.size && pl.carve.has(key)) return fromCell(cellOf(c, hh));
            if (pl.under.size && pl.under.has(key)) return nm(pl.under.get(key));
            if (depthUnder(c.r, h, ax, ay, az) < 1) return nm(pl.ground[c.k]);
            const lv = c.r - P; if (levelAt(c.reg, c.i + 1, c.j) < lv || levelAt(c.reg, c.i - 1, c.j) < lv || levelAt(c.reg, c.i, c.j + 1) < lv || levelAt(c.reg, c.i, c.j - 1) < lv) return nm(pl.retain);
            if (c.r > o) return nm(pl.fill);
            return null;
        }
        // the occupancy by the overlay: the base fill, then every plan column's run as isSolidT answers it, and the cells
        // the ground rolls over between a plot's two faces
        function fillT(solid, HALF) {
            fill(solid, HALF);
            const D = 2 * HALF, DD = D * D;
            const setAt = (x, y, z) => { if (x < -HALF || x >= HALF || y < -HALF || y >= HALF || z < -HALF || z >= HALF) return; solid[(x + HALF) + (y + HALF) * D + (z + HALF) * DD] = isSolidT(x, y, z) ? 1 : 0; };
            const cc = [0, 0, 0];
            for (const reg of regions) {
                const pl = reg.plan, t = reg.t, N = reg.N, rTop = t.P + pl.top + 1, fr = reg.fr;
                for (let i = -N; i < N; i++) for (let j = -N; j < N; j++) {
                    const k = (i + N) * reg.W2 + (j + N), h = t.P + pl.g[k], o = reg.orig[k];
                    let r0 = (h < o ? h : o) - TOWN_FLOOR19 + 1; if (t.P + reg.carveLo - 1 < r0) r0 = t.P + reg.carveLo - 1;
                    for (let r = r0; r <= rTop; r++) { fr.cell(i, j, r - t.P, cc); setAt(cc[0], cc[1], cc[2]); }
                }
                // the roll over the edge: past the reference square on both of its axes, out to the town's top
                if (fr.edge) {
                    const e = t.edge, sAx = e.s < 2 ? 0 : 1, sgn = (e.s & 1) ? 1 : -1;
                    for (let o2 = -N; o2 < N; o2++) {
                        const qa = sgn > 0 ? e.e - 1 : e.e, i = sAx === 0 ? qa : o2, j = sAx === 0 ? o2 : qa;
                        const base = fr.cell(i, j, 0), X = e.s < 2 ? fr.ua : fr.va, ax = fr.ax, sA = fr.neg ? -1 : 1;
                        for (let a = refR; a <= rTop; a++) for (let b = refR; b <= rTop; b++) {
                            const c = base.slice(); c[ax] = sA > 0 ? a : -a - 1; c[X] = sgn > 0 ? b : -b - 1; setAt(c[0], c[1], c[2]);
                        }
                    }
                }
            }
            return solid;
        }
        // what scatters nothing and grows nothing: a town column, or what the town has over the land
        function covers(x, y, z) { const c = townCol(x, y, z); if (!c) return false; const pl = c.reg.plan;
            if (pl.natural[c.k]) return !!cellOf(c, c.r - c.reg.t.P);
            const h = c.reg.t.P + pl.g[c.k], o = c.reg.orig[c.k]; return c.r >= (h < o ? h : o); }
        // EVERY BIT OF THE TOWNS, as the game seats them (fr.slot: a plan corner's seat in its grid cell, on either face)
        function forEachBit(fn) {
            const cc = [0, 0, 0];
            for (const reg of regions) {
                const pl = reg.plan, t = reg.t, N = t.N, fr = reg.fr, slots = [[], []];
                for (let k2 = 0; k2 < 8; k2++) { slots[0].push(fr.slot(0, k2)); slots[1].push(fr.slot(1, k2)); }
                for (let i = -N; i < N; i++) for (let j = -N; j < N; j++) { const prt = fr.part(i, j);
                    for (let hh = pl.hlo; hh < pl.hhi; hh++) {
                        const v = pl.kcell[((i + N) * reg.W2 + (j + N)) * reg.HR + (hh - pl.hlo)];
                        if (v >= 0) continue;
                        fr.cell(i, j, hh, cc);
                        const b0 = (-v - 1) * 8;
                        for (let k2 = 0; k2 < 8; k2++) { const m = pl.bits[b0 + k2]; if (m) fn(cc[0], cc[1], cc[2], slots[prt][k2], reg.names[m - 1]); }
                    } }
            }
        }
        // TURFED ROOFS (temperate), version 18's rule; the land outside the outline keeps the world's own turf
        if (turf) {
            const cols = turf.colours || {}, roofTurf = regions.some((r) => r.plan.climate === 'temperate') ? cols.grass || cols[Object.keys(cols).sort()[0]] || null : null;
            const base = turf;
            turfOut = Object.freeze({ ...base, at: (x, y, z) => {
                const c = townCol(x, y, z);
                if (!c) return base.at(x, y, z);
                const pl = c.reg.plan, hh = c.r - c.reg.t.P;
                if (pl.natural[c.k] && !cellOf(c, hh)) return base.at(x, y, z);
                const v = cellOf(c, hh);
                return v > 0 && roofTurf && / Regolith$/.test(pl.names[v - 1]) && !/ (Rammed|Sintered) Regolith$/.test(pl.names[v - 1]) && !cellOf(c, hh + 1) ? roofTurf : null;
            } });
        }
        townOver = {
            isSolid: isSolidT, fill: fillT, airR: topR, waterAt: null, waterTop: -1e9, waterLow: 1e9,
            info: Object.freeze({ people: townPeople, biome: townPeople.biome, towns: townList, plans: regions.map((r) => r.plan), names: regions.map((r) => r.names), refR, at: townAt, covers,
                                  materials: mats, forEachBit, version: TOWNS19_SINCE }),
        };
    }
    else if (townList && townList.length) {
        const mats = PEOPLE_MATERIALS[townPeople.id], byFace = new Array(6).fill(null), regions = [];
        let topR = tMax;
        for (const t of townList) {
            const plan = planFor(townPeople.id, t), ext = t.N + t.M, W2 = 2 * ext, v18 = plan.version >= TOWNS18_SINCE, N2 = 2 * t.N;
            const hNew = new Int16Array(W2 * W2), orig = new Int16Array(W2 * W2);
            for (let a = 0; a < W2; a++) for (let b = 0; b < W2; b++) {
                const i = a - ext, j = b - ext, ti = t.fi * WW + (t.cu + i) * Wd + (t.cv + j), h = heightOfColumn(ti);
                const di = i < -t.N ? -t.N - i : i >= t.N ? i - t.N + 1 : 0, dj = j < -t.N ? -t.N - j : j >= t.N ? j - t.N + 1 : 0;
                const d = di > dj ? di : dj;
                orig[a * W2 + b] = h;
                // (version 18) a plot column stands at its own level (alien-peoples.mjs plan.g: terraces, stairs, the canal,
                // the walls, the fields on their own ground). Round it there is no ramp (the owner, Oct 13: "they should border
                // right up against the natural landscape"): only where a lane leaves the plot (its gate, a track out to the
                // country) do the columns straight out from it step from its level to the ground, a kube a column.
                if (v18 && d > 0) {
                    let L = null;
                    if (di === 0 || dj === 0) {
                        const bi = i < -t.N ? -t.N : i >= t.N ? t.N - 1 : i, bj = j < -t.N ? -t.N : j >= t.N ? t.N - 1 : j, bk = (bi + t.N) * N2 + (bj + t.N);
                        const sc = plan.street[bk];
                        if (sc === ST18.MAIN || sc === ST18.TRACK) L = t.P + plan.g[bk];
                    }
                    hNew[a * W2 + b] = L === null ? h : (h < L - d ? L - d : h > L + d ? L + d : h);
                    continue;
                }
                const L = !v18 ? t.P : t.P + plan.g[(i + t.N) * N2 + (j + t.N)];
                hNew[a * W2 + b] = d === 0 ? L : (h < L - d ? L - d : h > L + d ? L + d : h);
            }
            const reg = { t, plan, ext, W2, N2, hNew, orig, v18, u0: t.cu - refR, v0: t.cv - refR, ax: t.fi >> 1, neg: (t.fi & 1) === 1 };
            if (v18) { let lo = 0; for (const key of plan.carve) { const hh = +key.split(',')[2]; if (hh < lo) lo = hh; } reg.carveLo = lo; reg.HR = plan.hhi - plan.hlo; }
            byFace[t.fi] = reg; regions.push(reg);
            if (t.P + plan.top > topR) topR = t.P + plan.top;
        }
        // VERSION 18, A TOWN IN ITS WORLD'S OWN COLOURS (the owner, Oct 13): its living bits (vines, moss, flower boxes, crops,
        // garden trees, glowing seams) are its world's life, in the pigments the world's own growth wears (organics.mjs
        // pigmentsOf: the leaves by its star, its petal, its glow), and its windows the glass its world's regolith melts to
        // ("<P> Glass", see-through as placed glass is; world.regolith, which the page gives from its crust), or else a glass
        // of the people's own ground, never the opaque Reinforced Glass. One table a world; plan.names stays the people's.
        const pg18 = regions.some((r) => r.v18) ? pigmentsOf(bioWorld || world || {}, ver >= LIFE_PIGMENTS_SINCE) : null;
        const glassPg = (plan) => { const own = world && typeof world.regolith === 'string' && /^[A-Z][a-z]+$/.test(world.regolith) ? world.regolith : null;
            if (own) return own; const g0 = /^([A-Z][a-z]+) Glass$/.exec(plan.names.find((n) => / Glass$/.test(n) && n !== 'Reinforced Glass') || ''); if (g0) return g0[1];
            const gr = /^([A-Z][a-z]+) Regolith$/.exec(mats.ground || ''); return gr ? gr[1] : 'Lull'; };
        const renamed18 = (plan) => {
            const gp = glassPg(plan);
            return Object.freeze(plan.names.map((n) => {
                if (/ Glass$/.test(n)) return gp + ' Glass';
                if (!pg18) return n;
                if (/ Leaf Biomass$/.test(n)) return (n === 'Bloom Leaf Biomass' ? pg18.leaf2 : pg18.leaf) + ' Leaf Biomass';
                if (/ Petal$/.test(n)) return pg18.petal + ' Petal';
                if (/ Glow Biomass$/.test(n)) return pg18.glow + ' Glow Biomass';
                if (/^[A-Z][a-z]+ Wood$/.test(n)) return pg18.leaf + ' Wood';
                return n;
            }));
        };
        for (const reg of regions) reg.names = reg.v18 ? renamed18(reg.plan) : reg.plan.names;
        const roleKube = (role) => (role ? mats[role] || null : null);
        const GROUND_ROLE = MATERIAL_ROLES;      // plan.ground holds a role's index + 1
        // The region a cell's column falls in, its plot coordinates and its radius, or null (into `q`).
        const q = { reg: null, i: 0, j: 0, r: 0, a: 0 };
        function townCol(x, y, z) {
            const cx = x + 0.5, cy = y + 0.5, cz = z + 0.5, ax = cx < 0 ? -cx : cx, ay = cy < 0 ? -cy : cy, az = cz < 0 ? -cz : cz;
            let fi, u, v;
            if (ax >= ay && ax >= az) { fi = cx > 0 ? 0 : 1; u = y; v = z; }
            else if (ay >= az) { fi = cy > 0 ? 2 : 3; u = z; v = x; }
            else { fi = cz > 0 ? 4 : 5; u = x; v = y; }
            const reg = byFace[fi];
            if (!reg) return null;
            const i = u - reg.u0, j = v - reg.v0;
            if (i < -reg.ext || i >= reg.ext || j < -reg.ext || j >= reg.ext) return null;
            q.reg = reg; q.i = i; q.j = j; q.a = (i + reg.ext) * reg.W2 + (j + reg.ext);
            q.r = Math.round(ax > ay ? (ax > az ? ax : az) : (ay > az ? ay : az));
            return q;
        }
        const inPlot = (c) => c.i >= -c.reg.t.N && c.i < c.reg.t.N && c.j >= -c.reg.t.N && c.j < c.reg.t.N;
        // VERSION 18: what a plot cell over its column's ground holds (0 nothing, > 0 a kube's code, < 0 bits), and whether a
        // cell at or under it is exposed (a terrace's, a stair's, the canal's or a cut's side: its retaining wall).
        const cell18 = (c, hh) => { const pl = c.reg.plan; if (hh < pl.hlo || hh >= pl.hhi) return 0; return pl.kcell[((c.i + c.reg.t.N) * c.reg.N2 + (c.j + c.reg.t.N)) * c.reg.HR + (hh - pl.hlo)]; };
        const levelAt = (reg, i, j) => { const N = reg.t.N; if (i >= -N && i < N && j >= -N && j < N) return reg.plan.g[(i + N) * reg.N2 + (j + N)];
            if (i < -reg.ext || i >= reg.ext || j < -reg.ext || j >= reg.ext) return 99; return reg.hNew[(i + reg.ext) * reg.W2 + (j + reg.ext)] - reg.t.P; };
        const exposed = (c, hh) => levelAt(c.reg, c.i + 1, c.j) < hh || levelAt(c.reg, c.i - 1, c.j) < hh || levelAt(c.reg, c.i, c.j + 1) < hh || levelAt(c.reg, c.i, c.j - 1) < hh;
        const pileAt = (i, j) => ((i & 1) === 0 && (j & 1) === 0);
        const TOWN_FLOOR = 7;
        function isSolidT(x, y, z) {
            const c = townCol(x, y, z);
            if (!c) return isSolid(x, y, z);
            const h = c.reg.hNew[c.a], o = c.reg.orig[c.a];
            if (c.reg.v18 && inPlot(c)) {
                const hh = c.r - c.reg.t.P, pl = c.reg.plan;
                if (c.r > h) return cell18(c, hh) !== 0;
                if (pl.carve.size && pl.carve.has(c.i + ',' + c.j + ',' + hh)) return false;
                if (c.r > o) return !pl.wet[(c.i + c.reg.t.N) * c.reg.N2 + (c.j + c.reg.t.N)] || c.r === h || pileAt(c.i, c.j);   // (over water: a deck on piles)
                // (Oct 13) a town's ground is whole for TOWN_FLOOR kubes under it: a cave or a cut the world has just under a
                // plot (a canal dug down into one) is filled, so nothing in town opens on to a hole through the world
                if (c.r > h - TOWN_FLOOR) return true;
                return isSolid(x, y, z);
            }
            if (c.r > h) return inPlot(c) && !c.reg.v18 && c.reg.plan.above.has(c.i + ',' + c.j + ',' + (c.r - c.reg.t.P));
            if (c.r > o) return true;
            return isSolid(x, y, z);
        }
        function townAt(x, y, z) {
            const c = townCol(x, y, z);
            if (!c) return null;
            const h = c.reg.hNew[c.a], o = c.reg.orig[c.a], plot = inPlot(c);
            if (c.reg.v18) {
                const pl = c.reg.plan, names = c.reg.names, nm = (code) => (code > 0 ? names[code - 1] : null);
                if (!plot) { if (c.r === h) return h !== o ? roleKube('ground') : null; if (c.r > o && c.r < h) return nm(pl.fill); return null; }
                const hh = c.r - c.reg.t.P;
                if (c.r > h) { const v = cell18(c, hh); if (v > 0) return nm(v); if (v < 0) { const b0 = (-v - 1) * 8; for (let k = 0; k < 8; k++) if (pl.bits[b0 + k]) return nm(pl.bits[b0 + k]); } return null; }
                if (c.r === h) return nm(pl.ground[(c.i + c.reg.t.N) * c.reg.N2 + (c.j + c.reg.t.N)]);
                const key = c.i + ',' + c.j + ',' + hh;
                if (pl.under.size && pl.under.has(key)) return nm(pl.under.get(key));
                if (pl.carve.size && pl.carve.has(key)) return null;
                if (exposed(c, hh)) return nm(pl.retain);
                if (c.r > o) return nm(pl.fill);
                return null;
            }
            if (c.r > h) return plot ? roleKube(c.reg.plan.above.get(c.i + ',' + c.j + ',' + (c.r - c.reg.t.P))) : null;
            if (c.r === h) {
                if (plot) { const N2 = 2 * c.reg.t.N, g = c.reg.plan.ground[(c.i + c.reg.t.N) * N2 + (c.j + c.reg.t.N)]; return roleKube(GROUND_ROLE[g - 1]); }
                return h !== o ? roleKube('ground') : null;
            }
            if (c.r > o) return roleKube('fill');
            return null;
        }
        // The occupancy by the overlay: the base fill, then every overlaid column's run from its lower top up past its
        // buildings, as isSolidT answers it (from version 18 from under its cavern rooms too).
        function fillT(solid, HALF) {
            fill(solid, HALF);
            const D = 2 * HALF, DD = D * D, c = [0, 0, 0];
            for (const reg of regions) {
                const sa = reg.ax === 0 ? 1 : reg.ax === 1 ? D : DD, ua = (reg.ax + 1) % 3, va = (reg.ax + 2) % 3;
                const su = ua === 0 ? 1 : ua === 1 ? D : DD, sv = va === 0 ? 1 : va === 1 ? D : DD, rTop = reg.t.P + reg.plan.top + 1;
                const lo18 = reg.v18 ? reg.t.P + reg.carveLo - 1 : 1e9;
                for (let a = 0; a < reg.W2; a++) for (let b = 0; b < reg.W2; b++) {
                    const h = reg.hNew[a * reg.W2 + b], o = reg.orig[a * reg.W2 + b], u = reg.u0 + a - reg.ext, v = reg.v0 + b - reg.ext;
                    let r0 = (h < o ? h : o) + 1; if (lo18 < r0) r0 = lo18;
                    if (reg.v18 && a >= reg.ext - reg.t.N && a < reg.ext + reg.t.N && b >= reg.ext - reg.t.N && b < reg.ext + reg.t.N && h - TOWN_FLOOR + 1 < r0) r0 = h - TOWN_FLOOR + 1;
                    for (let r = r0; r <= rTop; r++) {
                        const ai = reg.neg ? -r : r - 1;
                        if (ai < -HALF || ai >= HALF) continue;
                        c[reg.ax] = ai; c[ua] = u; c[va] = v;
                        solid[(ai + HALF) * sa + (u + HALF) * su + (v + HALF) * sv] = isSolidT(c[0], c[1], c[2]) ? 1 : 0;
                    }
                }
            }
            return solid;
        }
        // Whether a cell stands in a town's region (its plot or ramp) at or over its ground: what scatters nothing
        // (rocks, natural bits) and grows nothing there.
        // (version 18: its plot, and a gate's steps; the natural ground round it keeps its own rocks and growth)
        function covers(x, y, z) { const c = townCol(x, y, z); return !!c && (!c.reg.v18 || inPlot(c) || c.reg.hNew[c.a] !== c.reg.orig[c.a]) && c.r >= (c.reg.hNew[c.a] < c.reg.orig[c.a] ? c.reg.hNew[c.a] : c.reg.orig[c.a]); }
        // VERSION 18: EVERY BIT OF THE TOWNS, as the game seats them: fn(x, y, z, slot, material) for each bit of each cell
        // that holds bits (alien-peoples.mjs cornerSlot: a plan corner's seat in its grid cell). The page lays them in as it
        // lays natural bits (system-view.js seedTownBits); the name is the material's, its bit is "<material> Bit".
        function forEachBit(fn) {
            const cc = [0, 0, 0];
            for (const reg of regions) {
                if (!reg.v18) continue;
                const pl = reg.plan, t = reg.t, N = t.N, fr = townFrame(t, refR), slots = [];
                for (let k = 0; k < 8; k++) slots.push(cornerSlot(fr, k));
                for (let i = -N; i < N; i++) for (let j = -N; j < N; j++) for (let hh = pl.hlo; hh < pl.hhi; hh++) {
                    const v = pl.kcell[((i + N) * reg.N2 + (j + N)) * reg.HR + (hh - pl.hlo)];
                    if (v >= 0) continue;
                    fr.cell(i, j, hh, cc);
                    const b0 = (-v - 1) * 8;
                    for (let k = 0; k < 8; k++) { const m = pl.bits[b0 + k]; if (m) fn(cc[0], cc[1], cc[2], slots[k], reg.names[m - 1]); }
                }
            }
        }
        // VERSION 18, TURFED ROOFS (the owner: "turfed roofs on temperate worlds"): the top of a temperate town's roof, laid in
        // its soil, grows the world's own turf, drawn as turf on regolith is anywhere (system-view.js 't:'); nothing else in a
        // town's plot does, and everywhere else is the world's turf as it was.
        if (turf && regions.some((r) => r.v18 && r.plan.climate === 'temperate')) {
            const cols = turf.colours || {}, roofTurf = cols.grass || cols[Object.keys(cols).sort()[0]] || null;
            const base = turf;
            turfOut = Object.freeze({ ...base, at: (x, y, z) => {
                const c = townCol(x, y, z);
                if (!c || !c.reg.v18 || !inPlot(c)) return base.at(x, y, z);
                const hh = c.r - c.reg.t.P, v = c.r > c.reg.hNew[c.a] ? cell18(c, hh) : 0, pl = c.reg.plan;
                return v > 0 && roofTurf && / Regolith$/.test(pl.names[v - 1]) && !/ (Rammed|Sintered) Regolith$/.test(pl.names[v - 1]) && !cell18(c, hh + 1) ? roofTurf : null;
            } });
        }
        // VERSION 18, THE CANALS' WATER (alien-peoples.mjs plan.water): a cell of it, for the world's sea (townSeaOf).
        function waterAt(x, y, z, name) {
            const c = townCol(x, y, z);
            if (!c || !c.reg.v18 || !inPlot(c)) return false;
            const pl = c.reg.plan;
            return !!(pl.water && pl.water.size && pl.liquid === name && pl.water.has(c.i + ',' + c.j + ',' + (c.r - c.reg.t.P)));
        }
        let waterTop = -1e9, waterLow = 1e9;
        for (const reg of regions) if (reg.v18 && reg.plan.water && reg.plan.water.size && reg.plan.canal) { waterTop = Math.max(waterTop, reg.t.P + reg.plan.canal.water); waterLow = Math.min(waterLow, reg.t.P + reg.plan.canal.water - 1); }
        townOver = {
            isSolid: isSolidT, fill: fillT, airR: topR, waterAt: waterTop > -1e9 ? waterAt : null, waterTop, waterLow,
            info: Object.freeze({ people: townPeople, biome: townPeople.biome, towns: townList, plans: regions.map((r) => r.plan), names: regions.map((r) => r.names), refR, at: townAt, covers,
                                  materials: mats, forEachBit, version: regions.some((r) => r.v18) ? TOWNS18_SINCE : ALIENS_SINCE }),
        };
    }
    return { isSolid: townOver ? townOver.isSolid : isSolid, surfMat: surfMatOut, solidR, airR: townOver ? townOver.airR : tMax, surfLoR: tMin < lo ? tMin : lo,
             caveMinR: hasCaves ? caveMinR : null, roof, fill: townOver ? townOver.fill : fill, turf: turfOut, cutAt, town: townOver ? townOver.info : null,
             craters: geo.craters, tunnels: nWorms, mountains: geo.plates > 0, mesas: false,
             signature: null, valleys: geo.seaLevel !== null, volcanoes: geo.hotspots, spires: false, tablelands: false,
             dunes: !!(bm && bm.ids.indexOf('duneSea') >= 0), basaltColumns: false, iceCrevasses: !!cvSeed, boldSmall: false,
             geology: { age: geo.age, heat: geo.heat, relief: geo.relief, plates: geo.plates, hotspots: geo.hotspots, craters: geo.craters, grid: geo.N,
                        // For the review tools (tools/terrain-evolve/relief-options.mjs): a column's natural height before
                        // the band compresses it, on the refR grid. Read only; nothing in the world depends on it.
                        rawAt: (fi, iu, iv) => rawAt(fi, iu, iv), refR },
             biomes: bm ? (townOver && bioTab && townBiomeCode > 0 ? bm.ids.concat([townPeople.biome]) : bm.ids.slice()) : [], surfaceBiome, biomeCover, columnBiome,     // (surfaceBiome always: the water biomes need no land biome; version 17: an alien world's settlement biome last)
             organics: (sea) => life('organics', sea), flora: (sea) => life('flora', sea),
             genome: G, surfaceSea: townOver && townOver.waterAt ? townSeaOf(surfaceSea, townOver) : surfaceSea };
}

// ── VERSION 3 SEAS ─────────────────────────────────────────────────────────────────────────────────────────────────
// WHICH is still the phase model's (phase.mjs worldSeaOf) and WHERE still the terrain's. HOW MUCH changes:
// The figures below are the genome's defaults (V3_GENES), which are version 3 as it was built:
//   * AN OCEAN OR AN ICE SHEET COVERS A QUARTER TO A HALF OF A WORLD (oceanCoverMin..Max, by the world's own '#seas'
//     draw), never more than oceanCap (62%) when a level lower holds any: in the review every near-total cover (74-93%)
//     was unsure or disliked and every lake world liked, so the land around a sea is what makes it. The continents of
//     version 3's terrain give that sea coasts, islands and archipelagos. The draw decides dry, lakes or ocean as
//     version 2's does, with the same odds (V3_SEA_AMOUNT).
//   * LAKES are version 2's rule, 3-15% asked for, leaning to the fuller level and never more than a third (lakeCap).
//   * LAVA COMES FROM THE WORLD'S TEMPERATURE, not the draw, and a lava world is never dry: from LAVA_SEA_FROM_C (700 °C)
//     lakes and rivers in the basins, a tenth of the surface rising to a quarter at lavaOceanFrom (1,200 °C); above
//     that a molten surface, 55% rising to 90% at lavaFullAt (2,000 °C), with a few rafts of crust. The temperature
//     travels with the sea's description ({ kind: 'lava', tempC }, phase.mjs worldSeaOf), and a claim records it with
//     its sea, rounded to a degree, so every side builds the same one (functions/share.js worldSeaRecord, recordedSeaOf).
//
// WHAT THAT COMES TO at version 3, over the same 20,342 real rocky worlds as version 2's figures (ROCKY SEAS):
// none 72.2%, lakes 19.7%, oceans 8.1% (lava is never dry now). An ocean covers 12-62% of its world (a tenth of them under
// 28%, median 41%, a tenth over 53%); lakes a median 10%, none over 33%; lava lakes 1-35% (median 19%), lava oceans
// 50-96% (median 78%). The signatures dealt: a range 30.4%, a crater 24.8%, mesas 19.6%, a canyon 25.2%.
// tests/world-v3.test.mjs holds the rules on a smaller sample.
const V3_SEA_AMOUNT = { liquid: [0.3, 0.7], ice: [0.6, 0.9] };
const V3_LAVA_DEFAULT_C = 950;
// A lava sea's temperature, whole degrees: the description's, or a middling one where a description has none (a record
// from before temperatures were recorded, which version 3 never made, so this is a guard rather than a path).
function v3LavaTempOf(desc) {
    const t = desc && desc.tempC;
    return typeof t === 'number' && t === t && t > -1e6 && t < 1e6 ? Math.round(t) : V3_LAVA_DEFAULT_C;
}
// How much of a world a lava sea asks to cover at temperature T, by the genome's curve (VERSION 3 SEAS).
function v3LavaCover(T, G) {
    if (T < G.lavaOceanFrom) { const k = (T - 700) / (G.lavaOceanFrom - 700); return G.lavaLakeCover + G.lavaLakeRise * (k < 0 ? 0 : k > 1 ? 1 : k); }
    const k = (T - G.lavaOceanFrom) / (G.lavaFullAt - G.lavaOceanFrom);
    return G.lavaMoltenCover + G.lavaMoltenRise * (k > 1 ? 1 : k);
}

// A TOWN'S CANAL IS ITS WORLD'S OWN WATER (version 18, the owner, Oct 13: "issues with liquid canals where we can currently see
// through the world"): its cells (alien-peoples.mjs plan.water) are laid into the sea a world's basins hold, so the page
// draws them as it draws a river, a thermos dips them and both servers know them, and no kube of liquid stands in the
// ground with its banks' faces culled behind it. The same sea, with the canal in it and its top raised to hold it.
function townSeaOf(surfaceSea, over) {
    const memo = new Map();
    return (desc) => {
        const base = surfaceSea(desc);
        if (!base || base.kind === 'lava') return base;
        if (memo.has(base)) return memo.get(base);
        const code = base.kind === 'ice' ? SEA_CELL.ICE : SEA_CELL.LIQUID, at0 = base.at;
        const w = Object.freeze({ ...base, top: Math.max(base.top || base.level, over.waterTop), lowest: Math.min(base.lowest, over.waterLow), at: (x, y, z) => (over.waterAt(x, y, z, base.name) ? code : at0(x, y, z)) });
        memo.set(base, w);
        return w;
    };
}

// A sea's cells, for a renderer that draws the whole world: every cell it fills, as ascending indices into the D³ grid
// (D = 2·HALF, x fastest, buildVoxelWorld's at()) with its SEA_CELL code beside it. Only the shell between the lowest
// the ground goes and the sea's top is asked about; the rows inside it are skipped whole, as fillRockyOccupancy does.
// The top is the level, or on a version 3 ice sheet the level above it, where its pressure ridges stand.
export function rockySeaCells(sea, HALF) {
    const idx = [], code = [];
    if (!sea) return { idx: new Int32Array(0), code: new Uint8Array(0) };
    const D = 2 * HALF, DD = D * D, S = Math.min(sea.top || sea.level, HALF), lo = sea.lowest;
    for (let z = -S; z < S; z++) for (let y = -S; y < S; y++) {
        const base = HALF + (y + HALF) * D + (z + HALF) * DD, inner = y >= -lo && y < lo && z >= -lo && z < lo;
        for (let x = -S; x < S; x++) {
            if (inner && x === -lo) { x = lo - 1; continue; }   // the whole run inside the lowest ground: never sea
            const c = sea.at(x, y, z);
            if (c) { idx.push(base + x); code.push(c); }
        }
    }
    return { idx: Int32Array.from(idx), code: Uint8Array.from(code) };
}

// A SEA AS A CLAIM RECORDS IT. claimWorld works out which sea a world's basins hold when it is claimed and writes it on
// the claim and on the world's public version record: { kind, name, level, genVersion }, or null for none. It is
// recorded because the kind is read from the world's climate, and nothing may ever let a later change to the climate
// model, or to a world's air, turn a claimed world's ice into water under what was built on it. So once a world is
// claimed, the client and both servers take its sea from the record, and only an unclaimed world's is read from its
// climate. Returns what surfaceSea asks for ({ kind, name }), null for "no sea", or undefined when there is no record to
// read (a claim made before seas were recorded), so a caller can tell "none" from "not known".
// A LAVA SEA ALSO CARRIES ITS TEMPERATURE ({ ..., tempC }, whole degrees), because at version 3 how much of a world is
// molten follows it (VERSION 3 SEAS). A record without one, and every other kind, reads exactly as it always did.
export function recordedSeaOf(rec) {
    if (rec === undefined) return undefined;
    if (!rec || typeof rec !== 'object') return null;
    const kind = rec.kind;
    if (kind !== 'liquid' && kind !== 'ice' && kind !== 'lava') return null;
    // The material is saved as its item id since Oct 1 (item-ids.mjs); a record made before then holds the name.
    const name = typeof rec.name === 'number' ? decodeItem(rec.name) : typeof rec.name === 'string' ? rec.name : null;
    if (kind === 'lava' && typeof rec.tempC === 'number' && rec.tempC === rec.tempC) return Object.freeze({ kind, name, tempC: Math.round(rec.tempC) });
    return Object.freeze({ kind, name });
}

// Rocky grid radius. Matches voxelRadiusFor(b) = round(voxelDiamFor(b)/2), CLAMP INCLUDED — the client bounds a rocky
// diameter to [VOX_MIN_D, VOX_MAX_D] = [24, 96] before halving (system-view.js voxelDiamFor), so a server that halved the
// raw stored diameter would build a different grid from the one the player is standing on for any world outside the band.
// submitEdits happens to reject such a claim up front today, which is exactly what would keep this latent.
// (HALF = 2R+1, not R: the grid is far larger than the world because you can pillar well above the surface.)
export function rockyRadius(diameter) { return Math.round(Math.max(24, Math.min(96, Math.round(diameter))) / 2); }

// THE SIXTEEN #vox DRAWS, in their one home. Every world, rocky or transient, on the client or the server, starts from
// this stream: the noise seed, the twelve grid offsets, then roughness, variance and the material threshold. It used
// to be replayed by hand in six places (the renderer twice, genDensity, the share-card renderer, and twice here), and
// a copy that slipped by one draw would silently re-materialise every world. Now each of them asks for it.
export function voxDraws(addr) {
    const rng = makeRng((addr || '') + '#vox');
    const noise = makeNoise(Math.floor(rng() * 0x7fffffff));   // draw 1: noise seed
    const o = () => rng() * 10000;
    const off = [o(), o(), o(), o(), o(), o(), o(), o(), o(), o(), o(), o()];   // draws 2-13: the 12 grid offsets
    const rough = 1 + Math.floor(rng() * 10);                  // draw 14 (shapes only rocky worlds)
    const variance = rng();                                    // draw 15 (likewise)
    const surfThresh = rng() - 0.5;                            // draw 16
    return { noise, off, rough, variance, surfThresh };
}

// ---- GENERATION VERSIONS ------------------------------------------------------------------------------------------
// A player's edits are stored as differences from the generated terrain, so a world somebody has built on must keep
// generating the same terrain forever. Each claim records the version it was made with (claimWorld writes the world's
// public version record if it has one, else GEN_VERSION_LATEST, and records it in worldVersions/{addr} too, where
// guests can read it before the world is built); everything that generates a claimed world asks for that version; a
// world with no claim and no record gets the newest. So improving how worlds look means ADDING a version here, never
// editing one: tests/worldgen-golden.test.mjs freezes every version's terrain, materials, ore and rocks.
//
// SHIPPING A VERSION IS THIS ONE LINE, and taking it back is the same line: nothing else in the game names a version.
// Worlds claimed while it was live keep it on their claim and their record, so rolling back leaves them as they are.
export const ROLLED_TURF_SINCE = 11;   // the world version whose rolled edge and corner tops grow turf (livingDepth)
export const GEN_VERSION_LATEST = 24;   // version 24 ("No Rime On Conifers", Oct 26: a needle tree's pale rime rings are its own leaves, so a conifer is one colour; all else version 23's).
// version 23 ("Bark By Starlight", Oct 23: every wood's bark its own, pale to near black by the light on its tree's face, each tree form one bark per face, all 32 pigments; all else version 22's).
// version 22 ("Doors And Rungs In Towns", Oct 20: a door in every front doorway in its people's look, rungs up watch towers and to roof caches, a hatch and rungs down every hidden cellar; all else version 21's).
// version 21 ("Towns Fit Their Worlds", Oct 20: towns may cover a fifth of every world, every town big enough for two blocks and its square — fewer towns on small worlds, the blocks laid from a corner of a small plot, a narrow face's town folded over an edge, no people on a world too small for that; all else version 20's).
// version 20 ("Town Buildings", Oct 20: alien towns of row houses on the 5-kube module, shared walls, 4-kube storeys, U stairs, grids on flat land, no villages; all else version 19's).
// version 19 ("City Layouts And Interiors": a people's towns grown from the land in each people's two forms, joined into cities of districts, over cube edges, within a tenth of the world, with real rooms, furniture, loot and secrets inside: the owner's two proposals, Oct 19); version 18 ("Alien Towns": a people's towns rebuilt in bits on their own ground, rectangles shoulder to shoulder along lanes grown from the heart, each people's façades in its climate's look: the owner's round 4, Oct 13); version 17 ("Alien Peoples": one fitting world in a hundred nobody holds belongs to a people, its towns in its own materials, its residents walking by the clock: the owner, Oct 13); version 16 ("Life Of Every Pigment": the six pigments nothing grew are grown, for their bioliths: the owner, Oct 13); version 15 ("Tufts Are Painted": no grass tuft on turf, its tufts painted in its texture: the owner, Oct 12); version 14 ("Mine What's Drawn": a tinted cell of the ground is the kube its tint is drawn on: the owner, Oct 12); version 13 ("One Temperature": every world one temperature, locked worlds too: the owner, Oct 12); version 12 ("Biome Wave 6": no latitude on turning worlds, woods by the world's average, twelve new biomes: the owner, Oct 11); version 11 (turf on the rolled tops of edges and corners: the owner, Oct 8); version 10 (what grows is planned on its up side, whole on its face, at edges too: the owner, Oct 8); version 9 (turf on regolith over all living ground: the owner's spec, Oct 5); version 8 (rivers stand level, a skinny basin each: the owner, Oct 4); version 7 (the owner's tree forms, built 2026-10-02); version 6 (the bare ground of airless and thin-air worlds: mineral hills, mineral mare, crater fields, regolith plains) built 2026-10-02 at the owner's word; version 5 (biomes cover the whole world: rockyTerrainV4 opts.fill) went live on dev on 2026-09-30, at the owner's word; version 4 (WG-11: simulated geology, biomes decide the relief, the whole Biome Atlas, water biomes) went live on dev on 2026-09-30, at the owner's word; version 3 (WG-08/09/10) went live 2026-09-29, version 2 (WG-03) 2026-09-28
// The version a world is generated at, from its claim: no claim means nobody has built there, so the newest; a claim
// without the field was made before versions existed, with version 1.
export function genVersionOf(claim) {
    if (!claim) return GEN_VERSION_LATEST;
    const v = Number(claim.genVersion);
    return Number.isInteger(v) && v >= 1 ? v : 1;
}
// version → (draws, R, addr) → the world's shape and material. Version 1 is the generator every early world was built
// with. Each version also says WHERE its terrain can vary, as a cube radius r (rockyIsSolid's measure: the rounded L∞
// distance of the cell's centre): every cell with r ≤ solidR is solid and every cell with r > airR is air, whatever the
// noise says. fillRockyOccupancy calls isSolid only between the two, which is what keeps focusing a big planet from
// freezing the game; a version that carves caves or raises mountains widens its own bounds and the fast path stays
// exact. surfLoR is the lowest the SURFACE can sit (caves go deeper), for a renderer drawing faces as height maps.
// R = 0 asks for the material field alone (rockySurfMat). Versions 1 and 2 answer it without the world's size; version
// 3's crust follows its terrain, so it answers `needsSize` instead and rockySurfMat asks again with the diameter.
// surfaceSea(kind) is what stands in the basins (ROCKY SEAS): version 1 has none, whatever the world's climate.
const ROCKY_GENERATORS = {
    1: (d, R) => ({
        isSolid: (x, y, z) => rockyIsSolid(d.noise, d.off, R, d.rough, d.variance, x, y, z),
        surfMat: (x, y, z) => voxSurfMat(d.noise, d.off, d.surfThresh, x, y, z),
        solidR: Math.max(1, R - 3), airR: R,          // rockyIsSolid's own two early returns
        surfLoR: R - 3,
    }),
    // Version 2's material field IS version 1's: same crust pair, same patches, same server price per cell.
    2: (d, R, addr) => {
        const surfMat = (x, y, z) => voxSurfMat(d.noise, d.off, d.surfThresh, x, y, z);
        if (!(R > 0)) return { isSolid: () => false, surfMat, solidR: 0, airR: 0, surfLoR: 0 };
        const t = rockyTerrainV2(d, R, addr);
        return { isSolid: t.isSolid, surfMat, solidR: t.solidR, airR: t.airR, surfLoR: t.surfLoR, surfaceSea: t.surfaceSea,
                 features: { craters: t.craters, tunnels: t.tunnels, mountains: t.mountains, mesas: t.mesas,
                             caveMinR: t.caveMinR, roof: t.roof } };
    },
    // Version 3 (ROCKY WORLDS, VERSION 3): its own shape, seas and material field, the crust following the terrain.
    // Version 3's world is also its GENOME's (VERSION 3'S GENOME): none, or {}, is version 3 as it was built.
    3: (d, R, addr, genome, world) => {
        if (!(R > 0)) {
            const needs = () => { throw new Error('version 3\'s crust follows its terrain: its material field needs the world\'s diameter'); };
            return { isSolid: () => false, surfMat: needs, solidR: 0, airR: 0, surfLoR: 0, needsSize: true };
        }
        const t = rockyTerrainV3(d, R, addr, genome, world);
        return { isSolid: t.isSolid, surfMat: t.surfMat, solidR: t.solidR, airR: t.airR, surfLoR: t.surfLoR, surfaceSea: t.surfaceSea,
                 genome: t.genome, surfaceBiome: t.surfaceBiome, biomeCover: t.biomeCover, columnBiome: t.columnBiome || null, organics: t.organics, flora: t.flora,
                 features: { craters: t.craters, tunnels: t.tunnels, mountains: t.mountains, mesas: t.mesas,
                             caveMinR: t.caveMinR, roof: t.roof, signature: t.signature, valleys: t.valleys,
                             volcanoes: t.volcanoes, spires: t.spires, tablelands: t.tablelands, dunes: t.dunes,
                             basaltColumns: t.basaltColumns, iceCrevasses: t.iceCrevasses, boldSmall: t.boldSmall, biomes: t.biomes } };
    },
    // Version 4 (ROCKY WORLDS, VERSION 4): the ground simulated (geology.mjs), the biomes and seas kept.
    4: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, null),
    // Version 5 (the owner, Sep 30): version 4 with its biomes covering the whole world (rockyTerrainV4 opts.fill).
    5: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true }),
    // Version 6 (the owner, Oct 2): version 5 with the bare ground of airless and thin-air worlds (biomes.mjs wave 5).
    6: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 6 }),
    // Version 7 (the owner, Oct 2): version 6 with the tree forms the owner kept (thirteen, one to three a biome), every
    // leaf joined to its trunk. The ground is version 6's to the bit; only what grows on it differs.
    7: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 7 }),
    // Version 8 (the owner, Oct 4): version 7 with every river one level its whole length, a narrow basin, and dry valley
    // above that. Only the rivers' columns and their banks differ.
    8: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 8 }),
    // Version 9 (the owner's turf spec, Oct 5): version 8 with the surface of every living biome turned to regolith, two
    // cells deep, and its tops grown over with turf (turf.mjs). The shape, seas, ore, rocks and life are version 8's.
    9: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 9 }),
    // Version 10 (the owner, Oct 8: "ensure that they plan their footprint on what is considered the up side of a kube"):
    // version 9 with what grows near an edge planned on its anchor's up side, the whole of it on that face (organics.mjs
    // planUpSide, flora.mjs ctx.edgeUp). The ground, seas, ore, rocks and turf are version 9's to the bit.
    10: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 10 }),
    // Version 11 (the owner, Oct 8: "fix why turf isnt growing on some world edges/corners"): version 10 with the top cell
    // of ground rolled over an edge or a corner counted as living ground. Its depth under the rolled surface (depthUnder)
    // comes out a little below 0 there (-0.5..0: the cell's centre stands just outside the quarter circle's radius less
    // its half cell), which version 9 and 10 read as "not living ground", so a whole edge line kept its crust and no turf.
    // Only those cells differ: they are regolith, capped with turf, like the face either side.
    11: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 11 }),
    // Version 12, "Biome Wave 6" (the owner, Oct 11): version 11 with no latitude in a turning world's climate (one
    // temperature, its mean: climate.mjs opts.flat; a locked world keeps its day and night faces), its woods needle or
    // broadleaf by that mean (organics.mjs woods v12), its turf living by it, its dunes run to the poles, and twelve new
    // biomes (biomes.mjs wave 6): mudpot fields, column coast, halite spires, cenote fields, slot canyons, butte country,
    // ashfall plains, penitente fields, pitch lakes, stromatolite shoals, hanging gardens and thorn scrub. Every world's
    // biomes may move (the climate decides what is eligible), so its ground, life and turf are its own; seas are kept.
    12: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 12 }),
    // Version 13, "One Temperature" (the owner, Oct 12: "change world gen so the entire worlds are one temperature —
    // seasons only affects daylight/geometry"; "LOCKED WORLDS ARE ONE TEMPERATURE TOO"): version 12 with a world locked to
    // its star as flat as a turning one (climate.mjs opts.oneTemp). Its hot day face and frozen night face are gone, so
    // its biomes, its life and its turf follow its one temperature, the number its card shows (temperature.mjs's
    // planet-wide equilibrium with its greenhouse, the same formula as every turning world's), and Volcanic Fields no
    // longer let in a day face under 150 °C. A turning world is version 12's to the bit (it was flat already), and so
    // are every world's seas, which were always chosen from that number. Its light is kept: a locked world's night face
    // is still dark (the nightside biomes, life's glow), its growths lean to its star, its dunes ring its star's point.
    // …AND NO FLOATING FLORA FAR FROM A COAST (the owner, Oct 12: "remove floating flora far from coastlines"): float
    // leaves grow only within three columns of land (flora.mjs FLOAT_SHORE_REACH), on every world, turning or locked.
    13: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 13 }),
    // Version 14, "Mine What's Drawn" (the owner, Oct 12: keep every world's look and change the generation so the cells
    // really are the kube that is drawn): version 13 with every cell the renderer draws in a tint of the crust made the
    // kube the tint is drawn on, the world's first crust kube (rockyTerrainV4's DRAWN KUBE). A tidal garden over a world's
    // Silicon, or a crater field over its Regolith, is drawn as its Mineral, and now mines as it. The looks are kept to the
    // pixel, and so is everything else: the shape, the seas, the biomes, the life, the ore, the rocks and the turf.
    14: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 14 }),
    // Version 15, "Tufts Are Painted" (the owner's turf picks, Oct 12: TOP clover, EDGE a wavy hem, TUFT stars, and "no small
    // swaying tuft kubes — just tuft texture"): version 14 with no grass tuft standing on the seed's turf, the tufts being
    // painted into every turfed top (texture-turf.js). Each one left out is still counted where it stood, so every other
    // flora (blooms, ferns, moss, a grass tuft on bare ground: dune seas, thorn scrub, ash) is version 14's, item for item,
    // and so is everything else: the shape, the seas, the biomes, the organics, the ore, the rocks and the turf.
    15: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 15 }),
    // VERSION 16, "Life Of Every Pigment" (the owner's biolith decision, Oct 13: "add life that grows them"): version 15 to the
    // cell and to the bit, but for three colours a world picks for its life (organics.mjs pigmentsOf, LIFE_PIGMENTS_SINCE):
    // its petals may be Ignition red or Surge or Reach blue, its bone Lull grey and its slime frames Veil or Haze, so every
    // regolith pigment is grown by some life somewhere and every pigment's biolith can be made. Same draws, same shapes,
    // same counts: only those materials' names (and the flowers' colours) can differ.
    16: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 16 }),
    // VERSION 17, "Alien Peoples" (the owner's confirmed plan, Oct 13): version 16 to the cell and to the bit on every world
    // but one in a hundred of those whose climate fits a people's group and that nobody holds (world.free: no claim, no
    // public record; alien-peoples.mjs peopleOf). Those belong to a people: one to three towns of its building kit in its
    // materials, levelled into the ground on faces of their own (rockyTerrainV4 THE TOWNS, THE TOWN OVERLAY), each ringed
    // by the people's settlement biome (biomes.mjs, since 17), where nothing of the world's own life grows. A claimed
    // world, or one whose record holds builds, is never taken: its build is version 16's exactly (tests/aliens.test.mjs).
    17: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 17 }),
    // VERSION 18, "Alien Towns" (the owner's round 4, confirmed Oct 13: "confirmed and ready to implement"): version 17 to the
    // cell and to the bit on every world no people holds. A people's world (never claimed: alien-peoples.mjs peopleOf, the same
    // worlds as 17's) has the same towns on the same faces at the same plaza level, rebuilt (alien-peoples.mjs planTown18):
    // lanes grown from the heart along the gentlest ground (main 3, side 2, alleys 1, squares 4 to 6), narrow rectangular
    // buildings shoulder to shoulder on terraces of their own, three kubes a storey, taller toward the landmark at the heart
    // (the town's one round form), their façades in bits (bands, quoins, courses and cornices, jetties, framed windows,
    // shutters, sills and flower boxes, arched doors, lanterns, balconies, awnings, vines, pipes, roof clutter, street
    // furniture) in each people's set and its climate's look (turfed roofs, grates, plaster, snow, glowing seams, tholin);
    // walls and gates round an aggressive town, a canal where the world has water, cavern quarters, courtyards through arches.
    18: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 18 }),
    // VERSION 19, "City Layouts And Interiors" (the owner's two proposals, both confirmed Oct 19 20:28 UTC, every decision on
    // its recommendation): version 18 to the cell and to the bit on every world no people holds. A people's world (the same
    // worlds: never claimed) has its towns placed again and laid out anew (alien-towns.mjs planTown19): each grown from the
    // land in the better of its people's two forms, joined with a neighbour into one city of up to four districts, over a
    // cube edge where its ground runs on, within a tenth of the world's surface; its buildings of eight kinds with rooms,
    // stairs, furniture (the new furniture machines), containers, the town's machines and its secrets inside.
    19: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 19 }),
    // Version 20, "Town Buildings" (the owner's confirmed proposal, Oct 20): only a people's towns change (alien-towns.mjs
    // TOWN20: houses on a 5-kube module with shared walls, 4-kube storeys, U stairs, a grid where the land is flat, no
    // villages); every other world is version 19's to the cell.
    20: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 20 }),
    // Version 21, "Towns Fit Their Worlds" (Oct 20, fixing 20, the owner's answers): only a people's towns change
    // (alien-towns.mjs TOWN21: a fifth of the world, split among fewer towns where each share would be too small, a small
    // plot's blocks laid from a corner, a narrow face's town folded over an edge; a world too small for one has no people).
    21: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 21 }),
    // Version 22, "Doors And Rungs In Towns" (the owner's confirmed doors-and-rungs proposal, Oct 20): only a people's towns
    // change (alien-towns.mjs TOWNS22_SINCE: doors in the doorways, rungs on towers, roofs and cellars).
    22: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 22 }),
    // Version 23, "Bark By Starlight" (the owner's Tree Light and Bark picks, confirmed Oct 23 15:01Z, artifact
    // C4UNDwLujm5XJThCaEuD6G: w_rule Starlight, w_scope per form by its face, w_pal all 32, w_apply a new world version):
    // version 22 to the cell and to the bit but for the names of its woods. A tree's wood is its own bark's pigment
    // (organics.mjs barkOf: the light its face gets, from the flux its world record carries, worldRecordOf), not its
    // leaves': every shape, count, leaf and draw is version 22's, and only the "<Pigment> Wood" bits' pigments differ.
    23: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 23 }),
    // Version 24, "No Rime On Conifers" (the owner, Oct 26: black conifers with white bands on an M giant's world; their
    // pick: no frost): version 23 to the cell and to the bit but for a needle tree's rime rings, which are its own leaves
    // (organics.mjs NO_RIME_SINCE) where they were Halo Leaf Biomass.
    24: (d, R, addr, genome, world) => rockyV4Gen(d, R, addr, genome, world, { fill: true, version: 24 }),
};
function rockyV4Gen(d, R, addr, genome, world, opts) {
        if (!(R > 0)) {
            const needs = () => { throw new Error('version 4\'s ground is simulated for its size: its material field needs the world\'s diameter'); };
            return { isSolid: () => false, surfMat: needs, solidR: 0, airR: 0, surfLoR: 0, needsSize: true };
        }
        const t = rockyTerrainV4(d, R, addr, genome, world, opts);
        return { isSolid: t.isSolid, surfMat: t.surfMat, solidR: t.solidR, airR: t.airR, surfLoR: t.surfLoR, surfaceSea: t.surfaceSea, fill: t.fill, turf: t.turf, town: t.town || null,
                 genome: t.genome, surfaceBiome: t.surfaceBiome, biomeCover: t.biomeCover, columnBiome: t.columnBiome || null, organics: t.organics, flora: t.flora,
                 features: { craters: t.craters, tunnels: t.tunnels, mountains: t.mountains, mesas: t.mesas,
                             caveMinR: t.caveMinR, roof: t.roof, signature: t.signature, valleys: t.valleys,
                             volcanoes: t.volcanoes, spires: t.spires, tablelands: t.tablelands, dunes: t.dunes,
                             basaltColumns: t.basaltColumns, iceCrevasses: t.iceCrevasses, boldSmall: t.boldSmall, biomes: t.biomes,
                             geology: t.geology, cutAt: t.cutAt } };
}
export function isKnownGenVersion(v) { return Number.isInteger(v) && Object.prototype.hasOwnProperty.call(ROCKY_GENERATORS, v); }
// Every version this build can generate, oldest first — which is NOT 1..GEN_VERSION_LATEST: a version can be known
// (so claims made with it still build) while the newest is set back to an older one.
export const GEN_VERSIONS = Object.keys(ROCKY_GENERATORS).map(Number).sort((a, b) => a - b);
function generatorFor(genVersion) {
    if (!isKnownGenVersion(genVersion)) throw new Error('unknown world generation version ' + genVersion);
    return ROCKY_GENERATORS[genVersion];
}

// Which of a rocky world's two crust materials shows at a cell, at a generation version. Versions 1 and 2 need nothing
// but the address (their material does not depend on the world's size, only their shape does); from version 3 the
// crust follows the terrain, so the world's diameter is required, and asking without it is refused rather than
// answered for some other size of world: a server pricing a break by the wrong crust would credit a kube nobody sees.
export function rockySurfMat(addr, genVersion = GEN_VERSION_LATEST, diameter, genome, world) {
    const make = generatorFor(genVersion), d = voxDraws(addr);
    const g = make(d, 0, addr || '');
    if (!g.needsSize) return g.surfMat;
    if (!(Number(diameter) > 0)) throw new Error('version ' + genVersion + '\'s crust needs the world\'s diameter');
    return make(d, rockyRadius(Number(diameter)), addr || '', genomeToBuild(addr, genVersion, genome), world).surfMat;
}
// A BIOME LOOK THAT IS A REAL KUBE (the owner, Oct 11: "what i mine is not the same as what i see — white kubes mined as
// scorched mineral"). Every other biome's ground is the crust it is drawn and mined as. These are a real kube instead,
// so they are named, mined and credited as that kube: the page's break (brokenKubeNameAt) and
// both servers' credit (genDensity kubeNameAt) read this one table. Each is DRAWN exactly as a placed kube of that name
// (system-view.js naturalMatIdAt 'k:'; the owner, Oct 12: "no kube should have a biome dependent color/texture"), but for
// lava, a Slag kube drawn as the owner's lava (biome-looks.mjs BIOME_LOOKS.lava).
export const BIOME_LOOK_KUBES = Object.freeze({
    glacier: 'Water Ice', saltFlats: 'Halite', crystal: 'Halite', volcanicFields: 'Slag', warm: 'Slag', lava: 'Slag',
    voidRifts: 'Void Mineral', riftGlow: 'Void Mineral', driftIsles: 'Drift Mineral',
    // version 12's: a column coast's basalt, halite spires' crust and their brine, penitentes' ice, and a pitch lake's tar
    columns: 'Slag', halite: 'Halite', brinePool: 'Halite', penitentes: 'Water Ice', pitchPool: 'Carbon',
    // version 17's settlement biomes (alien-peoples.mjs): the snow round the cold peoples' towns, the ash round the Cinderhost's
    hearthHollows: 'Water Ice', iceFisheries: 'Water Ice', fangRidges: 'Water Ice', ashWarrens: 'Slag' });
// The crust selector AND the biome look of every cell, from one build of the world: what kubeNameAt needs to answer for a
// glacier the way the page does. rockySurfMat stays as it was for its other callers.
export function rockySurfaceFields(addr, genVersion = GEN_VERSION_LATEST, diameter, genome, world) {
    const make = generatorFor(genVersion), d = voxDraws(addr);
    let g = make(d, 0, addr || '');
    if (g.needsSize) {
        if (!(Number(diameter) > 0)) throw new Error('version ' + genVersion + '\'s crust needs the world\'s diameter');
        g = make(d, rockyRadius(Number(diameter)), addr || '', genomeToBuild(addr, genVersion, genome), world);
    }
    // THE SEA, AS THE PAGE LAYS IT IN (the owner, Oct 12: "fix the servers"). The page asks its build for the world's sea
    // (system-view.js seaFor) before it draws, and from then its looks know the water biomes: an ocean's, a lake's or a
    // river's floor shows that biome (rockyTerrainV4 waterBiomeCode). The servers asked this build for none, so under a
    // lake whose column is a glacier or a column coast they credited Water Ice or Slag while the page drew, named and
    // broke the lake's floor over the crust. The sea is the one the world record carries (world.sea: the claim's recorded
    // sea, recordedWorldOf), the one the page asks for, at every version. It moves no cell's shape or crust, only which
    // look a cell under water has, and so what a look that is a real kube credits there.
    if (g.surfaceBiome && g.surfaceSea && world && world.sea) g.surfaceSea(world.sea);
    return { surfMat: g.surfMat, surfaceBiome: g.surfaceBiome || null, townAt: g.town ? g.town.at : null };
}
// The genome a world is built with: the one given, or at version 3 the world's own (genomeOf) when none is.
function genomeToBuild(addr, genVersion, genome) { return genome == null && genVersion >= 3 ? genomeOf(addr) : genome; }

// A ROCKY world's terrain, at a generation version (the newest by default). An unknown version is refused rather than
// answered with the wrong terrain: it means a newer build made the claim, and guessing would bury or float every
// build on it, which is exactly what versions exist to prevent.
// surfaceSea(kind) answers what stands in its basins for a kind of sea ({ kind: 'liquid' | 'ice' | 'lava', name }, from
// phase.mjs worldSeaOf), or null: see ROCKY SEAS.
// `genome` is version 3's (VERSION 3'S GENOME); versions 1 and 2 have none and ignore it. Left out (or null), a version 3
// world is built with its own (genomeOf), as it ships; {} is version 3's defaults. `genome` on the answer is the genome
// the world was built with, every gene resolved, or null.
// `world` is what a version 3 world's biomes follow (WG-09): { addr, star, tempC, pressureMbar, airless, sea }, the
// star's name, the world's average climate and its sea's description. Without it, or with a genome that turns no biome
// on, there are none. surfaceBiome(x, y, z) is then the look a cell of the ground takes from its biome (null for none),
// and biomeCover(seaAt) how much of the surface each biome shows. organics(sea) and flora(sea) are what grows on it
// (organics.mjs, flora.mjs), for the sea surfaceSea gave, or null where nothing does.
// WHETHER A WORLD'S BUILD LAYS OUT A PEOPLE'S TOWNS (the owner's play review 2, Oct 24, s9): rockyTerrainV4's own test, asked
// without building anything (a version 17+ world nobody holds, whose climate fits a people). The page uses it to let a
// worker lay the towns out first (system-view.js plansFirst), the costliest part of such a world's generator.
export function rockyHasPeople(addr, genVersion, world) {
    const ver = genVersion;
    if (!(ver >= ALIENS_SINCE) || !world || world.free !== true) return false;
    const climOf = (w) => climateOf(w, ver >= ONE_TEMP_SINCE ? ONE_TEMP_CLIMATE : ver >= FLAT_CLIMATE_SINCE ? FLAT_CLIMATE : undefined);
    return !!peopleOf(addr || '', world, climOf(world), ver);
}
export function rockyFields(addr, diameter, genVersion = GEN_VERSION_LATEST, genome, world) {
    const make = generatorFor(genVersion);
    const d = voxDraws(addr);
    const R = rockyRadius(diameter);
    const HALF = 2 * R + 1;
    const g = make(d, R, addr || '', genomeToBuild(addr, genVersion, genome), world || null);
    return { R, HALF, rough: d.rough, variance: d.variance, surfThresh: d.surfThresh, genVersion,
             isSolid: g.isSolid, surfMat: g.surfMat, solidR: g.solidR, airR: g.airR, surfLoR: g.surfLoR, fill: g.fill || null,
             surfaceSea: g.surfaceSea || NO_SEA, features: g.features || null, genome: g.genome || null,
             surfaceBiome: g.surfaceBiome || null, biomeCover: g.biomeCover || NO_BIOMES,
             organics: g.organics || NO_LIFE, flora: g.flora || NO_LIFE, turf: g.turf || null, town: g.town || null };
}
const NO_BIOMES = () => [];
const NO_LIFE = () => null;

// A rocky world's generated occupancy, written into `solid` (a zeroed D³ grid, D = 2·HALF, indexed x-fastest exactly as
// buildVoxelWorld's at()). Bit-for-bit what calling f.isSolid on every cell gives — tests/voxel-occupancy.test.mjs holds
// it to that — but it asks isSolid only about the shell between f.solidR and f.airR: on a Ø96 world that is ~156k
// cells of a 7.3M-cell grid at version 1, and ~1.6M at version 2 (relief and caves widen it; each ask is a table
// lookup). Along each axis a cell's rounded distance is x + 1 for x ≥ 0 and −x below, so "r ≤ K" on every axis is the
// box [−K, K − 1]³.
export function fillRockyOccupancy(solid, f, HALF) {
    if (f.fill) return f.fill(solid, HALF);             // version 4 fills by column runs (rockyTerrainV4 fill)
    const D = 2 * HALF, DD = D * D;
    const lo = -f.airR, hi = f.airR - 1, ilo = -f.solidR, ihi = f.solidR - 1;
    for (let z = lo; z <= hi; z++) for (let y = lo; y <= hi; y++) {
        const base = HALF + (y + HALF) * D + (z + HALF) * DD;
        if (y >= ilo && y <= ihi && z >= ilo && z <= ihi) {
            for (let x = lo; x < ilo; x++) if (f.isSolid(x, y, z)) solid[base + x] = 1;
            solid.fill(1, base + ilo, base + ihi + 1);   // the interior run of this row: solid by the generator's own bound
            for (let x = ihi + 1; x <= hi; x++) if (f.isSolid(x, y, z)) solid[base + x] = 1;
        } else {
            for (let x = lo; x <= hi; x++) if (f.isSolid(x, y, z)) solid[base + x] = 1;
        }
    }
    return solid;
}

// Which of the 2 crust materials (0 or 1) shows at cell (x,y,z). VERBATIM from buildVoxelWorld's surfMat (shared by rocky
// worlds + transients in the renderer; here it serves the transient path). `o` = the same 12 offsets.
export function voxSurfMat(noise, o, surfThresh, x, y, z) {
    const [ox1, oy1, oz1, ox2, oy2, oz2, ox3, oy3, oz3, ox4, oy4, oz4] = o;
    const macro = noise((x+ox1)*0.012, (y+oy1)*0.012, (z+oz1)*0.012);
    let sv;
    if (macro < -0.2) sv = noise((x+ox2)*0.04, (y+oy2)*0.04, (z+oz2)*0.04);
    else if (macro > 0.2) sv = noise((x+ox3)*0.2, (y+oy3)*0.2, (z+oz3)*0.2);
    else sv = (0.5 - Math.abs(noise((x+ox4)*0.08, (y+oy4)*0.08, (z+oz4)*0.08))) * 2;
    return (sv + noise(x*0.4, y*0.4, z*0.4) * 0.15) > surfThresh ? 0 : 1;
}

// Asteroid/comet grid radius — Ø clamped to [3,16], halved. Matches voxelRadiusFor(b) for a transient.
export function transientRadius(diameter) { return Math.round(Math.max(3, Math.min(16, Math.round(diameter))) / 2); }

const rarityVal = r => { if (typeof r === 'number') return r || 1; if (typeof r === 'string') { const p = parseInt(r.split('/')[0]); return (!isNaN(p) && p > 0) ? p : 1; } return 1; };
// Two distinct crust kubes, rarity-weighted, drawn with `rng`. VERBATIM from system-view.js pickCrust/pickIce (the pools
// are injected so this module needs no data of its own; the caller passes SOLID_KUBES / ICE_KUBES from the same kubes.json).
function pickCrust(rng, SOLID_KUBES) {
    let pool = SOLID_KUBES.filter(k => { const d = parseFloat(k.density || '0'); return d >= 3.0 && d <= 3.99; });
    if (pool.length < 2) pool = SOLID_KUBES.slice();
    if (pool.length < 2) return [{ hex: '#95a5a6' }, { hex: '#7f8c8d' }];
    pool = pool.slice();
    const draw = () => {
        let total = pool.reduce((s, k) => s + 1 / rarityVal(k.rarity), 0), r = rng() * total;
        for (let i = 0; i < pool.length; i++) { r -= 1 / rarityVal(pool[i].rarity); if (r <= 0) return pool.splice(i, 1)[0]; }
        return pool.splice(pool.length - 1, 1)[0];
    };
    return [draw(), draw()];
}
function pickIce(rng, ICE_KUBES) {
    let pool = (ICE_KUBES || []).slice();
    if (pool.length < 2) return [{ hex: '#CFEFFF' }, { hex: '#9fd8ff' }];
    const draw = () => {
        let total = pool.reduce((s, k) => s + 1 / rarityVal(k.rarity), 0), r = rng() * total;
        for (let i = 0; i < pool.length; i++) { r -= 1 / rarityVal(pool[i].rarity); if (r <= 0) return pool.splice(i, 1)[0]; }
        return pool.splice(pool.length - 1, 1)[0];
    };
    return [draw(), draw()];
}
// The 2 crust kubes for a transient body — comets draw ICE, asteroids draw density-3 crust. Seeded by addr#crust, matching
// crustFor(b) in system-view.js.
// ── SPECTRAL TYPES: WHAT A PASSING ROCK IS MADE OF ───────────────────────────────────────────────────────────────
// A transient used to be two crust kubes and nothing else, which made every asteroid in the game interchangeable
// scenery: you could mine one for regolith you already had by the hundred. Meanwhile the materials the tech tree
// actually gates on sit in a planet's OUTER CORE and CORE — one Battery costs 1 Cor, 2 Gold, 2 Silver and a
// Cobalt — so the only way to build anything was to dig to the centre of a world.
//
// THE BANDS ARE THE PLANETARY LAYERS, and that is not a coincidence dressed up as one — it is the same ladder read
// a different way. VEIN_BANDS prices depth in density, and the three asteroid classes fall out of the identical
// numbers:
//
//     C-type  2.60–3.30   Silicon, Carbon, Phosphorus, Halite, Bauxite, Titanium Ore    = a world's CRUST
//     S-type  4.00–4.72   Calcium, Magnesium, Potassium, Manganese, Chromium,
//                         Zirconium, Pyrite, Magnetite                                  = its SUBCRUST + MANTLE
//     M-type  4.73–9.99   Copper, Cobalt, Silver, Gold, Cor                             = its OUTER CORE + CORE
//
// So an asteroid is a planetary layer flying past, and the depth ladder becomes a RARITY ladder: what you would
// otherwise tunnel to the centre of a world for arrives on its own, if you catch the right rock. Nothing new is
// reachable that was not reachable before — the pools are derived from the same catalogue by the same density
// rule — it is the same materials at a different price, paid in attention rather than in digging.
//
// THE SHARES ARE NOT THE REAL ONES. Actual main-belt spectra run roughly C 75 / S 17 / M 8; these are tilted toward
// the metallic end because with four bodies in flight at a time and a pass lasting minutes, an 8% M-type means a
// player can watch for an hour and see none. The ORDER is real and the scarcity is real; the slope is playable.
export const TRANSIENT_TYPES = [
    { id: 'C', label: 'C-type', name: 'Carbonaceous', share: 0.58, lo: 2.60, hi: 3.30, thr: 0.22 },
    { id: 'S', label: 'S-type', name: 'Silicaceous',  share: 0.30, lo: 4.00, hi: 4.72, thr: 0.32 },
    { id: 'M', label: 'M-type', name: 'Metallic',     share: 0.12, lo: 4.73, hi: 9.99, thr: 0.44 },
];
// A comet is its own class: its bulk is ice and so is its prize. Water alone is 14 of a Hyper-Cor Matrix, and
// Methane and Nitrogen have no other source at all, so "a comet is worth catching" needs no extra rule.
export const COMET_TYPE = { id: 'K', label: 'Comet', name: 'Volatile', share: 1, lo: 0, hi: 9.99, thr: 0.26 };

// Which class this body is. Its OWN rng stream, deliberately: folding a draw into _transientDraws would shift every
// subsequent draw and silently re-roll the orbit, size and timing of every transient in the universe.
export function transientType(addr, isComet) {
    if (isComet) return COMET_TYPE;
    const r = makeRng((addr || '') + '#type')();
    let acc = 0;
    for (const t of TRANSIENT_TYPES) { acc += t.share; if (r < acc) return t; }
    return TRANSIENT_TYPES[TRANSIENT_TYPES.length - 1];
}

// Every material a transient of class `t` can carry as its seam: the pool transientRich draws from, exported so the
// reachability model (reachability.mjs) counts exactly what a player can fly out and mine.
export function seamPool(t, isComet, SOLID_KUBES, ICE_KUBES, made) {
    const decorative = (n) => / Mineral$/.test(n) || / Regolith$/.test(n) || / Glass$/.test(n);
    return ((isComet ? ICE_KUBES : SOLID_KUBES) || []).filter((k) => {
        if (!k || !k.name || decorative(k.name)) return false;
        if (made && typeof made.has === 'function' && made.has(k.name)) return false;
        const d = parseFloat(k.density || '0');
        return d >= t.lo && d <= t.hi;
    });
}

// The one thing this body is worth flying to, or null if the catalogue offers nothing in its band.
//
// `made` is the vein-exclusion set — the names a recipe produces — and it is honoured here for the same reason
// veinAt honours it: smelting is how you are meant to get Iron, and finding it lying in a rock makes the Induction
// Smelter pointless. Decorative minerals, regoliths and glasses are filtered by the same rule veinCandidates uses;
// they are a body's own scenery and are already what the bulk is made of.
// WHAT KIND OF PLACE a transient's system is: system-engine's positionType roll, repeated here (this module stands alone; the
// test pins the two together). 'blackhole', 'nebula', or '' for a star or a rogue.
export function systemKindOf(addr) {
    const p = String(addr || '').split('.');
    if (!(p.length >= 2)) return '';
    const r = makeRng(`${p[0]}.${p[1]}.type`)();
    return r >= 0.95 && r < 0.96 ? 'nebula' : r >= 0.96 && r < 0.97 ? 'blackhole' : '';
}
// THE DEBRIS OF A BLACK HOLE AND A NEBULA (the owner's picks, Oct 27, B8 and N2): a rock flung from a black hole's disc carries
// seams of EXOTIC MATTER (products.json, found nowhere else); one from a nebula's birth discs the rarer minerals of its band.
export const EXOTIC_MATTER = 'Exotic Matter';
export function transientRich(addr, isComet, SOLID_KUBES, ICE_KUBES, made) {
    const kind = systemKindOf(addr);
    if (kind === 'blackhole' && !isComet) return { name: EXOTIC_MATTER, hex: '#B46BFF', state: 'solid', density: '0.90', rarity: '10/10' };
    let pool = seamPool(transientType(addr, isComet), isComet, SOLID_KUBES, ICE_KUBES, made);
    if (kind === 'nebula') { const rare = pool.filter((k) => rarityVal(k.rarity) >= 5); if (rare.length) pool = rare; }
    if (!pool.length) return null;
    const rng = makeRng((addr || '') + '#rich');
    let total = pool.reduce((sum, k) => sum + 1 / rarityVal(k.rarity), 0), r = rng() * total;
    for (const k of pool) { r -= 1 / rarityVal(k.rarity); if (r <= 0) return k; }
    return pool[pool.length - 1];
}

// WHERE it is. A noise lobe rather than a scatter, so the prize runs through the body in seams you follow instead
// of single cells you stumble on — the same reason world veins are round pockets and not speckle. Its own rng and
// its own noise field, so adding this could not disturb the shape the body already had.
export function transientRichMask(addr, isComet, diameter) {
    const t = transientType(addr, isComet);
    const rng = makeRng((addr || '') + '#richmask');
    const noise = makeNoise(Math.floor(rng() * 0x7fffffff));
    const ox = rng() * 10000, oy = rng() * 10000, oz = rng() * 10000;
    // THE BAR DROPS ON A SMALL BODY, because a lobe a few cells across can miss a 17-cell rock entirely: measured,
    // 31% of R=2 bodies had no seam at all against 0% at R>=5. A rock you flew out to and found empty is not
    // "scarcity", it is a wasted pass — and on an M-type, which is 12% of asteroids to begin with, it is the
    // difference between the feature working and the feature being a rumour. Scaling the threshold rather than the
    // frequency keeps the seam CONTIGUOUS: a tiny body gets one fat lobe instead of speckle.
    const R = transientRadius(diameter);
    const eff = t.thr * Math.min(1, Math.max(0.25, (R - 1) / 4));
    // 0.25 per cell: lobes a few cells across on a body whose radius is 2–8, so a small rock gets one seam and a
    // big one gets several, instead of either being uniformly rich or having a single unfindable cell.
    return (x, y, z) => noise((x + ox) * 0.25, (y + oy) * 0.25, (z + oz) * 0.25) > eff;
}

export function transientCrust(addr, isComet, SOLID_KUBES, ICE_KUBES) {
    const rng = makeRng((addr || '') + '#crust');
    return isComet ? pickIce(rng, ICE_KUBES) : pickCrust(rng, SOLID_KUBES);
}

// The full #vox draw sequence + the transient shape/material closures for one body. MUST stay in lockstep with
// buildVoxelWorld (system-view.js): same seed string, same rng DRAW ORDER. Note the two discarded draws — `rough` and
// `variance` are consumed by buildVoxelWorld (used only by the ROCKY isSolid) BEFORE surfThresh, so a transient must draw
// them too or surfThresh (→ surfMat) would land on the wrong rng output.
export function transientFields(addr, diameter) {
    const { noise, off, surfThresh } = voxDraws(addr);         // rough + variance are drawn there too, keeping the order
    const R = transientRadius(diameter);
    const HALF = R + 2 + Math.ceil(R * 0.5);
    return {
        R, HALF,
        isSolid: (x, y, z) => transientIsSolid(noise, off, R, x, y, z),
        surfMat: (x, y, z) => voxSurfMat(noise, off, surfThresh, x, y, z),
    };
}

// SERVER anti-cheat entry point: the kube NAME at cell (x,y,z) of a transient, or null if that cell isn't part of the body
// (or the crust kube has no name). Deterministic from the address + diameter + isComet.
export function transientKubeAt(addr, isComet, diameter, x, y, z, SOLID_KUBES, ICE_KUBES, made) {
    const f = transientFields(addr, diameter);
    if (!f.isSolid(x, y, z)) return null;
    // THE SEAM WINS OVER THE BULK. Checked first and separately from surfMat, so the body's two crust materials are
    // untouched by this — a transient looks exactly as it always did until you cut into the part that matters.
    const rich = transientRich(addr, isComet, SOLID_KUBES, ICE_KUBES, made);
    if (rich && transientRichMask(addr, isComet, diameter)(x, y, z)) return rich.name || null;
    const crust = transientCrust(addr, isComet, SOLID_KUBES, ICE_KUBES);
    const k = crust[f.surfMat(x, y, z)] || crust[0];
    return (k && k.name) || null;
}
